import { LucideIcon } from 'lucide-react';

export type ToolCategory = 'Text' | 'Image' | 'Dev' | 'Network' | 'Converters' | 'Media' | 'Calculator' | 'Science' | 'SEO' | 'Utility' | 'PDF' | 'Design' | 'Brain Games' | 'Fun' | 'Discovery' | 'Security' | 'Earth & Space' | 'Explore';

export interface Tool {
  id: string;
  name: string;
  description: string;
  category: ToolCategory;
  icon: LucideIcon | string;
  route: string;
  longDescription?: string;
  howToUse?: string[];
  benefits?: string[];
  faq?: { question: string; answer: string }[];
  metaTitle?: string;
  metaDescription?: string;
  keywords?: string[];
  image?: string;
  technicalDeepDive?: string;
  useCases?: string[];
  comparisonData?: { feature: string; vibeTools: string; others: string }[];
  expertNote?: string;
  relatedToolIds?: string[];
}

import {
  FileText,
  Smartphone,
  Terminal,
  Database,
  Video,
  Mic,
  Camera,
  Layers,
  Sliders,
  Youtube,
  PenTool,
  Code2,
  Sparkles,
  Mail,
  Send,
  ArrowRightLeft,
  Search,
  Shield,
  ShieldAlert,
  Layout,
  Type,
  FileCode,
  ClipboardList,
  RotateCw,
  Minimize2,
  Strikethrough,
  Underline,
  Bold,
  Italic,
  Crown,
  Image as ImageIcon,
  Palette,
  Crop,
  Zap,
  Weight,
  Thermometer,
  HardDrive,
  Fuel,
  Activity,
  Dumbbell,
  Smile,
  Timer,
  Trophy,
  GlassWater,
  Heart,
  Baby,
  Wind,
  BoxSelect,
  Beaker,
  BatteryCharging,
  Braces,
  Lock,
  RefreshCw,
  Ghost,
  Skull,
  CheckCircle,
  Wrench,
  Fingerprint,
  Hash,
  Ruler,
  Droplet,
  Clock,
  DollarSign,
  AlignLeft,
  RemoveFormatting,
  Copy,
  ArrowUpDown,
  Shuffle,
  Link2,
  List,
  Code,
  FileJson,
  Key,
  Globe,
  FileCode2,
  Wifi,
  Monitor,
  FileImage,
  Moon,
  Star,
  Tv,
  FilePlus,
  Scissors,
  FileOutput,
  Eraser,
  Instagram,
  Music,
  RotateCcw,
  RefreshCcw,
  Network,
  Link,
  Calendar,
  Percent,
  Calculator,
  GraduationCap,
  Scale,
  Tags,
  Eye,
  FileSearch,
  Lightbulb,
  Heading,
  QrCode,
  Dices,
  Users,
  Coins,
  Play,
  Pause,
  StickyNote,
  Wallet,
  Keyboard,
  Text,
  Repeat,
  ArrowDownAZ,
  Maximize2,
  BookOpen,
  Image,
  Volume2,
  Ratio,
  Table,
  Binary,
  BarChart3,
  LayoutGrid,
  Files,
  Shapes,
  Phone,
  CreditCard,
  Share2,
  Box,
  Move,
  Circle,
  Hexagon,
  Square,
  TrendingUp,
  Briefcase,
  Receipt,
  Captions,
  ShieldCheck,
  EyeOff,
  Radio,
  Settings,
  Gamepad2 as GamepadIcon,
  Crosshair,
  BrainCircuit,
  Grid,
  ListOrdered,
  Languages,
  ScanLine,
  Puzzle,
  User,
  Stamp,
  FileUp,
  FileDown,
  FileSpreadsheet,
  FileType,
  Bot,
  Wand2,
  MessageSquare,
  ArrowLeftRight,
  LocateFixed,
  Cone,
  Gauge,
  ArrowRight,
  Rocket,
  MailCheck,
  UserX,
  Car,
  ScanBarcode,
  Satellite,
  Globe2,
  Pipette,
  BookMarked,
  SpellCheck,
  CloudSun
} from 'lucide-react';

export const legacyTools: Tool[] = [
  // Text Tools
  {
    id: 'word-counter',
    name: 'Word Counter',
    description: 'Count words, characters, and sentences',
    category: 'Text',
    icon: FileText,
    route: '/tools/word-counter',
    metaTitle: 'Word Counter: Count Words, Characters & Sentences Online',
    metaDescription: 'Count words, characters, and sentences instantly with our free online word counter. Analyze text length, reading time, and keyword density. No sign-up required.',
    keywords: ['word counter', 'character count', 'sentence counter', 'text analyzer', 'word count tool', 'online word counter', 'reading time calculator', 'paragraph counter', 'free word counter', 'text statistics'],
    image: '/Tool Pages/Word Counter.png'
  },
  {
    id: 'case-converter',
    name: 'Case Converter',
    description: 'Transform text between different letter cases',
    category: 'Text',
    icon: Type,
    route: '/tools/case-converter',
    metaTitle: 'Case Converter: Text to Uppercase, Lowercase & Title Case',
    metaDescription: 'Convert text to uppercase, lowercase, title case, sentence case, and more instantly online. Free text case converter tool for writers and developers.',
    keywords: ['case converter', 'text converter', 'uppercase to lowercase', 'title case', 'sentence case', 'camelcase converter', 'capitalization tool', 'text case changer', 'online case converter', 'toggle case'],
    image: '/Tool Pages/Case Converter.png'
  },
  {
    id: 'text-diff',
    name: 'Text Diff',
    description: 'Compare texts and highlight differences',
    category: 'Text',
    icon: Search,
    route: '/tools/text-diff-checker-tool',
    metaTitle: 'Text Diff Checker: Compare Two Texts Online (Free)',
    metaDescription: 'Compare two texts or code snippets to find differences instantly. Our free online text diff tool highlights additions, deletions, and changes. Fast and private.',
    keywords: ['text diff', 'diff checker', 'compare text', 'online diff tool', 'text difference', 'compare strings', 'code diff', 'file comparison', 'version comparison', 'text comparison'],
    image: '/Tool Pages/Text Diff.png'
  },
  {
    id: 'markdown-preview',
    name: 'Markdown Preview',
    description: 'Live markdown editor with preview',
    category: 'Text',
    icon: FileCode,
    route: '/tools/markdown-preview-editor',
    metaTitle: 'Markdown Preview: Live Online Markdown Editor & Viewer',
    metaDescription: 'Edit Markdown with a live preview in your browser. Our free online Markdown editor supports GitHub Flavored Markdown for instant rendering. No download.',
    keywords: ['markdown preview', 'markdown editor', 'online markdown', 'live md preview', 'github markdown', 'readme editor', 'md to html', 'markdown viewer', 'markdown converter', 'free markdown editor'],
    image: '/Tool Pages/Markdown Preview.png'
  },
  {
    id: 'lorem-ipsum',
    name: 'Lorem Ipsum',
    description: 'Generate placeholder text',
    category: 'Text',
    icon: ClipboardList,
    route: '/tools/lorem-ipsum-generator',
    metaTitle: 'Lorem Ipsum Generator: Create Dummy Text Online Instantly',
    metaDescription: 'Generate Lorem Ipsum placeholder text for design mockups and content layouts. Customize paragraph count, word length, and HTML tags. Free, no sign-up required.',
    keywords: ['lorem ipsum generator', 'dummy text generator', 'placeholder text', 'lorem generator', 'text filler', 'design mockups', 'greeking text', 'latin generator', 'generate lorem ipsum', 'online lorem ipsum'],
    image: '/Tool Pages/Lorem Ipsum Generator.png'
  },

  // SEO Tools
  {
    id: 'link-extractor',
    name: 'Link Extractor',
    description: 'Extract and analyze links from HTML, Text, or Websites',
    category: 'SEO',
    icon: Link2,
    route: '/tools/link-extractor-tool',
    metaTitle: 'Link Extractor: Extract URLs from Websites & Text Online',
    metaDescription: 'Extract all internal and external links from any URL, HTML, or plain text. Analyze link types and export results. Free online link extraction tool.',
    keywords: ['link extractor', 'url extractor', 'extract links from website', 'link analyzer', 'bulk url extractor', 'internal link checker', 'seo link tool', 'get all links from url', 'website link scraper']
  },

  // Image Tools
  {
    id: 'image-compressor',
    name: 'Image Compressor',
    description: 'Reduce image file size',
    category: 'Image',
    icon: ImageIcon,
    route: '/tools/image-compression-tool',
    image: '/Tool Pages/Image Compressor.png',
    metaTitle: 'Image Compressor: Reduce Image File Size Online Free',
    metaDescription: 'Compress JPG, PNG, and GIF images to reduce file size without visible quality loss. Optimize photos for web, email, and faster loading. Free online tool.',
    keywords: ['image compressor', 'compress image', 'reduce image size', 'optimize png', 'shrink jpeg', 'photo compressor', 'image optimizer', 'compress jpg online', 'free image compression']
  },

  // New Batch: Developer & Web (10)
  {
    id: 'dns-checker',
    name: 'DNS Propagation Checker',
    description: 'Check DNS records (A, MX, NS, CNAME) globally',
    category: 'Network',
    icon: Globe,
    route: '/tools/dns-propagation-checker',
    keywords: ['dns propagation checker', 'dns checker', 'dns propagation', 'dns lookup', 'mx record check', 'ns lookup', 'dns records', 'txt record lookup', 'global dns check', 'check dns changes'],
    metaTitle: 'DNS Propagation Checker: Check Global DNS Records Online',
    metaDescription: 'Check DNS propagation for A, MX, NS, CNAME, and TXT records across global DNS servers. Monitor real-time DNS changes instantly. Free online tool.'

  },
  {
    id: 'ssl-checker',
    name: 'SSL/TLS Certificate Checker',
    description: 'Verify SSL certificate expiry and validity',
    category: 'Network',
    icon: Shield,
    route: '/tools/ssl-tls-certificate-checker',
    keywords: ['ssl certificate checker', 'ssl checker', 'tls checker', 'ssl expiry', 'certificate validator', 'https check', 'ssl chain check', 'cert lookup', 'online ssl checker', 'free ssl checker'],
    metaTitle: 'Free SSL/TLS Certificate Checker — Verify Expiry & Validity',
    metaDescription: 'Verify SSL/TLS certificate validity, expiry, and chain issues instantly. Check HTTPS status and server configuration online. Free certificate validation.',
  },

  {
    id: 'whois-lookup',
    name: 'Whois Domain Lookup',
    description: 'Find domain registration information and owner details',
    category: 'Network',
    icon: Search,
    route: '/tools/whois-domain-lookup',
    keywords: ['whois domain lookup', 'whois lookup', 'domain whois', 'domain owner', 'domain expiry', 'registrar lookup', 'whois check', 'domain availability', 'free whois', 'domain registration info'],
    metaTitle: 'Free Whois Domain Lookup — Find Owner & Registration',
    metaDescription: 'Perform a Whois domain lookup to find registration details, owner information, and expiry dates. Check domain availability and registrar data online.',
  },
  {
    id: 'email-verifier',
    name: 'Email Verifier',
    description: 'Verify email address validity and existence',
    category: 'Network',
    icon: Mail,
    route: '/tools/email-verifier-tool',
    keywords: ['email verifier', 'verify email', 'email validation', 'check email address', 'email checker', 'disposable email check', 'email existence check', 'free email verifier', 'bulk email verifier'],
    metaTitle: 'Free Email Verifier — Check Email Validity & Existence',
    metaDescription: 'Verify email address validity and existence online. Check for syntax errors, disposable domains, and active mail servers. Free email validation.',
  },
  {
    id: 'ip-geolocation',
    name: 'IP Geolocation',
    description: 'Find location data for any IP address',
    category: 'Dev',
    icon: Globe,
    route: '/tools/ip-geolocation-lookup',
    keywords: ['ip geolocation', 'ip lookup', 'ip location', 'find ip address', 'geoip', 'ip tracker', 'locate ip', 'free ip geolocation', 'ip address location', 'isp lookup'],
    metaTitle: 'Free IP Geolocation — Trace IP Address Location & ISP',
    metaDescription: 'Perform IP geolocation to trace an IP address location, ISP, and organizational details. Find geographical data for any IP online. Free IP lookup.',
  },
  {
    id: 'domain-availability',
    name: 'Domain Availability',
    description: 'Check if a domain name is available for registration',
    category: 'Dev',
    icon: Search,
    route: '/tools/domain-availability-checker',
    keywords: ['domain availability', 'domain checker', 'is domain taken', 'domain name search', 'free domain availability', 'check domain name', 'url availability', 'domain lookup', 'whois search'],
    metaTitle: 'Free Domain Availability Checker — Instant Domain Search',
    metaDescription: 'Check domain availability instantly for any top-level domain (TLD). Verify if a domain name is taken or available for registration. Free online domain search tool.',
  },
  {
    id: 'subnet-calculator',
    name: 'Subnet Calculator',
    description: 'Calculate IPv4/IPv6 subnets and CIDR',
    category: 'Network',
    icon: Calculator,
    route: '/tools/ipv4-subnet-calculator',
    keywords: ['subnet calculator', 'cidr calculator', 'ipv4 subnet', 'ip calculator', 'network mask', 'cidr notation', 'broadcast address'],
    metaTitle: 'Subnet Calculator: Calculate IPv4 CIDR & Network Mask',
    metaDescription: 'Subnet Calculator to calculate IPv4 subnets, CIDR notation, and network masks. Determine usable host ranges and broadcast addresses easily.',
  },
  {
    id: 'mac-lookup',
    name: 'MAC Address Lookup',
    description: 'Identify device manufacturer from MAC OUI',
    category: 'Network',
    icon: Search,
    route: '/tools/mac-address-lookup',
    keywords: ['mac address lookup', 'mac lookup', 'mac address check', 'oui lookup', 'ethernet vendor', 'mac manufacturer', 'device lookup', 'mac vendor lookup', 'free mac lookup', 'online mac address tool'],
    metaTitle: 'MAC Address Lookup: Identify OUI & Manufacturer Instantly',
    metaDescription: 'Perform a MAC address lookup to identify the manufacturer (OUI) of any network device. Instantly check vendor details online for free.',
  },
  {
    id: 'port-scanner',
    name: 'Open Port Scanner',
    description: 'Check for open ports on any domain or IP',
    category: 'Network',
    icon: Search,
    route: '/tools/open-port-scanner',
    keywords: ['open port scanner', 'check open ports', 'online port checker', 'tcp port scan', 'udp port scan', 'network port scanner', 'firewall test', 'server security check', 'port status checker', 'scan ip ports'],
    metaTitle: 'Open Port Scanner: Check TCP/UDP Ports Online (Free)',
    metaDescription: 'Check open ports on any IP address or domain with this free online port scanner. Identify active services and test firewall configurations instantly. No sign-up required.',
  },
  {
    id: 'react-generator',
    name: 'React Component Gen',
    description: 'Generate boilerplate for React components',
    category: 'Dev',
    icon: Code2,
    route: '/tools/react-component-generator',
    keywords: ['react component generator', 'react boilerplate', 'create react component', 'tsx generator', 'react scaffolding', 'react hook generator', 'react code generator', 'online react generator'],
    metaTitle: 'React Component Generator: Create Boilerplate Code Online',
    metaDescription: 'Generate React component boilerplate for functional or class components, including TypeScript (TSX) and hooks. Create React code instantly for free.',
  },
  {
    id: 'htaccess-generator',
    name: '.htaccess File Generator',
    description: 'Create Apache redirects and security rules',
    category: 'Dev',
    icon: Lock,
    route: '/tools/htaccess-file-generator',
    keywords: ['htaccess generator', 'apache config generator', '301 redirect generator', 'force https htaccess', 'hotlink protection htaccess', 'apache rewrite rules', 'web server config', 'link redirection tool'],
    metaTitle: '.htaccess File Generator: Create Apache Redirects & Rules',
    metaDescription: 'Generate .htaccess rules for 301 redirects, HTTPS enforcement, hotlink protection, and more. Create Apache configurations instantly online.',
  },
  {
    id: 'robots-tester',
    name: 'robots.txt Tester',
    description: 'Test if a specific URL is allowed by robots.txt',
    category: 'Dev',
    icon: FileSearch,
    route: '/tools/robots-txt-tester',
    keywords: ['robots.txt tester', 'validate robots.txt', 'robots.txt validator', 'googlebot checker', 'seo crawl tool', 'test robots.txt file', 'technical seo tool', 'check robots.txt'],
    metaTitle: 'robots.txt Tester: Validate Googlebot & SEO Crawl Rules',
    metaDescription: 'Test if a URL is allowed or disallowed by your robots.txt file for Googlebot and other user-agents. Validate SEO crawl directives instantly.',
  },
  {
    id: 'color-picker',
    name: 'Image Color Picker',
    description: 'Extract exact colors and palettes from images',
    category: 'Image',
    icon: Palette,
    route: '/tools/image-color-picker',
    image: '/Tool Pages/Image Color Picker.png',
    metaTitle: 'Image Color Picker: Extract HEX & RGB Colors from Images',
    metaDescription: 'Extract exact HEX, RGB, and HSL color codes from any image. Upload an image and pick colors instantly online for free.',
    keywords: ['image color picker', 'color picker from image', 'extract colors from image', 'color palette generator', 'hex code finder', 'image to hex', 'rgb color picker', 'detect colors in image']
  },
  {
    id: 'image-cropper',
    name: 'Image Cropper & Editor',
    description: 'Crop, resize, rotate, and flip images',
    category: 'Image',
    icon: Crop,
    route: '/tools/image-cropper-tool',
    image: '/Tool Pages/Image Cropper.png',
    metaTitle: 'Image Cropper & Editor: Crop, Resize, Rotate Photos Online Free',
    metaDescription: 'Crop images online to specific dimensions or aspect ratios. Rotate, flip, and resize photos instantly in your browser. Free image editing without software.',
    keywords: ['image cropper online', 'crop image free', 'resize image', 'rotate photo', 'flip image', 'aspect ratio cropper', 'circle crop online', 'photo editor web']
  },
  {
    id: 'svg-optimizer',
    name: 'SVG Optimizer',
    description: 'Optimize SVG files',
    category: 'Image',
    icon: Zap,
    route: '/tools/svg-optimizer-tool',
    image: '/Tool Pages/SVG Optimizer.png',
    metaTitle: 'SVG Optimizer: Minify & Clean SVG Files Online (Free)',
    metaDescription: 'Optimize SVG files by removing redundant data and minifying code. Reduce SVG file size for faster web loading. Free online SVG cleaner.',
    keywords: ['svg optimizer online', 'minify svg', 'clean svg', 'svg compressor', 'reduce svg size', 'vector optimizer', 'svg minifier free', 'optimize svg code']
  },

  // Dev Tools
  {
    id: 'json-to-toon-converter',
    name: 'JSON to TOON Converter',
    description: 'Optimize JSON for LLMs (30-60% Token Reduction)',
    category: 'Dev',
    icon: Zap,
    route: '/tools/json-to-toon-converter',
    keywords: ['json to toon converter', 'reduce llm tokens', 'json optimizer for gpt', 'context window optimizer', 'token saver llm', 'prompt engineering tool', 'minimize json for ai', 'tree object notation'],
    metaTitle: 'JSON to TOON Converter: Reduce LLM Token Usage (Free)',
    metaDescription: 'Convert JSON to TOON (Tree Object Notation) to reduce LLM token consumption by 30-60%. Optimize JSON data for AI context windows. Free online converter.',
  },
  {
    id: 'pomodoro-focus-timer',
    name: 'Pomodoro Focus Timer',
    description: 'Productivity timer with 25/5 intervals & sound',
    category: 'Utility',
    icon: Timer,
    route: '/tools/pomodoro-focus-timer',
    keywords: ['pomodoro timer online', 'focus timer', 'productivity timer', 'tomato timer', '25 minute timer', 'work break timer', 'study timer', 'pomodoro technique sound', 'online focus tool'],
    metaTitle: 'Pomodoro Focus Timer: Online Productivity Timer (25/5)',
    metaDescription: 'Boost focus with a free online Pomodoro timer. Work in 25-minute intervals, take 5-minute breaks. Customizable sessions and sound notifications.',
  },
  {

    id: 'base64-encoder',
    name: 'Base64 Encoder',
    description: 'Encode text to Base64',
    category: 'Text',
    icon: Binary,
    route: '/tools/base64-encoder-decoder',
    image: '/Tool Pages/Base64 Encoder.png',
    metaTitle: 'Free Base64 Encoder Online — Text to Base64 & URL Safe',
    metaDescription: 'Encode text to Base64 strings, including URL-safe variants, instantly online. Supports UTF-8 and various character sets. Free, no sign-up required.',
    keywords: ['base64 encoder', 'text to base64', 'base64 encode online', 'url safe base64', 'base64url encode', 'string to base64', 'utf8 base64 encode', 'base64 converter']
  },
  {
    id: 'hex-to-base64',
    name: 'Hex to Base64',
    description: 'Convert Hex to Base64 & Vice Versa',
    category: 'Converters',
    icon: Hash,
    route: '/tools/hex-to-base64-converter',
    metaTitle: 'Free Hex to Base64 Converter — Hexadecimal to Base64 Online',
    metaDescription: 'Convert hexadecimal strings to Base64 and Base64 to hexadecimal instantly online. Supports various input formats and provides accurate conversions. Free, no sign-up.',
    keywords: ['hex to base64', 'base64 to hex', 'hexadecimal to base64', 'base64 to hexadecimal', 'hex converter', 'base64 converter', 'hex string to base64', 'base64 string to hex']
  },
  {
    id: 'file-to-base64',
    name: 'File to Base64',
    description: 'Convert any file to Base64 string',
    category: 'Converters',
    icon: FileUp,
    route: '/tools/file-to-base64-converter',
    keywords: ['file to base64', 'image to base64', 'pdf to base64', 'audio to base64', 'video to base64', 'base64 converter', 'encode file to base64', 'data uri generator', 'base64 file encoder'],
    metaTitle: 'Free File to Base64 Converter — Image, PDF, Audio, Video',
    metaDescription: 'Convert any file (image, PDF, audio, video) to a Base64 string instantly online. Generates Data URI for embedding. Free, no server upload.',
  },
  {
    id: 'base64-to-file',
    name: 'Base64 to File',
    description: 'Decode Base64 string to file',
    category: 'Converters',
    icon: FileDown,
    route: '/tools/base64-to-file-converter',
    keywords: ['base64 to file', 'base64 to image', 'base64 to pdf', 'base64 decoder', 'decode base64', 'base64 string to file', 'base64 to audio', 'base64 to video', 'base64 file decoder'],
    metaTitle: 'Free Base64 to File Converter — Decode Image, PDF, Audio',
    metaDescription: 'Decode Base64 strings back into original files (image, PDF, audio, video) instantly online. Download decoded files. Free, client-side processing.',
  },
  {
    id: 'regex-tester',
    name: 'Regex Tester',
    description: 'Test and debug regular expressions',
    category: 'Dev',
    icon: Wrench,
    route: '/tools/regex-tester-debugger',
    keywords: ['regex tester', 'regular expression tester', 'online regex debugger', 'test regex online', 'regex validator', 'javascript regex tester', 'python regex tester', 'php regex tester', 'regex match tool', 'debug regular expression'],
    metaTitle: 'Regex Tester: Online Regular Expression Debugger & Validator',
    metaDescription: 'Test and debug regular expressions against sample text. Validate regex patterns, visualize matches, and build expressions with a live editor. Free online tool.',
  },
  {
    id: 'uuid-generator',
    name: 'UUID Generator',
    description: 'Generate unique identifiers (UUIDs/GUIDs) instantly',
    category: 'Dev',
    icon: Fingerprint,
    route: '/tools/uuid-v4-generator',
    metaTitle: 'UUID Generator: Create Random v4 UUIDs & GUIDs Online',
    metaDescription: 'Generate unique identifiers (UUIDs/GUIDs) instantly. Create random v4 UUIDs for databases, distributed systems, or unique keys. Free and client-side.',
    keywords: ['uuid generator', 'guid generator', 'random uuid', 'v4 uuid', 'bulk uuid generator', 'generate uuid online', 'unique identifier generator', 'create guid', 'uuid v4 online', 'generate unique id']
  },
  {
    id: 'hash-generator',
    name: 'Hash Generator',
    description: 'Generate cryptographic hashes securely',
    category: 'Dev',
    icon: Hash,
    route: '/tools/hash-generator-tool',
    metaTitle: 'Hash Generator: MD5, SHA-256, SHA-1, SHA-512 Online Free',
    metaDescription: 'Generate cryptographic hashes (MD5, SHA-1, SHA-256, SHA-512) from text or files. Verify data integrity with checksums. Free, client-side hashing.',
    keywords: ['hash generator', 'md5 generator', 'sha256 calculator', 'online hashing tool', 'checksum generator', 'file hash generator', 'sha512 generator', 'generate hash online', 'text hash', 'data integrity check']
  },

  // NEW DEVELOPER TOOLS - Batch 1
  {
    id: 'json-diff',
    name: 'JSON Diff Comparator',
    description: 'Compare two JSON structures and visualize differences',
    category: 'Dev',
    icon: FileJson,
    route: '/tools/json-diff-comparator',
    metaTitle: 'JSON Diff Comparator: Compare JSON Objects Online Free',
    metaDescription: 'Compare two JSON structures to find differences. Visualize additions, deletions, and modifications in JSON data. Free online JSON comparison tool.',
    keywords: ['json diff', 'compare json', 'json comparator', 'json difference checker', 'diff json online', 'json structure compare', 'json compare tool', 'json data diff', 'online json diff', 'json merge tool']
  },
  {
    id: 'xml-validator',
    name: 'XML Validator',
    description: 'Validate XML syntax and structure with detailed error reporting',
    category: 'Dev',
    icon: FileCode,
    route: '/tools/xml-validator-checker',
    metaTitle: 'XML Validator: Check XML Syntax & Structure Online Free',
    metaDescription: 'Validate XML syntax and structure with detailed error reporting. Ensure well-formed XML documents for data exchange and configuration. Free online XML checker.',
    keywords: ['xml validator', 'validate xml', 'xml checker', 'xml syntax checker', 'xml well-formed', 'check xml online', 'xml document validation', 'xml error reporting', 'free xml validator']
  },
  {
    id: 'yaml-validator',
    name: 'YAML Validator',
    description: 'Validate YAML syntax and convert to JSON',
    category: 'Dev',
    icon: FileCode2,
    route: '/tools/yaml-validator-checker',
    metaTitle: 'YAML Validator: Check YAML Syntax & Convert to JSON Free',
    metaDescription: 'Validate YAML syntax and structure online. Convert YAML to JSON format for easy data manipulation. Ensure correct YAML for configurations and data exchange.',
    keywords: ['yaml validator', 'validate yaml', 'yaml checker', 'yaml to json', 'yaml syntax checker', 'yaml parser', 'check yaml online', 'free yaml validator', 'yaml converter']
  },
  {
    id: 'toml-json-converter',
    name: 'TOML ↔ JSON Converter',
    description: 'Convert between TOML and JSON formats bidirectionally',
    category: 'Dev',
    icon: ArrowLeftRight,
    route: '/tools/toml-json-converter',
    metaTitle: 'TOML ↔ JSON Converter: Bidirectional Config Converter Free',
    metaDescription: 'Convert TOML to JSON and JSON to TOML bidirectionally. Validate configuration files and switch formats. Free online TOML and JSON converter.',
    keywords: ['toml to json', 'json to toml', 'toml converter', 'json converter', 'config converter', 'toml parser', 'json parser', 'free toml json converter', 'toml validator']
  },

  // NEW DEVELOPER TOOLS - Batch 2
  {
    id: 'cron-translator',
    name: 'Cron Expression Translator',
    description: 'Convert cron expressions to human-readable text',
    category: 'Dev',
    icon: Clock,
    route: '/tools/cron-expression-translator',
    metaTitle: 'Cron Expression Translator: English & Next Run Online',
    metaDescription: 'Translate cron expressions to human-readable English. Predict next execution dates for cron jobs. Free online cron schedule describer and debugger.',
    keywords: ['cron translator', 'cron expression to english', 'crontab checker', 'cron schedule describer', 'cron debugger', 'next cron execution', 'quartz cron parser', 'free cron translator', 'cron expression generator']
  },
  {
    id: 'ipv6-converter',
    name: 'IPv6 Expander & Compressor',
    description: 'Expand or compress IPv6 addresses to shorthand forms',
    category: 'Dev',
    icon: Globe,
    route: '/tools/ipv6-expander-compressor',
    metaTitle: 'IPv6 Expander & Compressor – Free Online Address Converter',
    metaDescription: 'Expand or compress IPv6 addresses to their full or shorthand forms. Compliant with RFC 5952 for canonical representation. Convert IPv6 addresses instantly.',
    keywords: ['ipv6 expander', 'ipv6 compressor', 'ipv6 canonical form', 'rfc 5952 tool', 'ipv6 zero suppression', 'network address converter', 'ipv6 address formatter', 'ipv6 shorthand', 'full ipv6 address', 'online ipv6 tool']
  },
  {
    id: 'mac-generator',
    name: 'MAC Address Generator',
    description: 'Generate random MAC addresses for testing',
    category: 'Dev',
    icon: Network,
    route: '/tools/mac-address-generator',
    metaTitle: 'MAC Address Generator – Free Random MAC Address Creator',
    metaDescription: 'Generate random MAC addresses for testing and development. Create bulk MAC addresses with specific OUI prefixes or fully random. Free and instant.',
    keywords: ['mac address generator', 'random mac address', 'mac address creator', 'oui generator', 'bulk mac addresses', 'network testing tool', 'l2 address generator', 'ethernet address generator', 'mac address faker', 'hardware address generator']
  },
  {
    id: 'http-header-parser',
    name: 'HTTP Header Parser',
    description: 'Parse raw HTTP headers into structured data',
    category: 'Dev',
    icon: FileText,
    route: '/tools/http-header-parser-tool',
    metaTitle: 'HTTP Header Parser – Free Online Request & Response Analyzer',
    metaDescription: 'Parse raw HTTP headers into structured, readable data. Analyze request and response headers for debugging, security, and development. Free and instant.',
    keywords: ['http header parser', 'analyze http headers', 'http request analyzer', 'http response parser', 'header to json', 'web security audit', 'csp checker', 'http metadata logger', 'parse http request', 'http header viewer']
  },
  {
    id: 'url-parser',
    name: 'URL Parser & Splitter',
    description: 'Break down URLs into individual components',
    category: 'Dev',
    icon: Globe,
    route: '/tools/url-parser-splitter',
    metaTitle: 'URL Parser & Splitter – Free Online URL Component Analyzer',
    metaDescription: 'Parse and split URLs into their individual components: protocol, hostname, path, query, and fragment. Analyze query parameters instantly. Free and online.',
    keywords: ['url parser', 'url splitter', 'split url into parts', 'extract query parameters', 'url component analyzer', 'utm debugger', 'uri splitter', 'parse url online', 'url breakdown tool', 'query string parser']
  },
  {
    id: 'query-string-converter',
    name: 'Query String ↔ JSON Converter',
    description: 'Convert between URL query strings and JSON',
    category: 'Dev',
    icon: ArrowLeftRight,
    route: '/tools/query-string-json-converter',
    metaTitle: 'Query String ↔ JSON Converter: Free URL Parameter Tool Online',
    metaDescription: 'Convert URL query strings to JSON and JSON to query strings instantly. Supports nested objects, arrays, and URL encoding. Free online conversion tool.',
    keywords: ['query string to json converter', 'json to query string converter', 'url parameter parser', 'query string generator', 'json to url parameters', 'url query string encoder', 'free query string tool', 'javascript query string parse', 'url parameter to json', 'json to url query']
  },

  // NEW DEVELOPER TOOLS - Batch 3 (Link Generators)
  {
    id: 'mailto-generator',
    name: 'Mailto Link Generator',
    description: 'Create mailto: links with subject, body, CC, and BCC',
    category: 'Dev',
    icon: Mail,
    route: '/tools/mailto-link-generator',
    metaTitle: 'Mailto Link Generator: Free HTML Email Link Creator Online',
    metaDescription: 'Create mailto: links with subject, body, CC, and BCC fields. Generate HTML email links for websites and applications instantly. Free online tool.',
    keywords: ['mailto link generator', 'html email link creator', 'mailto subject body', 'email link with cc bcc', 'mailto encoder', 'free mailto link tool', 'generate email link', 'mailto link html', 'email address link generator']
  },
  {
    id: 'youtube-timestamp-generator',
    name: 'YouTube Timestamp Link Generator',
    description: 'Generate links to specific moments in YouTube videos',
    category: 'Dev',
    icon: Youtube,
    route: '/tools/youtube-timestamp-link-generator',
    metaTitle: 'YouTube Timestamp Link Generator: Share Video Moments Free',
    metaDescription: 'Generate YouTube links with specific timestamps to share video moments. Create time-coded YouTube URLs instantly. Free online tool.',
    keywords: ['youtube timestamp generator', 'youtube link with time', 'share youtube video moment', 'youtube time offset link', 'video timestamp creator', 'youtube url time code', 'free youtube link tool', 'youtube video start time', 'youtube share at time']
  },
  {
    id: 'whatsapp-generator',
    name: 'WhatsApp Link Generator',
    description: 'Create direct click-to-chat links for WhatsApp',
    category: 'Dev',
    icon: MessageSquare,
    route: '/tools/whatsapp-link-generator',
    metaTitle: 'WhatsApp Link Generator: Free wa.me Click-to-Chat Online',
    metaDescription: 'Create direct WhatsApp click-to-chat links (wa.me) with pre-filled messages. Generate WhatsApp links for business and personal use. Free online tool.',
    keywords: ['whatsapp link generator', 'wa.me link creator', 'whatsapp click to chat', 'whatsapp message generator', 'direct whatsapp link', 'whatsapp api link', 'free whatsapp link tool', 'whatsapp chat link', 'pre-filled whatsapp message']
  },
  {
    id: 'telegram-generator',
    name: 'Telegram Link Generator',
    description: 'Create custom links for Telegram users, bots, and channels',
    category: 'Dev',
    icon: Send,
    route: '/tools/telegram-link-generator',
    metaTitle: 'Telegram Link Generator: Create t.me Links for Users, Bots',
    metaDescription: 'Generate Telegram t.me links for users, bots, and channels instantly. Customize start parameters for bots. Free online Telegram link creator.',
    keywords: ['telegram link generator', 't.me link creator', 'telegram username link', 'telegram bot start link', 'telegram channel link', 'telegram group invite link', 'create telegram link', 'telegram deep link generator']
  },
  {
    id: 'csp-generator',
    name: 'CSP Generator',
    description: 'Generate Content Security Policy headers',
    category: 'Dev',
    icon: Shield,
    route: '/tools/csp-generator',
    metaTitle: 'CSP Generator: Create Content Security Policy Headers Online',
    metaDescription: 'Generate Content Security Policy (CSP) headers to mitigate XSS and data injection attacks. Define source directives for scripts, styles, and more. Free CSP tool.',
    keywords: ['csp generator', 'content security policy', 'xss protection', 'security headers', 'csp header creator', 'csp meta tag', 'web security policy', 'generate csp', 'content security policy builder']
  },
  {
    id: 'webmanifest-generator',
    name: 'Webmanifest Generator',
    description: 'Create PWA manifest.json files',
    category: 'Dev',
    icon: Layout,
    route: '/tools/webmanifest-generator',
    metaTitle: 'Webmanifest Generator: Create PWA manifest.json Files Online',
    metaDescription: 'Generate `manifest.json` files for Progressive Web Apps (PWAs). Define app name, icons, theme color, and display mode. Free PWA manifest creator.',
    keywords: ['webmanifest generator', 'manifest.json creator', 'pwa manifest', 'progressive web app', 'pwa icons', 'theme color generator', 'manifest json builder', 'web app manifest', 'generate manifest json']
  },
  {
    id: 'permissions-policy-generator',
    name: 'Permissions Policy',
    description: 'Generate browser feature headers',
    category: 'Dev',
    icon: ShieldAlert,
    route: '/tools/permissions-policy-tool',
    metaTitle: 'Permissions Policy Generator: Create Feature-Policy Headers Online',
    metaDescription: 'Generate Permissions Policy (formerly Feature Policy) headers to control browser features like camera, geolocation, and microphone. Enhance web security. Free online tool.',
    keywords: ['permissions policy', 'feature policy', 'permissions-policy header generator', 'browser security headers', 'camera permission', 'geolocation policy', 'security headers generator', 'web feature control', 'generate permissions policy']
  },
  {
    id: 'meta-tags-analyzer',
    name: 'Meta Tags Analyzer',
    description: 'Audit and inspect HTML meta tags',
    category: 'Dev',
    icon: Search,
    route: '/tools/meta-tags-analyzer',
    metaTitle: 'Free Meta Tags Analyzer: Audit SEO & Social Media Tags Online',
    metaDescription: 'Analyze meta tags for SEO and social media. Inspect Open Graph, Twitter Cards, and HTML meta elements. Get a detailed report instantly.',
    keywords: ['meta tags analyzer', 'seo audit tool', 'open graph checker', 'twitter card validator', 'html meta inspector', 'website meta tags', 'meta description checker', 'title tag analyzer', 'free meta tag tool']
  },

  // Converters
  {
    id: 'unit-converter',
    name: 'Unit Converter',
    description: 'Convert units of measurement',
    category: 'Converters',
    icon: Ruler,
    route: '/tools/unit-converter-tool',
    metaTitle: 'Free Unit Converter: Convert Length, Weight, Temp, Volume Online',
    metaDescription: 'Convert units of measurement instantly. Supports length, weight, temperature, volume, area, and more. Accurate and easy to use.',
    keywords: ['unit converter', 'online unit converter', 'measurement converter', 'length converter', 'weight converter', 'temperature converter', 'volume converter', 'area converter', 'metric to imperial', 'imperial to metric']
  },
  {
    id: 'color-converter',
    name: 'Color Converter',
    description: 'Convert HEX, RGB, HSL colors',
    category: 'Converters',
    icon: Droplet,
    route: '/tools/color-format-converter',
    metaTitle: 'Free Color Converter: HEX, RGB, HSL, CMYK, HSV, XYZ Online',
    metaDescription: 'Convert colors between HEX, RGB, HSL, CMYK, HSV, and XYZ formats instantly. Essential for web design and graphic projects.',
    keywords: ['color converter', 'hex to rgb', 'rgb to hex', 'hsl to rgb', 'cmyk converter', 'hsv converter', 'color format conversion', 'css color converter', 'web color tool', 'color code converter']
  },
  {
    id: 'timestamp-converter',
    name: 'Timestamp Converter',
    description: 'Convert Unix timestamps',
    category: 'Converters',
    icon: Clock,
    route: '/tools/unix-timestamp-converter',
    metaTitle: 'Free Timestamp Converter: Unix Epoch Time to Date/Time Online',
    metaDescription: 'Convert Unix timestamps to human-readable dates and vice versa. Supports epoch time, milliseconds, and various time zones.',
    keywords: ['timestamp converter', 'unix timestamp', 'epoch converter', 'date to timestamp', 'timestamp to date', 'human readable time', 'unix time converter', 'online time converter', 'epoch time to date', 'timestamp calculator']
  },
  {
    id: 'currency-converter',
    name: 'Currency Converter',
    description: 'Convert between currencies',
    category: 'Converters',
    icon: DollarSign,
    route: '/tools/currency-exchange-converter',
    metaTitle: 'Currency Converter: Free Real-time Exchange Rate Calculator',
    metaDescription: 'Convert currencies instantly with live exchange rates. Access over 150 currencies, track historical data, and plan international transactions. Free online tool.',
    keywords: ['currency converter', 'exchange rate calculator', 'money converter', 'usd to eur', 'gbp to usd', 'forex calculator', 'currency exchange rates', 'live currency converter', 'international money converter']
  },

  {
    id: 'character-counter',
    name: 'Character Counter',
    description: 'Count characters, words, and sentences',
    category: 'Text',
    icon: Text,
    route: '/tools/character-counter-tool',
    metaTitle: 'Character Counter: Free Online Word, Letter & Sentence Count',
    metaDescription: 'Count characters, words, sentences, and paragraphs in text instantly. Analyze text length for essays, tweets, or SEO content. Free, no sign-up required.',
    keywords: ['character counter', 'word counter', 'letter count', 'sentence counter', 'text length checker', 'twitter character count', 'seo length checker', 'count spaces', 'paragraph counter', 'online text analyzer'],
    image: '/Tool Pages/Character Counter.png'
  },
  {
    id: 'remove-line-breaks',
    name: 'Remove Line Breaks',
    description: 'Remove or replace line breaks in text',
    category: 'Text',
    icon: RemoveFormatting,
    route: '/tools/remove-line-breaks-tool',
    metaTitle: 'Remove Line Breaks: Free Online Text Cleaner & Formatter',
    metaDescription: 'Remove or replace line breaks, newlines, and paragraph breaks from text instantly. Clean messy text from PDFs or web pages. Free online text formatter.',
    keywords: ['remove line breaks', 'text cleaner', 'remove newlines', 'replace line breaks', 'format text', 'pdf text fix', 'line break remover', 'text formatter', 'strip newlines', 'text de-breaker'],
    image: '/Tool Pages/Remove Line Breaks.png'
  },
  {
    id: 'remove-duplicate-lines',
    name: 'Remove Duplicate Lines',
    description: 'Remove duplicate lines from text',
    category: 'Text',
    icon: Copy,
    route: '/tools/remove-duplicate-lines-tool',
    metaTitle: 'Remove Duplicate Lines: Free Online Text Deduplicator Tool',
    metaDescription: 'Remove duplicate lines from any text or list instantly. Clean data, deduplicate lists, and get unique lines. Free online text deduplicator.',
    keywords: ['remove duplicate lines', 'deduplicate list', 'delete duplicates', 'text deduplicator', 'list cleaner', 'unique lines tool', 'remove repeated lines', 'text uniqueifier', 'online dedupe tool'],
    image: '/Tool Pages/Remove Duplicate Lines.png'
  },
  {
    id: 'text-repeater',
    name: 'Text Repeater',
    description: 'Repeat text multiple times',
    category: 'Text',
    icon: Repeat,
    route: '/tools/text-repeater-generator',
    metaTitle: 'Text Repeater: Repeat Text, Words & Emojis Online Instantly',
    metaDescription: 'Repeat text multiple times with our free online Text Repeater. Generate repeated words, phrases, or emojis for various uses. Instant results, no limits.',
    keywords: ['text repeater', 'repeat text', 'message multiplier', 'repeat words', 'emoji repeater', 'string repeater', 'loop text online', 'text duplicator', 'text generator repeat', 'free text repeater'],
    image: '/Tool Pages/Text Repeater.png'
  },
  {
    id: 'text-sorter',
    name: 'Text Sorter',
    description: 'Sort lines alphabetically A-Z or Z-A',
    category: 'Text',
    icon: ArrowDownAZ,
    route: '/tools/text-list-sorter',
    metaTitle: 'Free Text Sorter Online: Alphabetize, Reverse & Sort by Length',
    metaDescription: 'Sort text lines alphabetically (A-Z, Z-A), reverse order, or by line length instantly. Organize lists, code, or data with this free online text sorter.',
    keywords: ['text sorter', 'alphabetize text online', 'sort list a-z', 'reverse text order', 'sort by line length', 'online list organizer', 'alphabetical order tool', 'sort text z-a'],
    image: '/Tool Pages/Text Sorter.png'
  },
  {
    id: 'slug-generator',
    name: 'Slug Generator',
    description: 'Create SEO-friendly URL slugs',
    category: 'Text',
    icon: Link2,
    route: '/tools/url-slug-generator',
    keywords: ['slug generator', 'url slug', 'seo friendly url', 'text to slug', 'url cleaner', 'slugify', 'link generator', 'permalink creator'],
    image: '/Tool Pages/Slug Generator.png',
    metaTitle: 'Slug Generator: Create SEO Friendly URL Slugs Online',
    metaDescription: 'Free URL Slug Generator. Convert titles to SEO-friendly slugs. Remove special characters, lowercase text, and use hyphens for clean URLs.'
  },
  {
    id: 'html-to-markdown',
    name: 'HTML to Markdown',
    description: 'Convert HTML to Markdown format',
    category: 'Text',
    icon: FileCode,
    route: '/tools/html-to-markdown-converter',
    metaTitle: 'HTML to Markdown Converter – Free Online HTML to MD Tool',
    metaDescription: 'Convert HTML to Markdown format instantly. Preserve structure, links, and images. Client-side processing ensures data privacy. Free, no registration required.',
    keywords: ['html to markdown', 'convert html to md', 'markdown converter', 'html to text', 'html parser', 'turndown service', 'web to markdown', 'online html converter', 'html to markdown tool', 'convert html for github'],
    image: '/Tool Pages/HTML to Markdown.png'
  },
  {
    id: 'reverse-text-generator',
    name: 'Reverse Text',
    description: 'Reverse characters, words, or flip text',
    category: 'Text',
    icon: ArrowLeftRight,
    route: '/tools/reverse-text-generator',
    metaTitle: 'Reverse Text Generator: Flip Words & Mirror Text Online Free',
    metaDescription: 'Reverse text, flip words, or mirror characters instantly with this free online text reverser. Generate backwards text for creative uses or data obfuscation.',
    keywords: ['reverse text', 'mirror text', 'flip text', 'reverse words', 'backwards text generator', 'text flipper online', 'reverse character order', 'text backwards']
  },
  {
    id: 'upside-down-text-generator',
    name: 'Upside Down Text',
    description: 'Flip text upside down using Unicode',
    category: 'Text',
    icon: RotateCw,
    route: '/tools/upside-down-text-generator',
    metaTitle: 'Upside Down Text Generator: Flip Text 180° Online Free',
    metaDescription: 'Flip text upside down using Unicode characters instantly. Generate uʍop ǝpısdn text for social media or creative projects with this free online tool.',
    keywords: ['upside down text', 'flip text 180', 'unicode upside down', 'text flipper', 'uʍop ǝpısdn', 'flip text generator', 'inverted text', 'rotate text']
  },
  {
    id: 'small-text-generator',
    name: 'Small Text',
    description: 'Convert to superscript and subscript',
    category: 'Text',
    icon: Minimize2,
    route: '/tools/small-text-generator',
    metaTitle: 'Small Text Generator: Superscript & Subscript Online Free',
    metaDescription: 'Generate small text using Unicode superscript and subscript characters. Create tiny text for social media, footnotes, or mathematical expressions instantly.',
    keywords: ['small text', 'tiny text generator', 'superscript generator', 'subscript generator', 'mini text', 'unicode small text', 'facebook small text', 'small font generator']
  },
  {
    id: 'zalgo-text-generator',
    name: 'Zalgo Glitch Text',
    description: 'Generate corrupted/glitch text',
    category: 'Text',
    icon: Zap,
    route: '/tools/zalgo-text-generator',
    metaTitle: 'Zalgo Text Generator: Create Glitch & Horror Text Online Free',
    metaDescription: 'Generate Zalgo text, glitch text, or cursed text instantly. Create unsettling, corrupted text for horror themes or unique messages with this free online tool.',
    keywords: ['zalgo text', 'glitch text', 'cursed text', 'scary text generator', 'corrupted text', 'zalgo generator', 'creepy text', 'horror text']
  },
  {
    id: 'strikethrough-text-generator',
    name: 'Strikethrough Text',
    description: 'Add strikethrough to text',
    category: 'Text',
    icon: Strikethrough,
    route: '/tools/strikethrough-text-generator',
    metaTitle: 'Strikethrough Text Generator: Cross Out Text Online Free',
    metaDescription: 'Generate strikethrough text instantly for social media, forums, or notes. Cross out words with a single line. Copy and paste strike-through text easily.',
    keywords: ['strikethrough text generator', 'cross out text', 'strike text online', 'line through text', 'strikethrough font', 'text strikethrough', 'online strikethrough tool', 'generate crossed out text']
  },
  {
    id: 'underline-text-generator',
    name: 'Underline Text',
    description: 'Add various underlines to text',
    category: 'Text',
    icon: Underline,
    route: '/tools/underline-text-generator',
    metaTitle: 'Underline Text Generator: Double & Wavy Lines Online',
    metaDescription: 'Generate various underline text styles like single, double, or wavy lines. Convert text to Unicode underlined characters for social media and web.',
    keywords: ['underline text generator', 'wavy underline', 'double underline text', 'single underline', 'text underline online', 'unicode underline', 'underline font', 'generate underlined text']
  },
  {
    id: 'bold-text-generator',
    name: 'Bold Text',
    description: 'Convert to bold unicode font',
    category: 'Text',
    icon: Bold,
    route: '/tools/bold-text-generator',
    metaTitle: 'Bold Text Generator: Bold Font for Social Media Online',
    metaDescription: 'Generate bold text instantly using Unicode characters for social media, bios, and messages. Convert regular text to bold font online free.',
    keywords: ['bold text generator', 'bold font online', 'unicode bold text', 'facebook bold text', 'instagram bold font', 'bold text converter', 'generate bold text', 'bold letters']
  },
  {
    id: 'italic-text-generator',
    name: 'Italic Text',
    description: 'Convert to italic unicode font',
    category: 'Text',
    icon: Italic,
    route: '/tools/italic-text-generator',
    metaTitle: 'Italic Text Generator: Slanted Text for Bios Online Free',
    metaDescription: 'Generate italic text instantly using Unicode characters for social media, bios, and messages. Convert regular text to slanted font online.',
    keywords: ['italic text generator', 'slanted text online', 'unicode italic text', 'instagram italic font', 'facebook italic text', 'italic text converter', 'generate italic text', 'italic letters']
  },
  {
    id: 'cursive-text-generator',
    name: 'Cursive Text',
    description: 'Convert to script/handwriting style',
    category: 'Text',
    icon: PenTool,
    route: '/tools/cursive-text-generator',
    metaTitle: 'Cursive Text Generator: Free Online Script & Handwriting Fonts',
    metaDescription: 'Generate aesthetic cursive text, script fonts, and handwriting styles instantly. Copy and paste fancy text for social media or designs. Free, no sign-up.',
    keywords: ['cursive text generator', 'script font generator', 'handwriting text converter', 'aesthetic text generator', 'fancy text online', 'calligraphy font generator', 'instagram cursive text', 'copy paste cursive']
  },
  {
    id: 'old-english-text-generator',
    name: 'Old English Text',
    description: 'Convert to Gothic/Fraktur style',
    category: 'Text',
    icon: Crown,
    route: '/tools/old-english-text-generator',
    metaTitle: 'Old English Text Generator: Free Gothic & Fraktur Font Online',
    metaDescription: 'Convert text to Old English, Gothic, or Fraktur styles instantly. Generate blackletter fonts for aesthetic designs or historical themes. Free tool.',
    keywords: ['old english text generator', 'gothic font generator', 'fraktur text converter', 'blackletter font online', 'medieval text generator', 'aesthetic gothic text', 'copy paste old english', 'text to fraktur']
  },

  // Batch 5.2: Ciphers & Utilities
  {
    id: 'rot13-encoder',
    name: 'ROT13 Encoder',
    description: 'Encrypt/Decrypt using ROT13 cipher',
    category: 'Text',
    icon: RefreshCw,
    route: '/tools/rot13-encoder',
    metaTitle: 'ROT13 Encoder & Decoder: Free Online ROT13 Cipher Tool',
    metaDescription: 'Encode and decode text using the ROT13 cipher instantly. Translate ROT13 messages for spoilers or basic obfuscation. Free, no sign-up required.',
    keywords: ['rot13 encoder', 'rot13 decoder', 'rot13 cipher', 'rot13 translator', 'online rot13 tool', 'rot13 converter', 'substitution cipher', 'text obfuscation']
  },
  {
    id: 'caesar-cipher',
    name: 'Caesar Cipher',
    description: 'Shift letters to encrypt messages',
    category: 'Text',
    icon: Lock,
    route: '/tools/caesar-cipher',
    metaTitle: 'Caesar Cipher: Free Online Encrypt & Decrypt Text Tool',
    metaDescription: 'Encrypt and decrypt text using the Caesar cipher with a custom shift value. Translate secret messages and learn about shift ciphers. Free online tool.',
    keywords: ['caesar cipher', 'caesar cipher encoder', 'caesar cipher decoder', 'shift cipher', 'caesar shift tool', 'encrypt text online', 'decrypt secret message', 'cryptography tool']
  },
  {
    id: 'atbash-cipher',
    name: 'Atbash Cipher',
    description: 'Reverse alphabet substitution encryption',
    category: 'Text',
    icon: ArrowLeftRight,
    route: '/tools/atbash-cipher',
    metaTitle: 'Atbash Cipher: Free Online Text Encryption & Decryption Tool',
    metaDescription: 'Encrypt and decrypt text using the Atbash cipher, a simple substitution method. Instantly convert letters to their reverse alphabet counterparts online.',
    keywords: ['atbash cipher', 'atbash decoder', 'atbash encoder', 'substitution cipher', 'reverse alphabet', 'text encryption tool', 'cryptography online', 'ancient cipher', 'atbash converter', 'online cipher tool']
  },
  {
    id: 'vigenere-cipher',
    name: 'Vigenère Cipher',
    description: 'Polyalphabetic substitution encryption',
    category: 'Text',
    icon: Key,
    route: '/tools/vigenere-cipher',
    metaTitle: 'Vigenère Cipher: Free Online Polyalphabetic Encryption Tool',
    metaDescription: 'Encrypt and decrypt text using the Vigenère cipher with a keyword. This polyalphabetic substitution tool enhances security over simple ciphers.',
    keywords: ['vigenere cipher', 'vigenere encoder', 'vigenere decoder', 'polyalphabetic substitution', 'keyword cipher', 'text encryption online', 'cryptography tool', 'vigenere square', 'code maker', 'vigenere calculator']
  },
  {
    id: 'leet-speak-generator',
    name: 'Leet Speak',
    description: 'Convert text to 1337 5p34k',
    category: 'Text',
    icon: Terminal,
    route: '/tools/leet-speak-generator',
    metaTitle: 'Leet Speak Generator: Translate Text to 1337 Online',
    metaDescription: 'Convert normal text to Leet Speak (1337) instantly. Replace letters with numbers and symbols to create hacker-style text online.',
    keywords: ['leet speak generator', '1337 generator', 'leet converter', 'hacker text', '1337 speak', 'leet translator', 'online leet', 'text to leet', 'leet code', 'gaming text']
  },
  {
    id: 'invisible-text-generator',
    name: 'Invisible Text',
    description: 'Generate zero-width invisible characters',
    category: 'Text',
    icon: Ghost,
    route: '/tools/invisible-text-generator',
    metaTitle: 'Invisible Text Generator: Zero-Width Space & Blank Characters',
    metaDescription: 'Generate invisible text using zero-width characters for blank names, empty messages, or hidden spaces online. Copy and paste instantly.',
    keywords: ['invisible text generator', 'blank text', 'zero width space', 'empty message', 'invisible character', 'discord blank name', 'invisible name', 'empty character', 'hidden text', 'blank username']
  },
  {
    id: 'random-word-generator',
    name: 'Random Word Generator',
    description: 'Generate random English words',
    category: 'Text',
    icon: Shuffle,
    route: '/tools/random-word-generator',
    metaTitle: 'Random Word Generator: Nouns, Verbs, Adjectives Online Free',
    metaDescription: 'Generate random words including nouns, verbs, and adjectives instantly. Customize word count and type for writing, games, or brainstorming. Free online tool.',
    keywords: ['random word generator', 'word picker', 'random noun generator', 'random verb generator', 'random adjective generator', 'brainstorming tool', 'pictionary words', 'word list generator', 'free random words']
  },
  {
    id: 'pirate-ipsum-generator',
    name: 'Pirate Ipsum',
    description: 'Generate pirate-themed placeholder text',
    category: 'Text',
    icon: Skull,
    route: '/tools/pirate-ipsum-generator',
    metaTitle: 'Pirate Ipsum Generator: Funny Placeholder Text Online Free',
    metaDescription: 'Generate pirate-themed Lorem Ipsum placeholder text instantly. Customize paragraph and word count for web design or content mockups. Free online tool.',
    keywords: ['pirate ipsum', 'lorem ipsum alternative', 'funny dummy text', 'pirate text generator', 'placeholder text', 'mockup text', 'web design text', 'content filler']
  },
  {
    id: 'palindrome-checker',
    name: 'Palindrome Checker',
    description: 'Check if text reads same backwards',
    category: 'Text',
    icon: ArrowRightLeft,
    route: '/tools/palindrome-checker',
    metaTitle: 'Palindrome Checker: Validate Palindromes Online Fast & Free',
    metaDescription: 'Check if a word, phrase, or number is a palindrome instantly. This online tool ignores spaces, punctuation, and capitalization. Free and fast.',
    keywords: ['palindrome checker', 'is it a palindrome', 'reverse text check', 'palindrome validator', 'word puzzles', 'text reversal tool', 'free palindrome check', 'online palindrome detector']
  },
  {
    id: 'tabs-to-spaces-converter',
    name: 'Tabs to Spaces',
    description: 'Convert indentation tabs ↔ spaces',
    category: 'Text',
    icon: ArrowRightLeft,
    route: '/tools/tabs-to-spaces-converter',
    metaTitle: 'Tabs to Spaces Converter: Fix Indentation Online Free',
    metaDescription: 'Convert tabs to spaces or spaces to tabs for code indentation instantly. Customize tab width and maintain code formatting. Free online tool.',
    keywords: ['tabs to spaces', 'convert tabs to spaces', 'spaces to tabs', 'indentation converter', 'code formatter', 'fix indentation', 'code editor tool', 'online code converter']
  },


  {
    id: 'typing-speed-test',
    name: 'Typing Speed Test',
    description: 'Test your typing speed in WPM',
    category: 'Brain Games',
    icon: Keyboard,
    route: '/tools/wpm-typing-speed-test',
    metaTitle: 'Free Typing Speed Test Online — Check WPM & Accuracy',
    metaDescription: 'Measure your typing speed in Words Per Minute (WPM) and accuracy with this free online typing test. Improve your keyboard skills and track progress. Start typing now.',
    keywords: ['typing speed test', 'wpm test', 'words per minute test', 'online typing test', 'typing accuracy test', 'typing practice', 'keyboard speed test', 'touch typing test']
  },

  // NEW DEVELOPER TOOLS
  {
    id: 'json-to-csv',
    name: 'JSON to CSV',
    description: 'Convert JSON data to CSV format',
    category: 'Dev',
    icon: FileJson,
    route: '/tools/json-to-csv-converter',
    metaTitle: 'JSON to CSV Converter Online — Convert Data Instantly',
    metaDescription: 'Convert JSON data to CSV format online. Transform nested JSON objects and arrays into a flat, tabular CSV structure for spreadsheets. Free and fast.',
    keywords: ['json to csv', 'convert json to csv', 'json to csv converter', 'json to excel', 'json data to csv', 'online json converter', 'data transformation', 'json parser']
  },
  {
    id: 'csv-to-json',
    name: 'CSV to JSON',
    description: 'Convert CSV data to JSON format',
    category: 'Dev',
    icon: Braces,
    route: '/tools/csv-to-json-converter',
    metaTitle: 'CSV to JSON Converter Online — Convert Data Instantly',
    metaDescription: 'Convert CSV data to JSON format online. Transform tabular spreadsheet data into structured JSON objects or arrays. Free and fast conversion.',
    keywords: ['csv to json', 'convert csv to json', 'csv to json converter', 'excel to json', 'csv data to json', 'online csv converter', 'data transformation', 'csv parser']
  },
  {
    id: 'jwt-decoder',
    name: 'JWT Decoder',
    description: 'Decode and inspect JWT tokens instantly',
    category: 'Dev',
    icon: Key,
    route: '/tools/jwt-token-decoder',
    keywords: ['jwt decoder', 'decode jwt', 'json web token decoder', 'jwt debugger', 'jwt parser', 'jwt viewer', 'jwt inspector', 'online jwt tool', 'verify jwt signature'],
    metaTitle: 'JWT Decoder Online — Inspect JSON Web Tokens Free',
    metaDescription: 'Decode and inspect JSON Web Tokens (JWT) online. View header, payload, and verify signature status instantly. Free, client-side JWT debugger.',
  },
  {
    id: 'url-encoder',
    name: 'URL Encoder/Decoder',
    description: 'Encode or decode URL strings',
    category: 'Dev',
    icon: Globe,
    route: '/tools/url-encoder-decoder',
    metaTitle: 'Free URL Encoder & Decoder Online — Percent-Encode URLs',
    metaDescription: 'Encode or decode URL strings with percent-encoding. Handle special characters for web addresses and query parameters. Instant, client-side processing.',
    keywords: ['url encoder', 'url decoder', 'percent encoding', 'url escape', 'encode uri component', 'uri decoder', 'url tool', 'online url encoder', 'decode url online', 'url percent encode']
  },
  {
    id: 'html-minifier',
    name: 'HTML Minifier',
    description: 'Minify HTML code for production',
    category: 'Dev',
    icon: FileCode2,
    route: '/tools/html-minifier-compressor',
    metaTitle: 'Free HTML Minifier Online — Compress Web Code Instantly',
    metaDescription: 'Minify HTML code online to reduce file size and improve page load times. Removes whitespace, comments, and unnecessary characters. Instant, client-side.',
    keywords: ['html minifier', 'minify html', 'compress html', 'optimize html', 'clean html', 'html compressor', 'online html minifier', 'html code optimizer', 'reduce html size']
  },
  {
    id: 'css-minifier',
    name: 'CSS Minifier',
    description: 'Minify CSS code for production',
    category: 'Dev',
    icon: Code,
    route: '/tools/css-minifier-compressor',
    metaTitle: 'Free CSS Minifier Online — Compress Stylesheets Instantly',
    metaDescription: 'Minify CSS code online to reduce stylesheet file size and speed up page rendering. Removes whitespace, comments, and duplicates. Instant, client-side.',
    keywords: ['css minifier', 'minify css', 'css compressor', 'compress css', 'optimize css', 'css cleaner', 'online css minifier', 'reduce css size', 'css code optimizer']
  },
  {
    id: 'js-minifier',
    name: 'JS Minifier',
    description: 'Minify JavaScript code',
    category: 'Dev',
    icon: Braces,
    route: '/tools/javascript-minifier-compressor',
    metaTitle: 'Free JS Minifier Online — Compress JavaScript Instantly',
    metaDescription: 'Minify JavaScript code online to reduce file size and speed up script execution. Removes whitespace, comments, and shortens variable names. Instant, client-side.',
    keywords: ['js minifier', 'minify js', 'javascript compressor', 'compress javascript', 'optimize js', 'js cleaner', 'online js minifier', 'reduce js size', 'javascript code optimizer']
  },
  {
    id: 'dummy-data-generator',
    name: 'Dummy Data Generator',
    description: 'Generate fake data for testing',
    category: 'Dev',
    icon: Database,
    route: '/tools/dummy-data-generator-tool',
    metaTitle: 'Dummy Data Generator: Free Mock JSON, CSV & SQL Data',
    metaDescription: 'Generate realistic dummy data in JSON, CSV, or SQL formats for testing and development. Create custom schemas, download files, and accelerate your workflow.',
    keywords: ['dummy data generator', 'mock data generator', 'random data generator', 'generate json data', 'test data generator', 'csv generator', 'sql data generator', 'fake data api', 'data faker', 'generate test data online']
  },
  {
    id: 'ip-checker',
    name: 'My IP Address Checker',
    description: 'Check your public IP address instantly',
    category: 'Dev',
    icon: Wifi,
    route: '/tools/my-ip-address-checker',
    metaTitle: 'What Is My IP Address? Free Public IPv4 & IPv6 Checker',
    metaDescription: 'Instantly check your public IPv4 and IPv6 address. Identify your ISP, location, and verify VPN status with this free online IP lookup tool.',
    keywords: ['what is my ip', 'my ip address', 'check ip address', 'public ip checker', 'ipv4 lookup', 'ipv6 checker', 'vpn detector', 'network tools', 'show my ip', 'find my ip']
  },
  {
    id: 'user-agent-parser',
    name: 'User-Agent Parser',
    description: 'Parse and analyze user agent strings',
    category: 'Dev',
    icon: Monitor,
    route: '/tools/user-agent-string-parser',
    metaTitle: 'User-Agent Parser: Free Browser, OS & Device Detector',
    metaDescription: 'Parse any User-Agent string to detect browser name, version, operating system, and device type. Analyze UA strings instantly online.',
    keywords: ['user agent parser', 'ua parser', 'parse user agent', 'detect browser', 'user agent analyzer', 'user agent string', 'device detector', 'os detector', 'browser version checker', 'user agent lookup']
  },

  // CALCULATOR TOOLS
  {
    id: 'age-calculator',
    name: 'Age Calculator',
    description: 'Calculate exact age from birthdate',
    category: 'Calculator',
    icon: Calendar,
    route: '/tools/age-calculator-tool',
    metaTitle: 'Age Calculator: Free Online Exact Age & Birthday Tool',
    metaDescription: 'Calculate exact age in years, months, days, and even hours from a birthdate. Find out how many days until your next birthday with this free tool.',
    keywords: ['age calculator', 'calculate age', 'how old am i', 'dob calculator', 'date of birth calculator', 'chronological age', 'birthday calculator', 'age in days', 'age in months', 'age from dob']
  },
  {
    id: 'percentage-calculator',
    name: 'Percentage Calculator',
    description: 'Calculate percentages easily',
    category: 'Calculator',
    icon: Percent,
    route: '/tools/percentage-calculator-tool',
    metaTitle: 'Percentage Calculator: Free Online Percentages & Changes',
    metaDescription: 'Calculate percentages, percent change, and percentage difference instantly. Find \'X percent of Y\' or \'X is what percent of Y\'. Free online tool.',
    keywords: ['percentage calculator', 'calculate percentage', 'percent change calculator', 'percentage difference', 'percent of a number', 'find percentage', 'math percentage tool', 'online percentage calculator']
  },
  {
    id: 'discount-calculator',
    name: 'Discount Calculator',
    description: 'Calculate sale prices and savings',
    category: 'Calculator',
    icon: DollarSign,
    route: '/tools/discount-calculator-tool',
    metaTitle: 'Discount Calculator: Free Online Sale Price & Savings',
    metaDescription: 'Calculate sale prices, savings, and final costs after a discount. Find the percentage off or original price. Free online shopping tool.',
    keywords: ['discount calculator', 'sale price calculator', 'calculate discount', 'percent off calculator', 'savings calculator', 'price reduction tool', 'shopping discount', 'online discount calculator']
  },
  {
    id: 'bmi-calculator',
    name: 'BMI Calculator',
    description: 'Calculate Body Mass Index',
    category: 'Calculator',
    icon: Scale,
    route: '/tools/bmi-calculator-tool',
    metaTitle: 'BMI Calculator: Free Online Body Mass Index Check',
    metaDescription: 'Calculate your Body Mass Index (BMI) instantly using height and weight. Check your BMI category and understand health implications. Free online tool.',
    keywords: ['bmi calculator', 'body mass index', 'calculate bmi', 'bmi check', 'healthy weight calculator', 'bmi formula', 'bmi chart', 'online bmi tool']
  },
  {
    id: 'gpa-calculator',
    name: 'GPA Calculator',
    description: 'Calculate grade point average',
    category: 'Calculator',
    icon: GraduationCap,
    route: '/tools/gpa-calculator-tool',
    metaTitle: 'GPA Calculator: Free Online Semester & Cumulative GPA',
    metaDescription: 'Calculate your Grade Point Average (GPA) for a semester or cumulatively. Input grades and credits to track academic performance. Free online tool.',
    keywords: ['gpa calculator', 'cumulative gpa', 'semester gpa', 'grade point average', 'college gpa', 'gpa score', 'academic gpa', 'online gpa calculator']
  },

  // UTILITY TOOLS
  {
    id: 'qr-generator',
    name: 'QR Code Generator',
    description: 'Free QR Code Generator | Create Custom Logo, Color & WiFi QR Codes',
    category: 'Utility',
    icon: QrCode,
    route: '/tools/qr-code-generator-tool',
    metaTitle: 'Free QR Code Generator Online — Custom QR Codes with Logo',
    metaDescription: 'Generate custom QR codes with logos, colors, and frames for WiFi, URLs, vCards, and more. Create static or dynamic QR codes instantly online. Free, no sign-up.',
    keywords: ['qr code generator', 'free qr code', 'qr code with logo', 'wifi qr code', 'vcard generator', 'permanent qr code', 'custom qr code maker', 'dynamic qr code', 'qr code creator']
  },
  {
    id: 'online-drawing-board',
    name: 'Online Drawing Board',
    description: 'Free online digital whiteboard and sketchpad',
    category: 'Media',
    icon: PenTool,
    route: '/tools/online-drawing-board',
    metaTitle: 'Online Drawing Board: Free Digital Sketchpad & Whiteboard',
    metaDescription: 'Use this free online drawing board as a digital sketchpad or whiteboard. Draw, doodle, and annotate directly in your browser with various tools. No login required.',
    keywords: ['online drawing board', 'digital sketchpad', 'online whiteboard', 'browser drawing tool', 'paint online free', 'digital art tool', 'sketchpad online', 'no login drawing', 'free drawing app']
  },
  {
    id: 'base64-to-image',
    name: 'Base64 to Image',
    description: 'Convert Base64 strings back to images',
    category: 'Image',
    icon: Image,
    route: '/tools/base64-to-image-converter',
    metaTitle: 'Base64 to Image Converter — Decode Base64 to PNG, JPG',
    metaDescription: 'Decode Base64 strings back into image files like PNG, JPG, or GIF instantly. Paste your Base64 data and download the converted image. Free, no upload.',
    keywords: ['base64 to image', 'base64 decoder', 'convert base64 to png', 'base64 to jpg', 'decode base64 image', 'base64 to gif', 'online base64 image converter', 'data uri to image']
  },
  {
    id: 'svg-to-image',
    name: 'SVG to Image Converter',
    description: 'Convert SVG to PNG, JPG, or WEBP with custom scaling',
    category: 'Image',
    icon: FileOutput,
    route: '/tools/svg-to-image-converter',
    metaTitle: 'SVG to Image Converter — Convert SVG to PNG, JPG, WEBP',
    metaDescription: 'Convert SVG vector graphics to raster images like PNG, JPG, or WEBP. Adjust dimensions and background color. Free, high-resolution conversion online.',
    keywords: ['svg to image', 'svg to png', 'svg to jpg', 'convert svg string', 'render svg', 'svg to webp', 'high resolution svg converter', 'transparent png from svg', 'svg to raster']
  },
  {
    id: 'image-watermark-beautifier',
    name: 'Image Watermark & Beautifier',
    description: 'Add watermarks and filters to your images',
    category: 'Image',
    icon: Stamp,
    route: '/tools/image-watermark-beautifier',
    image: '/Tool Pages/Image Watermark.png',
    metaTitle: 'Image Watermark Tool: Protect Photos with Logo & Text (Free)',
    metaDescription: 'Add watermarks to images with text or logos. Apply filters, adjust opacity, and position for copyright protection. Free, online, and instant image branding.',
    keywords: ['image watermark', 'photo watermark', 'add logo to photo', 'copyright image', 'watermark photos free', 'picture branding', 'add text to image', 'watermark maker', 'online image editor', 'visual content protection']
  },
  {
    id: 'image-to-text',
    name: 'Image to Text (OCR)',
    description: 'Extract text from images',
    category: 'Converters',
    icon: FileText,
    route: '/tools/image-to-text-ocr',
    metaTitle: 'Image to Text Converter: Free Online OCR (Extract Text)',
    metaDescription: 'Extract text from images, screenshots, or scanned documents using OCR technology. Convert JPG, PNG, BMP to editable text instantly online and free.',
    keywords: ['image to text', 'ocr converter', 'extract text from image', 'online ocr', 'picture to text', 'screenshot to text', 'client side ocr', 'convert jpg to text', 'scan to text', 'optical character recognition']
  },
  {
    id: 'pdf-to-text',
    name: 'PDF to Text',
    description: 'Extract text from PDF files',
    category: 'PDF',
    icon: FileType,
    route: '/tools/pdf-to-text-converter',
    metaTitle: 'PDF to Text Converter: Extract Selectable Text (Free & Private)',
    metaDescription: 'Extract text from PDF files into editable plain text. Convert PDF to TXT online, client-side, and free for easy content reuse and analysis.',
    keywords: ['pdf to text', 'extract text from pdf', 'convert pdf to txt', 'read pdf text', 'pdf content extractor', 'pdf to notepad', 'copy text from pdf', 'online pdf converter', 'pdf text parser', 'free pdf tool']
  },
  {
    id: 'word-to-pdf',
    name: 'Word to PDF',
    description: 'Convert DOCX files to PDF',
    category: 'PDF',
    icon: FileText,
    route: '/tools/word-to-pdf-converter',
    metaTitle: 'Word to PDF Converter: DOCX to PDF Online (Secure)',
    metaDescription: 'Convert Word documents (DOCX, DOC) to PDF format online. Preserve formatting, fonts, and layouts securely. Free, fast, and client-side conversion.',
    keywords: ['word to pdf', 'docx to pdf', 'convert word to pdf', 'free word converter', 'microsoft word to pdf', 'doc to pdf online', 'save docx as pdf', 'word document to pdf', 'online pdf tool', 'document conversion']
  },
  {
    id: 'excel-to-pdf',
    name: 'Excel to PDF',
    description: 'Convert Excel/CSV to PDF Tables',
    category: 'PDF',
    icon: Table,
    route: '/tools/excel-to-pdf-converter',
    metaTitle: 'Excel to PDF Converter: XLSX & CSV to PDF Table (Free)',
    metaDescription: 'Convert Excel to PDF tables online. Transform XLSX and CSV files into structured PDF documents with accurate formatting. Free, no sign-up required.',
    keywords: ['excel to pdf', 'xlsx to pdf', 'csv to pdf', 'spreadsheet to pdf', 'convert excel to pdf free', 'save excel as pdf', 'table to pdf', 'excel to pdf online', 'convert spreadsheet to pdf', 'excel to pdf converter']
  },
  {
    id: 'txt-to-pdf',
    name: 'TXT to PDF',
    description: 'Convert plain text to PDF',
    category: 'PDF',
    icon: FileText,
    route: '/tools/txt-to-pdf-converter',
    metaTitle: 'TXT to PDF Converter: Text File to PDF Document (Free)',
    metaDescription: 'Convert TXT to PDF online. Transform plain text files into professional PDF documents with customizable formatting. Free, no sign-up required.',
    keywords: ['txt to pdf', 'text to pdf', 'convert text to pdf', 'create pdf from text', 'notepad to pdf', 'text file to pdf', 'save text as pdf', 'txt to pdf converter', 'plain text to pdf', 'text document to pdf']
  },
  {
    id: 'spongebob-mocking-meme-generator',
    name: 'Mocking Spongebob Generator',
    description: 'Create Mocking Spongebob memes instantly',
    category: 'Image',
    icon: Smile,
    route: '/tools/spongebob-mocking-meme-generator',
    metaTitle: 'Mocking Spongebob Meme Generator: Create Text Memes (Free)',
    metaDescription: 'Generate Mocking Spongebob memes instantly. Type your text, convert to alternating caps, and download your custom meme image. Free, no sign-up.',
    keywords: ['spongebob meme', 'mocking spongebob', 'meme generator', 'spongebob text', 'alternating caps', 'spongebob mocking meme', 'create spongebob meme', 'mocking spongebob text', 'meme maker', 'sarcastic spongebob meme']
  },
  {
    id: 'avatar-generator-maker',
    name: 'Avatar Generator',
    description: 'Create custom avatars instantly',
    category: 'Image',
    icon: User,
    route: '/tools/avatar-generator-maker',
    metaTitle: 'Avatar Generator: Create Free Custom Avatars Online',
    metaDescription: 'Create custom avatars online for free. Design unique profile pictures with various styles, features, and colors. No sign-up required.',
    keywords: ['avatar generator', 'create avatar', 'profile picture maker', 'svg avatar', 'free avatar creator', 'custom avatar', 'flat art avatar', 'online avatar maker', 'generate avatar', 'cartoon avatar']
  },
  {
    id: 'retro-ps2-style-filter',
    name: 'Retro PS2 Style Filter',
    description: 'Apply retro PS2/CRT aesthetic to photos',
    category: 'Image',
    icon: GamepadIcon,
    route: '/tools/retro-ps2-style-filter',
    metaTitle: 'Retro PS2 Filter: Convert Photos to PS2 Aesthetic Online Free',
    metaDescription: 'Apply a retro PS2 filter to your photos instantly. Transform images with CRT scanlines, pixelation, and Y2K aesthetic effects online for free.',
    keywords: ['ps2 filter', 'retro photo effect', 'crt effect online', 'y2k aesthetic generator', 'playstation 2 filter', 'ps2 graphics converter', 'pixelated image filter', 'vaporwave photo editor free']
  },
  {
    id: 'photo-to-pixel-art',
    name: 'Photo to Pixel Art',
    description: 'Convert photos to retro pixel art',
    category: 'Image',
    icon: Grid,
    route: '/tools/photo-to-pixel-art-converter',
    metaTitle: 'Photo to Pixel Art Converter: Pixelate Images Online Free',
    metaDescription: 'Convert photos to pixel art instantly. Transform any image into an 8-bit or 16-bit retro pixelated graphic online with adjustable settings.',
    keywords: ['photo to pixel art', 'pixelate image online', '8bit converter', 'pixel art generator free', 'retro image effect', 'image to pixel converter', 'pixel art maker', 'pixelate photo tool'],
    longDescription: 'Transform your photos into retro-style pixel art instantly. Our free online tool allows you to control the pixelation level and apply classic color palettes like GameBoy, NES, and PICO-8. Perfect for creating game assets, avatars, or nostalgic digital art.',
    howToUse: [
      'Upload an image from your device.',
      'Adjust the "Pixel Size" slider to control the level of detail.',
      'Select a color palette (e.g., GameBoy, NES) or keep original colors.',
      'Use the "Mix Ratio" to blend the palette with original colors.',
      'Download your pixel art masterpiece.'
    ],
    benefits: [
      'Instant retro aesthetic conversion',
      'No software installation required',
      'Customizable pixel size and palettes',
      'High-quality PNG export',
      'Free to use with no watermarks'
    ],
    faq: [
      {
        question: 'What image formats are supported?',
        answer: 'We support all common image formats including JPG, PNG, and WebP.'
      },
      {
        question: 'Is my image uploaded to a server?',
        answer: 'No, all processing happens locally in your browser for maximum privacy.'
      },
      {
        question: 'Can I use the pixel art for commercial projects?',
        answer: 'Yes, you own all rights to the images you process with our tool.'
      }
    ]

  },

  {
    id: 'mind-map-maker',
    name: 'Mind Map Maker',
    description: 'Create visual mind maps from text',
    category: 'Utility',
    icon: BrainCircuit,
    route: '/tools/mind-map-maker-online',
    metaTitle: 'Mind Map Maker: Text to Visual Mind Map Generator Free',
    metaDescription: 'Create visual mind maps from text instantly. Organize ideas, brainstorm, and visualize concepts with an auto-layout mind map generator online.',
    keywords: ['mind map maker', 'text to mind map', 'visualize ideas online', 'brainstorming tool free', 'online mind map generator', 'auto layout mind map', 'structured diagram maker', 'concept map generator'],
    image: '/Tool Pages/Mind Map Maker.png'
  },
  {
    id: 'random-number',
    name: 'Random Number Generator',
    description: 'Random Number Generator (RNG) | Pick True Random Numbers & Sequences',
    category: 'Utility',
    icon: Dices,
    route: '/tools/random-number-generator',
    metaTitle: 'Random Number Generator (RNG): Pick Numbers Online Free',
    metaDescription: 'Generate true random numbers and sequences instantly. Pick random integers, decimals, or simulate dice rolls online for free with our RNG tool.',
    keywords: ['random number generator', 'rng tool online', 'random integer picker', 'lottery number generator', 'true random number', 'google number generator', 'randomizer free', 'sequence randomizer']
  },
  {
    id: 'coin-flip',
    name: 'Coin Flip Simulator',
    description: 'Flip a virtual coin to decide Heads or Tails',
    category: 'Utility',
    icon: Coins,
    route: '/tools/coin-flip-simulator',
    metaTitle: 'Free Coin Flip Simulator: Online Heads or Tails Toss',
    metaDescription: 'Flip a virtual coin online for heads or tails decisions. This coin toss simulator uses a cryptographically secure random number generator. Instant, free, no sign-up.',
    keywords: ['coin flip', 'flip a coin', 'heads or tails', 'coin toss', 'online coin flipper', 'random decision maker', 'probability coin', 'virtual coin toss', 'random number generator coin']
  },
  {
    id: 'dice-roller',
    name: 'Dice Roller',
    description: 'Online Dice Roller | 3D Virtual Dice Simulator (D6, D20, DnD)',
    category: 'Utility',
    icon: Dices,
    route: '/tools/dice-roller-simulator',
    metaTitle: 'Free Online Dice Roller: Virtual D6, D20 & DnD Dice',
    metaDescription: 'Roll virtual dice online for D&D, board games, or random numbers. Supports D4, D6, D8, D10, D12, D20, and custom dice. Instant, free, no sign-up.',
    keywords: ['dice roller', 'online dice', 'dnd dice roller', 'virtual dice', 'd20 roller', 'rpg dice simulator', 'roll a die', 'pathfinder dice', 'dice simulator', 'custom dice roller']
  },
  {
    id: 'stopwatch',
    name: 'Stopwatch',
    description: 'Precise stopwatch with lap times',
    category: 'Utility',
    icon: Timer,
    route: '/tools/online-stopwatch-timer',
    metaTitle: 'Free Online Stopwatch: Precise Timer with Lap Times',
    metaDescription: 'A precise online stopwatch for timing events, workouts, or tasks. Features lap time recording, pause/resume, and reset functionality. Instant, free, no sign-up.',
    keywords: ['online stopwatch', 'stopwatch', 'timer', 'lap timer', 'count up timer', 'digital stopwatch', 'time tracker', 'precision timer', 'web stopwatch']
  },

  {
    id: 'countdown-timer',
    name: 'Countdown Timer',
    description: 'Set a countdown to any time',
    category: 'Utility',
    icon: Clock,
    route: '/tools/online-countdown-timer',
    metaTitle: 'Free Online Countdown Timer: Set Alarm & Timer',
    metaDescription: 'Set an online countdown timer with an optional alarm for tasks, cooking, or events. Customize duration, sound, and display. Instant, free, no sign-up.',
    keywords: ['countdown timer', 'online timer', 'timer with alarm', 'set timer', 'countdown clock', 'web timer', 'alarm clock', 'custom countdown', 'event timer']
  },

  // SEO TOOLS
  {
    id: 'meta-tag-generator',
    name: 'Meta Tag Generator',
    description: 'Generate SEO meta tags for websites',
    category: 'SEO',
    icon: Tags,
    route: '/tools/meta-tag-generator-tool',
    metaTitle: 'Free Meta Tag Generator: Create SEO Meta Tags Online',
    metaDescription: 'Generate SEO meta tags for your website with our free online tool. Create meta titles, descriptions, and keywords for better search engine visibility. Improve your site\'s ranking.',
    keywords: ['meta tag generator', 'seo meta tags', 'meta description generator', 'create meta tags', 'html meta tags', 'meta title generator', 'website meta tags', 'free meta tag tool']
  },
  {
    id: 'open-graph-generator',
    name: 'Open Graph Generator',
    description: 'Generate Open Graph tags for social sharing',
    category: 'SEO',
    icon: Eye,
    route: '/tools/open-graph-tag-generator',
    metaTitle: 'Free Open Graph Generator: Create OG Tags Online',
    metaDescription: 'Generate Open Graph (OG) tags for social media sharing. Create OG titles, descriptions, images, and URLs for Facebook, Twitter, and LinkedIn. Improve social media previews.',
    keywords: ['open graph generator', 'og tag generator', 'facebook open graph', 'twitter card generator', 'social media meta tags', 'og preview', 'open graph protocol', 'meta tags for social media']
  },
  {
    id: 'utm-builder',
    name: 'UTM Link Builder',
    description: 'Create and track marketing campaigns with UTM parameters',
    category: 'SEO',
    icon: Link2,
    route: '/tools/utm-link-builder',
    metaTitle: 'Free UTM Link Builder: Google Analytics URL Generator',
    metaDescription: 'Generate UTM tracking links for Google Analytics campaigns. Create custom URLs with source, medium, campaign, term, and content parameters. Track marketing performance.',
    keywords: ['utm link builder', 'google analytics url builder', 'utm generator', 'campaign url builder', 'ga4 url builder', 'tracking link generator', 'marketing url builder', 'utm parameter tool']
  },
  {
    id: 'emi-calculator',
    name: 'EMI Loan Calculator',
    description: 'Calculate monthly loan payments',
    category: 'Calculator',
    icon: Calculator,
    route: '/tools/emi-loan-calculator',
    metaTitle: 'Free EMI Calculator: Calculate Monthly Loan Payments Online',
    metaDescription: 'Calculate your Equated Monthly Installment (EMI) for loans. Determine monthly payments for home, car, or personal loans with interest rates and tenure. Plan your finances.',
    keywords: ['emi calculator', 'loan calculator', 'calculate emi', 'home loan emi', 'car loan calculator', 'monthly installment', 'interest calculator', 'loan payment calculator']
  },
  {
    id: 'barcode-generator',
    name: 'Barcode Generator',
    description: 'Free Barcode Generator | Create EAN-13, UPC, Code 128 & 39',
    category: 'Utility',
    icon: QrCode,
    route: '/tools/barcode-generator-tool',
    metaTitle: 'Free Barcode Generator: EAN-13, UPC, Code 128 & 39 Online',
    metaDescription: 'Generate EAN-13, UPC-A, Code 128, and Code 39 barcodes instantly online. Customize data, size, and format. Download high-resolution images for free.',
    keywords: ['barcode generator', 'create barcode', 'ean-13 generator', 'upc-a generator', 'code 128 generator', 'code 39 generator', 'free barcode maker', 'inventory barcode creator', 'product barcode', 'shipping label barcode']
  },
  {
    id: 'random-name-picker',
    name: 'Random Name Picker',
    description: 'Random Name Picker | Wheel of Names & Raffle Winner Generator',
    category: 'Utility',
    icon: Users,
    route: '/tools/random-name-picker-tool',
    metaTitle: 'Random Name Picker: Wheel of Names & Raffle Winner Generator',
    metaDescription: 'Select random names from a list using a virtual wheel or list picker. Ideal for raffles, giveaways, and decision-making. Free and instant.',
    keywords: ['random name picker', 'pick a winner', 'name selector', 'random chooser', 'raffle picker', 'random name generator', 'contest winner picker', 'wheel of names', 'online name selector', 'draw names']
  },
  {
    id: 'calorie-calculator',
    name: 'Calorie Calculator',
    description: 'Calculate daily calorie needs',
    category: 'Calculator',
    icon: Heart,
    route: '/tools/calorie-calculator-tool',
    metaTitle: 'Calorie Calculator: Daily Needs for Weight Loss & Gain',
    metaDescription: 'Calculate your daily calorie needs for maintenance, weight loss, or gain. Determines BMR, TDEE, and macro targets based on activity level.',
    keywords: ['calorie calculator', 'daily calorie needs', 'bmr calculator', 'tdee calculator', 'weight loss calculator', 'weight gain calculator', 'maintenance calories', 'calorie counter', 'metabolic rate calculator', 'calorie intake']
  },
  {
    id: 'image-resizer',
    name: 'Image Resizer',
    description: 'Resize images to custom dimensions',
    category: 'Image',
    icon: Maximize2,
    route: '/tools/image-resizer-tool',
    image: '/Tool Pages/Image Resizer.png',
    metaTitle: 'Image Resizer: Resize Images Online (Free & Fast)',
    metaDescription: 'Resize images to custom dimensions or specific aspect ratios online. Adjust width, height, and file size for web, social media, or print. Free.',
    keywords: ['image resizer', 'resize image', 'photo resizer', 'change image size', 'scale image', 'resize image pixels', 'change photo dimensions', 'resize for instagram', 'compress image', 'online image scaler']
  },
  {
    id: 'robots-txt-generator',
    name: 'Robots.txt Generator',
    description: 'Create and customize robots.txt files for SEO',
    category: 'SEO',
    icon: FileCode,
    route: '/tools/robots-txt-file-generator',
    metaTitle: 'Free Robots.txt Generator: Create SEO Crawl Rules Online',
    metaDescription: 'Generate a custom robots.txt file to manage search engine crawler access to your website. Control indexing and block specific URLs for better SEO.',
    keywords: ['robots.txt generator', 'create robots.txt', 'robots.txt maker', 'generate robots file', 'seo crawl rules', 'block googlebot', 'robots.txt syntax', 'create a robots txt', 'disallow pages', 'user-agent rules']
  },
  {
    id: 'sitemap-generator',
    name: 'Sitemap Generator',
    description: 'Generate XML sitemap for SEO',
    category: 'SEO',
    icon: FileText,
    route: '/tools/xml-sitemap-generator',
    metaTitle: 'Free XML Sitemap Generator: Create Google Sitemaps Online',
    metaDescription: 'Generate an XML sitemap for your website to help search engines discover and crawl all your important pages. Improve SEO visibility instantly.',
    keywords: ['sitemap generator', 'xml sitemap', 'create sitemap', 'google sitemap', 'seo sitemap', 'site map maker', 'url list to sitemap', 'website sitemap tool', 'generate xml sitemap']
  },
  {
    id: 'readability-checker',
    name: 'Readability Checker',
    description: 'Analyze text readability and complexity',
    category: 'Text',
    icon: BookOpen,
    route: '/tools/readability-checker-tool',
    metaTitle: 'Free Readability Checker: Flesch-Kincaid Grade Level',
    metaDescription: 'Analyze text readability using Flesch-Kincaid, Gunning Fog, and SMOG. Get grade level and reading ease scores to improve content clarity.',
    keywords: ['readability checker', 'flesch kincaid calculator', 'reading level test', 'text complexity', 'grade level checker', 'content analysis', 'writing improver', 'flesch reading ease', 'gunning fog index', 'smog index'],
    image: '/Tool Pages/Readability Checker.png'
  },
  {
    id: 'keyword-density',
    name: 'Keyword Density Analyzer',
    description: 'Analyze keyword frequency in text',
    category: 'SEO',
    icon: Search,
    route: '/tools/keyword-density-analyzer',
    metaTitle: 'Free Keyword Density Analyzer: Content SEO Checker',
    metaDescription: 'Analyze keyword density in your text to optimize for SEO. Check frequency of 1, 2, and 3-word phrases to avoid keyword stuffing.',
    keywords: ['keyword density', 'keyword checker', 'keyword frequency', 'word count', 'seo analysis', 'content analyzer', 'keyword stuffing', 'text analysis tool', 'phrase density', 'keyword counter']
  },
  {
    id: 'serp-preview',
    name: 'SERP Simulator',
    description: 'Preview and optimize how your pages appear in Google Search results',
    category: 'SEO',
    icon: Eye,
    route: '/tools/serp-snippet-preview',
    metaTitle: 'Free SERP Simulator: Google Snippet Preview Tool Online',
    metaDescription: 'Preview Google search results for your title tags and meta descriptions. Optimize your snippets for desktop and mobile devices before publishing. Get higher click-through rates.',
    keywords: ['serp simulator', 'google snippet preview', 'serp preview tool', 'seo meta tag preview', 'title tag checker', 'meta description optimization', 'search result visualizer', 'google serp checker', 'seo preview tool', 'snippet optimizer']
  },
  {
    id: 'simple-budget',
    name: 'Simple Budget Calculator',
    description: 'Track income and expenses',
    category: 'Calculator',
    icon: Wallet,
    route: '/tools/simple-budget-calculator',
    metaTitle: 'Free Simple Budget Calculator — Track Income & Expenses',
    metaDescription: 'Calculate your monthly budget by tracking income and expenses. Understand your financial position and identify areas for savings with this easy-to-use tool. Start managing your money today.',
    keywords: ['budget calculator', 'simple budget', 'expense tracker', 'income tracker', 'personal finance', 'budgeting tool', 'money management', 'monthly budget planner', 'financial calculator', 'free budget tool']
  },
  {
    id: 'pregnancy-calculator',
    name: 'Pregnancy Due Date Calculator',
    description: 'Calculate your baby\'s due date',
    category: 'Calculator',
    icon: Baby,
    route: '/tools/pregnancy-due-date-calculator',
    metaTitle: 'Free Pregnancy Due Date Calculator — EDD & Trimester Tracker',
    metaDescription: 'Calculate your baby\'s estimated due date (EDD) and track trimesters based on your last menstrual period (LMP) or conception date. Plan for your pregnancy journey instantly.',
    keywords: ['pregnancy calculator', 'due date calculator', 'edd calculator', 'baby due date', 'trimester calculator', 'pregnancy weeks', 'conception calculator', 'gestational age calculator', 'lmp due date calculator', 'pregnancy week by week']
  },
  {
    id: 'random-sentence',
    name: 'Random Sentence Generator',
    description: 'Generate random sentences for testing',
    category: 'Text',
    icon: Shuffle,
    route: '/tools/random-sentence-generator',
    metaTitle: 'Free Random Sentence Generator — Writing Prompts & Text',
    metaDescription: 'Generate unique random sentences instantly for creative writing, language practice, or testing. Get diverse sentence structures and vocabulary for any purpose. No sign-up needed.',
    keywords: ['random sentence generator', 'sentence maker', 'writing prompts', 'english practice', 'random text generator', 'creative writing tool', 'sentence creator', 'text generation', 'vocabulary builder', 'sentence structure practice'],
    image: '/Tool Pages/Random Sentence Generator.png'
  },
  {
    id: 'lorem-picsum',
    name: 'Lorem Picsum Generator',
    description: 'Generate placeholder images from Lorem Picsum',
    category: 'Image',
    icon: Image,
    route: '/tools/lorem-picsum-generator',
    image: '/Tool Pages/Lorem Picsum Generator.png',
    metaTitle: 'Free Lorem Picsum Generator: Random Placeholder Images Online',
    metaDescription: 'Generate random placeholder images from Lorem Picsum for web design and development. Specify dimensions, categories, and filters. Instant, no sign-up.',
    keywords: ['lorem picsum generator', 'placeholder images', 'random image generator', 'dummy image', 'web design assets', 'mockup photos', 'unsplash random', 'placeholder photo generator', 'lorem picsum api', 'image placeholder']
  },
  {
    id: 'password-generator',
    name: 'Password Generator',
    description: 'Generate secure random passwords',
    category: 'Utility',
    icon: Key,
    route: '/tools/secure-password-generator',
    metaTitle: 'Free Password Generator: Create Secure, Random Passwords Online',
    metaDescription: 'Generate strong, random passwords with customizable length and character sets. Enhance online security with unique, complex passphrases instantly.',
    keywords: ['password generator', 'strong password', 'secure password', 'random password', 'password maker', 'password creator', 'password security', 'generate password', 'online password tool', 'complex password']
  },
  {
    id: 'text-to-speech',
    name: 'Text to Speech',
    description: 'Convert text to spoken audio',
    category: 'Utility',
    icon: Volume2,
    route: '/tools/text-to-speech-converter',
    metaTitle: 'Free Text to Speech Online: Convert Text to Spoken Audio',
    metaDescription: 'Convert text to natural-sounding spoken audio instantly. Listen to documents, articles, or notes with various voices and languages online.',
    keywords: ['text to speech', 'tts online', 'read text aloud', 'text reader', 'voice generator', 'speech synthesis', 'audio reader', 'convert text to audio', 'online tts', 'text to voice']
  },
  {
    id: 'image-to-base64',
    name: 'Image to Base64',
    description: 'Convert images to Base64 encoded strings',
    category: 'Dev',
    icon: Image,
    route: '/tools/image-to-base64-converter',
    keywords: ['image to base64', 'convert image to string', 'base64 image encoder', 'image data uri', 'embed image css', 'online base64 converter', 'image to data url', 'base64 encoder', 'image to text'],
    metaTitle: 'Free Image to Base64 Converter: Online Data URI Encoder',
    metaDescription: 'Convert images to Base64 encoded strings (Data URIs) for embedding directly into HTML, CSS, or JavaScript. Instant, client-side conversion.'
  },
  {
    id: 'json-validator',
    name: 'JSON Validator',
    description: 'Validate and format JSON data',
    category: 'Dev',
    icon: FileJson,
    route: '/tools/json-validator-tool',
    metaTitle: 'Free JSON Validator Online — Validate, Format & Debug JSON',
    metaDescription: 'Validate JSON data instantly with this free online JSON validator. Check syntax, format code, and debug errors. No data leaves your browser.',
    keywords: ['json validator', 'validate json', 'json linter', 'check json syntax', 'debug json online', 'json error checker', 'json formatter', 'free json tool']
  },
  {
    id: 'cron-generator',
    name: 'Cron Expression Generator',
    description: 'Generate and understand cron expressions',
    category: 'Dev',
    icon: Clock,
    route: '/tools/cron-expression-generator',
    metaTitle: 'Free Cron Expression Generator — Build Crontab Schedules',
    metaDescription: 'Generate cron expressions for scheduling tasks. Understand cron syntax with examples. Build complex crontab schedules instantly.',
    keywords: ['cron generator', 'crontab generator', 'cron expression builder', 'schedule tasks cron', 'cron syntax help', 'cron job generator', 'crontab editor online', 'linux cron schedule']
  },
  {
    id: 'aspect-ratio-calculator',
    name: 'Aspect Ratio Calculator',
    description: 'Calculate and convert aspect ratios',
    category: 'Calculator',
    icon: Ratio,
    route: '/tools/aspect-ratio-calculator-tool',
    keywords: ['aspect ratio calculator', 'calculate aspect ratio', 'image aspect ratio', 'video aspect ratio', 'screen ratio calculator', '16:9 calculator', '4:3 ratio calculator', 'resize image ratio'],
    metaTitle: 'Free Aspect Ratio Calculator — Image & Video Dimensions',
    metaDescription: 'Calculate aspect ratios for images, videos, or screens. Find missing dimensions and scale content accurately. Free online tool.'
  },

  {
    id: 'screen-resolution-checker',
    name: 'Screen Resolution Checker',
    description: 'Check your screen and viewport dimensions',
    category: 'Utility',
    icon: Monitor,
    route: '/tools/screen-resolution-checker-tool',
    keywords: ['screen resolution checker', 'what is my resolution', 'viewport size checker', 'screen dimension test', 'monitor resolution check', 'device pixel ratio', 'browser window size', 'display resolution online'],
    metaTitle: 'Free Screen Resolution Checker — Viewport & Monitor Size',
    metaDescription: 'Check your screen resolution, viewport size, and pixel density instantly. Get precise display dimensions for any device. Free online tool.'
  },
  {
    id: 'markdown-table-generator',
    name: 'Markdown Table Generator',
    description: 'Create markdown tables visually',
    category: 'Text',
    icon: Table,
    route: '/tools/markdown-table-generator-tool',
    metaTitle: 'Markdown Table Generator: Free Online Visual Editor',
    metaDescription: 'Create Markdown tables online with a visual editor. Build tables for GitHub READMEs, documentation, or web content. Supports CSV import. Free, no sign-up.',
    keywords: ['markdown table generator', 'online markdown table editor', 'github markdown table', 'readme table generator', 'csv to markdown table', 'markdown table builder', 'visual markdown table', 'free markdown table tool'],
    image: '/Tool Pages/Markdown Table Generator.png'
  },
  {
    id: 'bitwise-calculator',
    name: 'Bitwise Calculator',
    description: 'Perform bitwise operations on numbers',
    category: 'Dev',
    icon: Binary,
    route: '/tools/bitwise-calculator-tool',
    metaTitle: 'Bitwise Calculator Online: AND, OR, XOR, NOT, Shifts Free',
    metaDescription: 'Perform bitwise operations (AND, OR, XOR, NOT, shifts) on binary, decimal, or hexadecimal numbers. Convert bases and visualize bit manipulations instantly online.',
    keywords: ['bitwise calculator', 'binary calculator', 'bitwise operations', 'xor calculator', 'bitwise and', 'bit shift calculator', 'programmer calculator', 'binary math', 'hex bitwise', 'decimal bitwise', 'online bitwise tool']
  },
  {
    id: 'number-base-converter',
    name: 'Number Base Converter',
    description: 'Convert numbers between any base',
    category: 'Dev',
    icon: Hash,
    route: '/tools/number-base-converter-tool',
    metaTitle: 'Free Number Base Converter — Binary, Decimal, Hex, Octal Online',
    metaDescription: 'Convert numbers between any base system (binary, decimal, hexadecimal, octal, etc.) instantly. Supports custom bases up to 36. Accurate conversions for developers and students.',
    keywords: ['number base converter', 'binary to decimal converter', 'hex to decimal', 'octal to binary', 'radix converter', 'base conversion tool', 'custom base converter', 'online number converter', 'binary calculator', 'decimal to hex', 'base 10 to base 2', 'base 16 converter']
  },
  {
    id: 'epoch-converter',
    name: 'Epoch Converter',
    description: 'Convert between Unix timestamps and dates',
    category: 'Dev',
    icon: Clock,
    route: '/tools/epoch-timestamp-converter',
    metaTitle: 'Epoch Timestamp Converter | Unix Time to Date & Date to Epoch',
    metaDescription: 'Convert Unix timestamps to human-readable dates and vice versa. Real-time epoch clock, supports batch conversion, seconds/milliseconds/microseconds.',
    keywords: ['unix timestamp converter', 'epoch time', 'unix time', 'date to epoch', 'epoch to date', 'timestamp calculator', 'epoch converter']
  },
  {
    id: 'string-encoder',
    name: 'String Encoder',
    description: 'Encode strings in multiple formats',
    category: 'Dev',
    icon: Code,
    route: '/tools/string-encoder-decoder',
    metaTitle: 'Free String Encoder: URL, Base64, HTML, Hex & URI Encode Online',
    metaDescription: 'Encode strings instantly to URL, Base64, HTML entities, Hexadecimal, and URI components. Protect data and prepare text for web transmission. Free online tool.',
    keywords: ['string encoder', 'text encoder', 'url encode', 'base64 encode', 'html entity encode', 'hex encoder', 'uri encode', 'online string converter', 'encode text free', 'web encoding tool']
  },
  {
    id: 'css-unit-converter',
    name: 'CSS Unit Converter',
    description: 'Convert between CSS units',
    category: 'Dev',
    icon: Ruler,
    route: '/tools/css-unit-converter-tool',
    metaTitle: 'Free CSS Unit Converter: PX to REM, EM, VW, VH & Percent',
    metaDescription: 'Convert CSS units like PX to REM, EM, VW, VH, and Percent instantly. Optimize responsive web design layouts with precise unit conversions. Free online tool.',
    keywords: ['css unit converter', 'px to rem', 'rem calculator', 'em converter', 'vw vh converter', 'css pixel converter', 'responsive design units', 'css unit conversion tool', 'pixel to em', 'rem to px', 'css font size converter']
  },
  {
    id: 'lorem-ipsum-paragraphs',
    name: 'Lorem Ipsum Paragraphs',
    description: 'Generate placeholder text paragraphs',
    category: 'Text',
    icon: FileText,
    route: '/tools/lorem-ipsum-paragraph-generator',
    metaTitle: 'Lorem Ipsum Paragraphs: Generator for Designers',
    metaDescription: 'Generate random Lorem Ipsum paragraphs. Customizable dummy text for web design and mockups. Add HTML tags <p> instantly. Free filler text tool.',
    keywords: ['lorem ipsum paragraphs', 'paragraph generator', 'dummy text generator', 'filler text', 'lipsum', 'design placeholder', 'html text generator'],
    image: '/Tool Pages/Lorem Ipsum Paragraphs.png'
  },
  {
    id: 'html-entity-encoder',
    name: 'HTML Entity Encoder',
    description: 'Encode and decode HTML entities',
    category: 'Dev',
    icon: Code,
    route: '/tools/html-entity-encoder-decoder',
    keywords: ['html entity encoder', 'html entity decoder', 'escape html characters', 'decode html entities', 'xss prevention html', 'special characters html', 'html character encoder', 'online html encoder', 'html escape tool'],
    metaTitle: 'HTML Entity Encoder & Decoder Online — Escape Special Chars',
    metaDescription: 'Encode HTML entities to prevent XSS and display special characters. Decode HTML entities back to text. Free online tool for web developers and content creators.'
  },
  {
    id: 'json-path-finder',
    name: 'JSON Path Finder',
    description: 'Find and query JSON paths',
    category: 'Dev',
    icon: Search,
    route: '/tools/json-path-finder-tool',
    keywords: ['json path finder', 'json navigator', 'find json key', 'json structure viewer', 'json path generator', 'json data extraction', 'jsonpath online', 'query json data'],
    metaTitle: 'Free JSON Path Finder Online — Query & Extract JSON Data',
    metaDescription: 'Query and extract data from JSON structures using JSONPath expressions. Navigate complex JSON, find specific values, and validate paths instantly online.'
  },
  {
    id: 'text-statistics',
    name: 'Text Statistics',
    description: 'Analyze text with detailed statistics',
    category: 'Text',
    icon: BarChart3,
    route: '/tools/text-statistics-analyzer',
    metaTitle: 'Free Text Statistics Tool — Word Count & Readability',
    metaDescription: 'Analyze text for word count, character count, reading time, and speaking time. Get detailed statistics and readability scores instantly online.',
    keywords: ['text statistics', 'word count tool', 'character counter', 'reading time calculator', 'content analysis', 'text analyzer', 'speaking time estimator', 'essay checker', 'readability score'],
    image: '/Tool Pages/Text Statistics.png'
  },
  {
    id: 'box-shadow-generator',
    name: 'Box Shadow Generator',
    description: 'Create CSS box shadows visually',
    category: 'Dev',
    icon: Layers,
    route: '/tools/css-box-shadow-generator',
    metaTitle: 'Free CSS Box Shadow Generator — Multi-Layer Effects',
    metaDescription: 'Generate custom CSS box-shadow effects with multiple layers, inset, blur, and spread. Visually create and copy CSS code instantly online.',
    keywords: ['css box shadow generator', 'box shadow generator', 'css shadow effect', 'multi layer box shadow', 'box-shadow css', 'web design shadow tool', 'css generator', 'drop shadow generator', 'inset shadow css']
  },
  {
    id: 'gradient-generator',
    name: 'Gradient Generator',
    description: 'Create beautiful CSS gradients',
    category: 'Dev',
    icon: Palette,
    route: '/tools/css-gradient-generator',
    metaTitle: 'Free CSS Gradient Generator — Linear, Radial & Conic',
    metaDescription: 'Generate custom CSS gradients: linear, radial, and conic. Visually create multi-color stops and copy cross-browser compatible CSS code instantly.',
    keywords: ['css gradient generator', 'css linear gradient', 'css radial gradient', 'gradient background generator', 'css color gradient', 'web design gradient tool', 'css gradient maker', 'background-image css', 'conic gradient css']
  },
  {
    id: 'yaml-json-converter',
    name: 'YAML <-> JSON Converter',
    description: 'Convert between YAML and JSON formats',
    category: 'Dev',
    icon: ArrowRightLeft,
    route: '/tools/yaml-json-converter-tool',
    metaTitle: 'Free YAML to JSON Converter Online — Validate & Format',
    metaDescription: 'Convert YAML to JSON and JSON to YAML instantly. Validate syntax, format data, and transform configuration files online. Free, no sign-up required.',
    keywords: ['yaml to json converter', 'json to yaml converter', 'yaml converter online', 'json converter online', 'yaml validator', 'json formatter', 'config file converter', 'data format converter', 'yaml parser', 'json parser']
  },
  {
    id: 'chmod-calculator',
    name: 'Chmod Calculator',
    description: 'Calculate Linux file permissions',
    category: 'Dev',
    icon: Shield,
    route: '/tools/chmod-calculator-tool',
    metaTitle: 'Free Chmod Calculator Online — Octal & Symbolic',
    metaDescription: 'Calculate Linux file permissions using octal or symbolic notation. Generate chmod commands for files and directories instantly. Free, no installation.',
    keywords: ['chmod calculator', 'linux permissions calculator', 'file permissions generator', 'octal permissions', 'symbolic permissions', 'unix chmod calculator', 'chmod command generator', 'linux file security', 'permission bits']
  },
  {
    id: 'image-to-pdf',
    name: 'Image to PDF',
    description: 'Convert images to PDF document',
    category: 'PDF',
    icon: FileText,
    route: '/tools/image-to-pdf-converter',
    image: '/Tool Pages/Image to PDF.png',
    keywords: ['image to pdf converter', 'jpg to pdf online', 'png to pdf converter', 'convert image to pdf', 'combine images to pdf', 'photo to pdf', 'create pdf from images', 'gif to pdf', 'free image to pdf'],
    metaTitle: 'Free Image to PDF Converter Online — JPG, PNG, GIF',
    metaDescription: 'Convert JPG, PNG, GIF, and other image formats to PDF documents online. Combine multiple images into a single PDF. Free, no software needed.',
  },
  {
    id: 'pdf-to-image',
    name: 'PDF to Image',
    description: 'Extract PDF pages as images',
    category: 'PDF',
    icon: FileImage,
    route: '/tools/pdf-to-image-converter',
    image: '/Tool Pages/PDF to Image.png',
    keywords: ['pdf to image converter', 'convert pdf to jpg', 'pdf to png online', 'extract images from pdf', 'pdf pages to images', 'save pdf as image', 'pdf to tiff converter', 'free pdf to image', 'pdf image extractor'],
    metaTitle: 'Free PDF to Image Converter Online — JPG, PNG, TIFF',
    metaDescription: 'Convert PDF pages to high-resolution JPG, PNG, or TIFF images online. Extract specific pages or entire documents. Free, no software required.'
  },
  {
    id: 'social-media-image-resizer',
    name: 'Social Media Resizer',
    description: 'Resize and crop images for Instagram, Facebook, and more',
    category: 'Image',
    icon: Smartphone,
    route: '/tools/social-media-image-resizer',
    image: '/Tool Pages/Social Media Resizer.png',
    metaTitle: 'Free Social Media Image Resizer — Crop for Instagram, Facebook',
    metaDescription: 'Resize images for all social media platforms like Instagram, Facebook, LinkedIn, and Twitter. Crop photos to exact dimensions for posts, stories, and banners. Instant, free, and online.',
    keywords: ['social media image resizer', 'instagram image resizer', 'facebook cover resizer', 'linkedin banner size', 'twitter header resizer', 'youtube thumbnail resizer', 'crop image online', 'photo resizer for social media', 'free image cropper', 'social media photo editor']
  },
  {
    id: 'svg-to-png',
    name: 'SVG to PNG Converter',
    description: 'Convert SVG vector files to high-resolution PNG images',
    category: 'Image',
    icon: FileCode,
    route: '/tools/svg-to-png-converter',
    image: '/Tool Pages/SVG to PNG Converter.png',
    metaTitle: 'Free SVG to PNG Converter — Vector to High-Res Raster',
    metaDescription: 'Convert SVG vector graphics to high-resolution PNG images online. Preserve transparency and specify output dimensions for crisp raster images. Instant, free, and client-side conversion.',
    keywords: ['svg to png converter', 'convert svg to png', 'vector to raster converter', 'svg to transparent png', 'high resolution svg to png', 'save svg as png', 'online svg converter', 'svg to image converter', 'png from svg', 'svg export to png']
  },
  {
    id: 'scientific-calculator',
    name: 'Scientific Calculator',
    description: 'Advanced calculator with trig functions',
    category: 'Calculator',
    icon: Calculator,
    route: '/tools/scientific-calculator-online',
    metaTitle: 'Free Scientific Calculator Online — Trig, Log, Exponents',
    metaDescription: 'Perform advanced mathematical calculations with this free online scientific calculator. Includes trigonometry, logarithms, exponents, and statistical functions. Instant and accurate results.',
    keywords: ['scientific calculator online', 'free scientific calculator', 'trigonometry calculator', 'logarithm calculator', 'exponent calculator', 'math calculator', 'engineering calculator', 'statistics calculator', 'online math tools', 'web scientific calculator']
  },
  {
    id: 'wifi-qr-generator',
    name: 'WiFi QR Generator',
    description: 'WiFi QR Code Generator | Share Password & Connect Instantly',
    category: 'Utility',
    icon: Wifi,
    route: '/tools/wifi-qr-code-generator',
    metaTitle: 'Free WiFi QR Code Generator — Share Network Instantly',
    metaDescription: 'Generate a QR code for your WiFi network to share credentials without typing. Connect guests instantly by scanning the code. Supports WPA/WPA2, WEP, and open networks. Free and secure.',
    keywords: ['wifi qr code generator', 'generate wifi qr code', 'share wifi password qr', 'guest wifi qr code', 'scan to connect wifi', 'wifi qr code maker', 'free wifi qr code', 'qr code for wifi network', 'android wifi qr', 'print wifi qr code']
  },
  {
    id: 'sql-formatter',
    name: 'SQL Formatter',
    description: 'Format, beautify, and standardise SQL queries online',
    category: 'Dev',
    icon: Database,
    route: '/tools/sql-query-formatter',
    metaTitle: 'Free SQL Formatter Online — Beautify & Standardize Queries',
    metaDescription: 'Format SQL queries online for MySQL, PostgreSQL, and SQL Server. Beautify unreadable code, standardize syntax, and improve readability instantly. Free SQL formatter.',
    keywords: ['sql formatter', 'sql beautifier', 'format sql online', 'sql minifier', 'pretty print sql', 'sql query formatter', 'postgres formatter', 'mysql formatter', 'sql server formatter', 'online sql tool']
  },
  {
    id: 'bcrypt-generator',
    name: 'Bcrypt Generator',
    description: 'Generate & Check Bcrypt hashes',
    category: 'Dev',
    icon: Lock,
    route: '/tools/bcrypt-hash-generator',
    metaTitle: 'Free Bcrypt Generator Online — Hash & Verify Passwords',
    metaDescription: 'Generate Bcrypt hashes for passwords with adjustable cost factors. Verify existing Bcrypt hashes securely. Free online Bcrypt generator and checker.',
    keywords: ['bcrypt generator', 'bcrypt hash', 'hash password', 'bcrypt verifier', 'check bcrypt', 'online bcrypt tool', 'password hashing', 'bcrypt calculator', 'bcrypt cost factor', 'generate bcrypt']
  },
  {
    id: 'keycode-info',
    name: 'Keycode Info',
    description: 'Visualize keyboard events',
    category: 'Dev',
    icon: Keyboard,
    route: '/tools/javascript-keycode-info',
    metaTitle: 'Free Keycode Info Online — JavaScript Keyboard Event Viewer',
    metaDescription: 'View JavaScript keyboard event properties like event.key, event.code, and keyCode instantly. Test keyboard inputs and debug event listeners. Free online keycode info.',
    keywords: ['keycode info', 'keyboard events', 'javascript keycode', 'event.key', 'event.code', 'keyboard test', 'js key viewer', 'key event viewer', 'keyboard event properties', 'online keycode checker']
  },
  {
    id: 'git-cheatsheet',
    name: 'Git Cheat Sheet',
    description: 'Interactive Git command generator for all levels',
    category: 'Dev',
    icon: Terminal,
    route: '/tools/git-cheatsheet-generator',
    metaTitle: 'Free Git Cheat Sheet Online — Commands & Generator',
    metaDescription: 'Access an interactive Git cheat sheet with 50+ commands. Generate specific Git commands for common tasks like rebase, merge, and stash. Free online Git guide.',
    keywords: ['git cheat sheet', 'git commands list', 'git generator', 'git rebase command', 'git merge guide', 'git stash', 'git cherry pick', 'advanced git commands', 'git reference', 'learn git commands']
  },
  {
    id: 'screen-recorder',
    name: 'Screen Recorder',
    description: 'Record screen, window, or tab',
    category: 'Media',
    icon: Video,
    route: '/tools/online-screen-recorder',
    metaTitle: 'Free Online Screen Recorder — Capture Screen, Audio & Mic',
    metaDescription: 'Record your screen, window, or browser tab with audio directly online. Capture system sound and microphone input. No downloads or watermarks. Start recording now.',
    keywords: ['screen recorder', 'online screen recorder', 'free screen capture', 'record screen online', 'browser screen recorder', 'no watermark screen recorder', 'capture system audio', 'webm screen recorder', 'desktop recorder']
  },
  {
    id: 'audio-recorder',
    name: 'Voice Recorder',
    description: 'Record voice with visualizer',
    category: 'Media',
    icon: Mic,
    route: '/tools/online-voice-recorder',
    metaTitle: 'Free Online Voice Recorder — Record Audio & Microphone',
    metaDescription: 'Record voice and audio directly from your microphone online. Visualize sound waves in real-time. Save recordings as MP3 or WAV files. No downloads needed.',
    keywords: ['voice recorder', 'online audio recorder', 'mic recorder', 'record voice online', 'sound recorder', 'browser audio recorder', 'dictaphone online', 'free voice recording', 'mp3 recorder']
  },
  {
    id: 'webcam-capture',
    name: 'Webcam Recorder',
    description: 'Record video from your webcam',
    category: 'Media',
    icon: Camera,
    route: '/tools/webcam-capture-online',
    metaTitle: 'Free Online Webcam Recorder — Record Video from Camera',
    metaDescription: 'Record video from your webcam directly online. Capture high-quality footage without software downloads. Save recordings as WebM files. No watermarks.',
    keywords: ['webcam recorder', 'online camera recorder', 'record video from webcam', 'camera capture tool', 'online video recorder', 'webcam video recorder', 'browser webcam recording', 'record webcam online', 'free webcam recorder']
  },
  {
    id: 'youtube-thumbnail',
    name: 'YouTube Thumbnail',
    description: 'Download YouTube thumbnails',
    category: 'Media',
    icon: Youtube,
    route: '/tools/youtube-thumbnail-downloader',
    metaTitle: 'YouTube Thumbnail Downloader — Save HD Covers Instantly',
    metaDescription: 'Download YouTube video thumbnails in various resolutions, including HD. Get the highest quality cover image from any YouTube video URL. Fast and free.',
    keywords: ['youtube thumbnail downloader', 'get youtube thumbnail', 'youtube cover image', 'download video thumbnail', 'yt thumbnail grabber', 'save youtube thumbnail', 'hd youtube thumbnail', 'youtube image downloader']
  },
  {
    id: 'json-to-typescript',
    name: 'JSON to TypeScript',
    description: 'Convert JSON to TS Interface',
    category: 'Dev',
    icon: FileJson,
    route: '/tools/json-to-typescript-converter',
    metaTitle: 'JSON to TypeScript Converter: Generate TS Interfaces Online',
    metaDescription: 'Convert JSON objects into TypeScript interfaces or types instantly. Define data structures for type-safe development. Free online JSON to TS conversion.',
    keywords: ['json to typescript', 'json to ts', 'typescript interface generator', 'json type generator', 'convert json to interface', 'json to types', 'typescript data model', 'json schema to ts']
  },
  {
    id: 'curl-to-fetch',
    name: 'cURL to Fetch',
    description: 'Convert cURL commands to JavaScript Fetch API',
    category: 'Dev',
    icon: Terminal,
    route: '/tools/curl-to-fetch-converter',
    metaTitle: 'cURL to Fetch Converter: Generate JavaScript Fetch API Code',
    metaDescription: 'Convert cURL commands to JavaScript Fetch API code instantly. Translate command-line API requests into browser-compatible fetch calls. Free online tool.',
    keywords: ['curl to fetch', 'curl to js fetch', 'convert curl to javascript', 'fetch api generator', 'curl converter', 'javascript fetch example', 'api request generator', 'command line to js']
  },
  {
    id: 'glassmorphism-generator',
    name: 'Glassmorphism Gen',
    description: 'CSS Frosted Glass Effect',
    category: 'Dev',
    icon: Layers,
    route: '/tools/glassmorphism-css-generator',
    metaTitle: 'Glassmorphism Generator: Create Frosted Glass CSS Online',
    metaDescription: 'Generate Glassmorphism CSS with live preview. Create modern frosted glass effects using `backdrop-filter` and `border-radius`. Free online tool.',
    keywords: ['glassmorphism generator', 'css glass effect', 'frosted glass css', 'backdrop-filter generator', 'css generator', 'glassmorphism css', 'ui design tool', 'css blur effect']
  },
  {
    id: 'clip-path-maker',
    name: 'CSS Clip-Path Maker',
    description: 'Generate CSS shapes',
    category: 'Dev',
    icon: Scissors,
    route: '/tools/css-clip-path-maker',
    metaTitle: 'CSS Clip-Path Maker: Visual Shape Generator Online',
    metaDescription: 'Generate custom CSS `clip-path` shapes visually. Create polygons, circles, ellipses, and insets for unique web layouts. Free online tool.',
    keywords: ['css clip-path generator', 'clip-path maker', 'css shape generator', 'polygon generator', 'css image mask', 'css triangle generator', 'visual css tool', 'css custom shapes']
  },
  {
    id: 'favicon-generator',
    name: 'Favicon Generator',
    description: 'Create .ico and PNG favicons',
    category: 'Media', // Image/Media
    icon: Image,
    route: '/tools/favicon-generator-tool',
    metaTitle: 'Free Favicon Generator: Create ICO & PNG Site Icons Online',
    metaDescription: 'Generate favicons in .ico and PNG formats from any image. Customize size, add transparency, and download your site icons instantly. Free and fast.',
    keywords: ['favicon generator', 'create favicon', 'online icon maker', 'website icon generator', 'apple touch icon generator', 'ico generator', 'favicon converter', 'convert image to favicon', 'png to ico', 'favicon maker', 'site icon creator']
  },
  {
    id: 'signature-pad',
    name: 'Signature Pad',
    description: 'Free Online Signature Pad | Draw & Download Digital Signatures (PNG)',
    category: 'Utility',
    icon: PenTool,
    route: '/tools/online-signature-pad',
    metaTitle: 'Free Online Signature Pad: Draw & Download Digital Signatures',
    metaDescription: 'Draw your digital signature online and download it as a transparent PNG image. Create electronic signatures for documents instantly, no sign-up.',
    keywords: ['signature pad', 'digital signature online', 'draw signature', 'electronic signature', 'online signature maker', 'esign tool', 'transparent signature', 'signature creator', 'free signature tool', 'png signature', 'web signature']
  },
  // Batch 8: Developer Tools
  {
    id: 'json-to-zod',
    name: 'JSON to Zod',
    description: 'Convert JSON objects to Zod schemas',
    category: 'Dev',
    icon: FileJson,
    route: '/tools/json-to-zod-converter',
    metaTitle: 'JSON to Zod: Generate Zod Schemas from JSON Online',
    metaDescription: 'Convert JSON objects into Zod validation schemas instantly. Generate TypeScript-compatible Zod schemas for data validation. Free and client-side.',
    keywords: ['json to zod', 'zod schema generator', 'typescript validation', 'json to ts', 'schema converter', 'zod inference', 'ts validation', 'zod generator', 'data validation', 'json schema to zod', 'zod type inference']
  },
  {
    id: 'css-grid-generator',
    name: 'CSS Grid Generator',
    description: 'Visually create CSS Grid layouts',
    category: 'Dev',
    icon: LayoutGrid,
    route: '/tools/css-grid-generator-tool',
    metaTitle: 'Free CSS Grid Generator: Visually Build CSS Grid Layouts',
    metaDescription: 'Visually create CSS Grid layouts with an interactive generator. Define rows, columns, gaps, and areas. Get instant CSS code. Free and online.',
    keywords: ['css grid generator', 'grid layout maker', 'css grid builder', 'visual css grid', 'web design tool', 'css layout generator', 'css grid maker', 'grid template areas', 'fr unit css grid', 'responsive grid generator', 'online css grid']
  },
  // Batch 8: PDF Tools
  {
    id: 'pdf-merger',
    name: 'PDF Merger',
    description: 'Merge multiple PDF files',
    category: 'PDF',
    icon: Files,
    route: '/tools/pdf-merger-tool',
    image: '/Tool Pages/PDF Merger.png',
    metaTitle: 'PDF Merger Online: Combine Multiple PDFs into One (Free)',
    metaDescription: 'Combine multiple PDF files into a single document online. Securely merge PDFs without uploads, preserving formatting. Free PDF binder tool.',
    keywords: ['pdf merger', 'combine pdf online', 'merge pdf files free', 'join pdf documents', 'pdf binder tool', 'online pdf combiner', 'free pdf merge', 'combine pdfs securely'],
  },
  // Batch 8: Design Tools
  {
    id: 'color-contrast-checker',
    name: 'Color Contrast Checker',
    description: 'Check WCAG accessibility contrast',
    category: 'Design',
    icon: Palette,
    route: '/tools/color-contrast-checker-tool',
    metaTitle: 'Color Contrast Checker: WCAG Accessibility Tool (Free)',
    metaDescription: 'Check color contrast ratios against WCAG 2.1 guidelines (AA & AAA) for web accessibility. Ensure text is readable for all users. Free online tool.',
    keywords: ['color contrast checker', 'wcag contrast tool', 'accessibility checker', 'contrast ratio tester', 'a11y color contrast', 'web accessibility colors', 'color contrast analyzer', 'wcag 2.1 contrast']
  },
  {
    id: 'svg-pattern-generator',
    name: 'SVG Pattern Generator',
    description: 'Create seamless background patterns',
    category: 'Design',
    icon: Shapes,
    route: '/tools/svg-pattern-generator-tool',
    metaTitle: 'SVG Pattern Generator: Create Seamless Backgrounds (Free)',
    metaDescription: 'Create custom SVG patterns for seamless website backgrounds and textures. Generate vector patterns with various shapes and colors. Free online tool.',
    keywords: ['svg pattern generator', 'seamless background maker', 'css pattern generator', 'vector pattern creator', 'svg texture generator', 'website background pattern', 'free svg patterns', 'geometric pattern maker']
  },
  // Batch 8: Utility Tools
  {
    id: 'phone-formatter',
    name: 'Phone Number Formatter',
    description: 'Format and validate phone numbers',
    category: 'Utility',
    icon: Phone,
    route: '/tools/phone-number-formatter',
    metaTitle: 'Phone Number Formatter: Validate & Format Numbers (Free)',
    metaDescription: 'Format and validate international phone numbers to E.164 standard. Parse, clean, and standardize phone numbers instantly. Free online tool.',
    keywords: ['phone number formatter', 'validate phone number', 'e164 format converter', 'international phone number format', 'clean phone numbers', 'msisdn formatter', 'phone number parser', 'standardize phone numbers']
  },
  {
    id: 'credit-card-validator',
    name: 'Credit Card Validator',
    description: 'Validate and identify credit cards',
    category: 'Utility',
    icon: CreditCard,
    route: '/tools/credit-card-validator-tool',
    metaTitle: 'Free Credit Card Validator — Check Card Numbers Online',
    metaDescription: 'Validate credit card numbers instantly using the Luhn algorithm. Identify card type (Visa, Mastercard, Amex) and issuer details. Free online CC validator.',
    keywords: ['credit card validator', 'luhn algorithm check', 'card bin checker', 'validate cc number', 'offline card check', 'cc validator', 'check card type', 'verify credit card', 'credit card number validation', 'free credit card checker', 'online card validation']
  },
  // Batch 8: Image Tools
  {
    id: 'meme-generator',
    name: 'Meme Generator',
    description: 'Create viral memes with custom text',
    category: 'Image',
    icon: Smile,
    route: '/tools/meme-generator-maker',
    metaTitle: 'Meme Generator: Create Custom Memes Online Free & Fast',
    metaDescription: 'Generate custom memes with your own images and text instantly. Add captions, resize, and download memes without watermarks. Free online meme creator.',
    keywords: ['meme generator', 'make a meme', 'meme maker', 'online meme creator', 'add text to image', 'custom meme tool', 'no watermark meme', 'viral meme generator', 'funny meme maker', 'image captioner'],
    image: '/Tool Pages/Meme Generator.png',

  },
  {
    id: 'ascii-art-generator',
    name: 'ASCII Art Generator',
    description: 'Convert images to text art',
    category: 'Image',
    icon: Type,
    route: '/tools/ascii-art-generator-tool',
    image: '/Tool Pages/ASCII Art Generator.png',
    metaTitle: 'Free ASCII Art Generator: Convert Image to Text Art Online',
    metaDescription: 'Convert images to ASCII art instantly. Transform photos into text-based graphics using various character sets. Create retro text art from any picture online.',
    keywords: ['ascii art generator', 'image to ascii', 'text art maker', 'picture to text', 'ascii converter', 'photo to text art', 'online ascii art', 'character art generator']
  },

  // New Design Tools (Batch 9)
  {
    id: 'flexbox-generator',
    name: 'Flexbox Generator',
    description: 'Visual CSS Flexbox builder',
    category: 'Design',
    icon: LayoutGrid,
    route: '/tools/css-flexbox-generator',
    metaTitle: 'Flexbox Generator: Visually Build CSS Flex Layouts Online',
    metaDescription: 'Generate CSS Flexbox layouts visually with our free online Flexbox Generator. Adjust properties like display, justify-content, and align-items. Get instant CSS code. Build responsive designs faster.',
    keywords: ['flexbox generator', 'css flexbox builder', 'visual flexbox', 'css layout generator', 'flexbox playground', 'css flexible box', 'flexbox maker', 'online flexbox tool', 'css flex properties', 'flexbox code generator']
  },
  {
    id: 'svg-blob-generator',
    name: 'SVG Blob Generator',
    description: 'Create extensive unique SVG blobs',
    category: 'Design',
    icon: Shapes,
    route: '/tools/svg-blob-generator-tool',
    metaTitle: 'Free SVG Blob Generator: Create Organic Shapes Online Instantly',
    metaDescription: 'Generate unique SVG blob shapes for web design backgrounds and elements. Customize complexity, points, and randomness. Download as SVG or CSS.',
    keywords: ['svg blob generator', 'organic shapes generator', 'svg background generator', 'blob maker online', 'random svg shape', 'web design shapes', 'free svg generator']
  },
  {
    id: 'fancy-border-radius',
    name: 'Fancy Border Radius',
    description: 'Generate advanced CSS border-radius shapes',
    category: 'Design',
    icon: Square,
    route: '/tools/fancy-border-radius-generator',
    metaTitle: 'Fancy Border Radius Generator: Custom CSS Blob Shapes Online',
    metaDescription: 'Generate advanced CSS border-radius values to create unique blob-like shapes. Visually adjust corners and copy the CSS code instantly for web design.',
    keywords: ['fancy border radius', 'css border-radius generator', 'blob shape css', 'css shape generator', 'custom border radius', 'web design css shapes', 'css blob comparison']
  },
  {
    id: 'css-filter-generator',
    name: 'CSS Filter Generator',
    description: 'Visual editor for CSS image filters',
    category: 'Design',
    icon: Sliders,
    route: '/tools/css-filter-generator-tool',
    metaTitle: 'Free CSS Filter Generator: Visual Image Effects Online',
    metaDescription: 'Generate CSS filter properties visually for images and elements. Adjust blur, brightness, contrast, grayscale, hue-rotate, invert, opacity, saturate, and sepia.',
    keywords: ['css filter generator', 'css image effects', 'visual css filters', 'css blur generator', 'css grayscale filter', 'web design filters', 'hue-rotate css', 'css photo editor online']
  },
  {
    id: 'css-transform-generator',
    name: 'CSS Transform Generator',
    description: '2D/3D CSS Transform builder',
    category: 'Design',
    icon: Move,
    route: '/tools/css-transform-generator-tool',
    metaTitle: 'Free CSS Transform Generator: Visual 2D & 3D Builder Online',
    metaDescription: 'Generate CSS transform properties visually for 2D and 3D effects. Adjust translate, rotate, scale, and skew with a live preview. Copy CSS code instantly.',
    keywords: ['css transform generator', 'css 3d transform', 'css rotate generator', 'css scale generator', 'css skew generator', 'transform origin css', 'css animation tools', 'css transform builder']
  },

  // New Finance & Math Tools (Batch 10)
  {
    id: 'compound-interest-calculator',
    name: 'Compound Interest Calculator',
    description: 'Calculate investment growth over time',
    category: 'Calculator',
    icon: TrendingUp,
    route: '/tools/compound-interest-calculator-tool',
    metaTitle: 'Free Compound Interest Calculator — Online Investment Growth',
    metaDescription: 'Calculate compound interest and investment growth over time. Project future value for savings, loans, and investments with this online calculator.',
    keywords: ['compound interest calculator', 'investment growth calculator', 'future value calculator', 'savings calculator', 'compound interest formula', 'online finance calculator', 'ROI calculator', 'interest compounding']
  },
  {
    id: 'margin-calculator',
    name: 'Margin Calculator',
    description: 'Calculate gross margin, markup, and profit',
    category: 'Calculator',
    icon: Percent,
    route: '/tools/margin-calculator-tool',
    metaTitle: 'Free Margin Calculator — Gross Margin, Markup & Profit',
    metaDescription: 'Calculate gross margin, markup percentage, and profit for products or services. Understand pricing strategies with this online margin calculator.',
    keywords: ['margin calculator', 'markup calculator', 'gross margin', 'profit calculator', 'margin vs markup', 'pricing calculator', 'sales margin', 'profit margin percentage']
  },
  {
    id: 'salary-converter',
    name: 'Salary Converter',
    description: 'Convert hourly wage to annual salary and more',
    category: 'Calculator',
    icon: Briefcase,
    route: '/tools/salary-converter-tool',
    metaTitle: 'Free Salary Converter — Hourly to Annual Wage Online',
    metaDescription: 'Convert hourly wage to annual salary, or annual to hourly, monthly, and weekly rates. Calculate your income with this online salary converter.',
    keywords: ['salary converter', 'hourly to annual salary', 'annual to hourly wage', 'wage converter', 'income calculator', 'hourly rate calculator', 'monthly salary converter', 'weekly pay calculator']
  },
  {
    id: 'sales-tax-calculator',
    name: 'Sales Tax Calculator',
    description: 'Calculate VAT, GST, and Sales Tax',
    category: 'Calculator',
    icon: Receipt,
    route: '/tools/sales-tax-calculator-tool',
    metaTitle: 'Free Sales Tax Calculator — VAT, GST & Tax Online',
    metaDescription: 'Calculate sales tax, VAT, or GST for any amount. Add or remove tax from a price with this online sales tax calculator.',
    keywords: ['sales tax calculator', 'VAT calculator', 'GST calculator', 'add tax to price', 'remove tax from price', 'reverse VAT calculator', 'exclude tax calculator', 'tax percentage calculator']
  },
  {
    id: 'inflation-calculator',
    name: 'Inflation Calculator',
    description: 'Calculate purchasing power over time',
    category: 'Calculator',
    icon: Coins,
    route: '/tools/inflation-calculator-tool',
    metaTitle: 'Inflation Calculator: Free Online Purchasing Power Tool',
    metaDescription: 'Calculate purchasing power over time with our free inflation calculator. Analyze historical money value using CPI data. Understand future costs easily.',
    keywords: ['inflation calculator', 'purchasing power calculator', 'future value of money', 'inflation rate tool', 'money value calculator', 'cpi calculator online', 'historical inflation data', 'cost of living calculator', 'real value of money', 'inflation impact tool']
  },

  // New Text & Privacy Tools (Batch 11)
  {
    id: 'text-cleaner',
    name: 'Text Cleaner & Sanitizer',
    description: 'Remove emojis, special chars, and format text',
    category: 'Text',
    icon: Eraser,
    route: '/tools/text-cleaner-tool',
    metaTitle: 'Free Text Cleaner & Sanitizer — Remove Emojis & Format Text',
    metaDescription: 'Clean text online by removing emojis, special characters, and extra spaces. Format text for readability and consistency. Free, no sign-up required.',
    keywords: ['text cleaner', 'remove emojis', 'text sanitizer', 'string cleaner', 'remove special characters', 'clean text online', 'strip html tags', 'format text', 'text scrubber', 'online text editor'],
    image: '/Tool Pages/Text Cleaner.png'
  },
  {
    id: 'slug-to-text',
    name: 'Slug to Text Converter',
    description: 'Convert URL slugs back to readable text',
    category: 'Converters',
    icon: RefreshCcw,
    route: '/tools/slug-to-text-converter',
    metaTitle: 'Slug to Text: Convert URL Slugs to Readable Text',
    metaDescription: 'Slug to Text Converter tool to transform URL slugs into human-readable sentences. Unslugify URLs instantly. Free online slug decoder.',
    keywords: ['slug to text', 'unslugify', 'slug converter', 'url to text', 'slug decoder', 'clean url slug']
  },
  {
    id: 'binary-to-text',
    name: 'Binary to Text Translator',
    description: 'Convert Binary code to Text and vice versa',
    category: 'Converters',
    icon: Binary,
    route: '/tools/binary-to-text-translator',
    metaTitle: 'Binary to Text Converter Online — Free & Instant Decoder',
    metaDescription: 'Convert binary code to human-readable text instantly. Decode binary strings to ASCII or UTF-8 characters directly in your browser. Free, no sign-up required.',
    keywords: ['binary to text', 'binary converter', 'binary decoder', 'binary translator', 'convert binary to text online', 'binary string to text', 'decode binary code', 'binary to ascii', 'binary to utf8', 'text from binary']
  },
  {
    id: 'morse-code-translator',
    name: 'Morse Code Translator',
    description: 'Text to Morse Code with Audio',
    category: 'Converters',
    icon: Activity,
    route: '/tools/morse-code-translator-tool',
    metaTitle: 'Free Morse Code Translator: Text to Morse & Audio Online',
    metaDescription: 'Convert text to Morse code and decode Morse to text instantly. Generate Morse audio signals and download. Free online Morse code encoder and decoder.',
    keywords: ['morse code translator', 'text to morse', 'morse code audio', 'morse decoder', 'morse code generator', 'morse encoder', 'learn morse code', 'morse code converter', 'online morse tool', 'morse signal generator']
  },
  {
    id: 'subtitle-converter',
    name: 'Subtitle Converter',
    description: 'Convert SRT to VTT and vice versa',
    category: 'Converters',
    icon: Captions,
    route: '/tools/subtitle-converter-tool',
    metaTitle: 'Free Subtitle Converter: SRT to VTT & VTT to SRT Online',
    metaDescription: 'Convert subtitle files between SRT and VTT formats instantly. Ensure compatibility for web videos and media players. Free online subtitle format converter.',
    keywords: ['subtitle converter', 'srt to vtt', 'vtt to srt', 'webvtt converter', 'caption converter', 'subtitle format tool', 'online srt converter', 'online vtt converter', 'subtitle file conversion', 'media caption tool']
  },
  {
    id: 'password-strength-tester',
    name: 'Password Strength Tester',
    description: 'Check password entropy and crack time',
    category: 'Utility',
    icon: ShieldCheck,
    route: '/tools/password-strength-tester-tool',
    metaTitle: 'Free Password Strength Tester: Check Entropy & Crack Time',
    metaDescription: 'Assess your password strength by checking entropy, crack time, and common patterns. Identify weak passwords instantly. Free online password security tool.',
    keywords: ['password strength tester', 'password checker', 'how strong is my password', 'entropy calculator', 'password security tool', 'crack time calculator', 'password meter', 'online password analyzer', 'password vulnerability', 'password entropy']
  },
  {
    id: 'text-encryption',
    name: 'Text Encryption (AES)',
    description: 'Encrypt text with a password',
    category: 'Utility',
    icon: Lock,
    route: '/tools/text-encryption-tool',
    metaTitle: 'Free Text Encryption (AES): Secure AES Encrypt & Decrypt',
    metaDescription: 'Encrypt and decrypt text using AES-256 with a password. Secure your messages and sensitive information instantly. Free online text encryption tool.',
    keywords: ['text encryption', 'aes encryption', 'encrypt text online', 'decrypt aes', 'secure message', 'message encryption', 'aes-256 encryption', 'online cipher tool', 'password encryption', 'data security tool']
  },
  {
    id: 'code-obfuscator',
    name: 'JS Code Obfuscator',
    description: 'Simple Javascript code obfuscator',
    category: 'Dev',
    icon: EyeOff,
    route: '/tools/js-code-obfuscator',
    metaTitle: 'JS Code Obfuscator: Free JavaScript Scrambler Online',
    metaDescription: 'Obfuscate JavaScript code online to deter reverse engineering and protect intellectual property. Scramble JS variables, functions, and strings. Free, no sign-up.',
    keywords: ['js obfuscator', 'javascript obfuscator', 'code scrambler', 'protect javascript', 'js code protection', 'online obfuscator', 'deter reverse engineering', 'javascript security']
  },
  {
    id: 'email-extractor',
    name: 'Email Extractor',
    description: 'Extract email addresses from any text securely',
    category: 'Text',
    icon: Mail,
    route: '/tools/email-extractor-tool',
    metaTitle: 'Email Extractor: Free Online Email Scraper & Finder',
    metaDescription: 'Extract email addresses from any text, documents, or web pages instantly. Find and list all valid emails securely online. Free, no sign-up required.',
    keywords: ['email extractor', 'email scraper', 'extract emails from text', 'bulk email finder', 'email list cleaner', 'find email addresses online', 'email grabber free', 'email parser'],
    image: '/Tool Pages/Email Extractor.png'
  },
  // New Tools - Phase 1: Web & Data (15)
  {
    id: 'xpath-tester',
    name: 'XPath Tester',
    description: 'Test and validate XPath queries',
    category: 'Dev',
    icon: Code2,
    route: '/tools/xpath-tester',
    keywords: ['xpath tester', 'xpath validator', 'test xpath online', 'xml path language', 'web scraping tool', 'evaluate xpath', 'xpath expression tester', 'html xpath'],
    metaTitle: 'XPath Tester: Free Online XPath Evaluator & Validator',
    metaDescription: 'Test and validate XPath queries against XML or HTML documents instantly online. Evaluate XPath expressions and see results in real-time. Free, no sign-up.'
  },
  {
    id: 'html-to-jsx',
    name: 'HTML to JSX',
    description: 'Convert HTML to React JSX',
    category: 'Dev',
    icon: Code,
    route: '/tools/html-to-jsx',
    keywords: ['html to jsx', 'html to react', 'convert html to jsx', 'jsx converter', 'react code generator', 'html to react component', 'online jsx converter', 'html to javascript xml'],
    metaTitle: 'HTML to JSX Converter: Free Online HTML to React',
    metaDescription: 'Convert HTML code to JSX (JavaScript XML) for React components instantly online. Transform HTML attributes and syntax for React. Free, no sign-up.'
  },
  {
    id: 'json-to-graphql',
    name: 'JSON to GraphQL',
    description: 'Generate GraphQL schema from JSON',
    category: 'Dev',
    icon: Database,
    route: '/tools/json-to-graphql',
    keywords: ['json to graphql', 'graphql schema generator', 'json to graphql converter', 'graphql type generator', 'api schema generator', 'json to type definition', 'online graphql tool'],
    metaTitle: 'JSON to GraphQL Schema Generator – Free & Online Converter',
    metaDescription: 'Convert JSON objects into GraphQL schema definitions instantly. Generate types, inputs, and queries for your API development. Free JSON to GraphQL tool.',
  },
  {
    id: 'tailwind-config-gen',
    name: 'Tailwind Config Gen',
    description: 'Generate tailwind.config.js file',
    category: 'Dev',
    icon: Settings,
    route: '/tools/tailwind-config-generator',
    keywords: ['tailwind config generator', 'tailwind theme builder', 'tailwind css customizer', 'tailwind breakpoints generator', 'tailwind color palette', 'tailwind config editor', 'online tailwind tool'],
    metaTitle: 'Tailwind Config Generator – Free Online CSS Theme Builder',
    metaDescription: 'Generate your tailwind.config.js file with ease. Visually customize colors, fonts, breakpoints, and spacing for your Tailwind CSS project.'
  },
  {
    id: 'css-keyframe-gen',
    name: 'CSS Keyframe Gen',
    description: 'Create CSS animations visually',
    category: 'Design',
    icon: Move,
    route: '/tools/css-keyframe-generator',
    keywords: ['css keyframe generator', 'css animation maker', 'keyframe animation tool', 'online css animation', 'css transition generator', 'web animation tool', 'css code generator'],
    metaTitle: 'CSS Keyframe Generator – Free Online Animation Tool',
    metaDescription: 'Create custom CSS keyframe animations visually. Define animation steps, timing, and properties with an interactive editor. Generate CSS code instantly.'
  },
  {
    id: 'css-bezier-gen',
    name: 'Cubic Bezier Generator',
    description: 'Visual cubic-bezier easing generator',
    category: 'Design',
    icon: Activity,
    route: '/tools/css-cubic-bezier-generator',
    keywords: ['css cubic bezier', 'cubic bezier generator', 'css easing tool', 'animation timing function', 'css transition generator', 'web animation tool', 'bezier curve editor', 'css animation curve'],
    metaTitle: 'Cubic Bezier Generator – Free CSS Easing Curve Tool Online',
    metaDescription: 'Visually create custom CSS cubic-bezier timing functions. Adjust curve points to control animation speed and generate CSS code instantly. Free easing tool.'
  },
  {
    id: 'srt-to-vtt',
    name: 'SRT to VTT',
    description: 'Convert subtitles from SRT to VTT',
    category: 'Converters',
    icon: Captions,
    route: '/tools/srt-to-vtt',
    keywords: ['srt to vtt', 'convert srt to vtt', 'srt to webvtt', 'subtitle converter', 'webvtt converter', 'html5 video subtitles', 'caption format converter', 'online srt vtt'],
    metaTitle: 'SRT to VTT Converter: Convert SubRip to WebVTT Online Free',
    metaDescription: 'Convert SRT subtitle files to VTT format for web video players. Preserve timing and styling for HTML5 compatibility. Fast, free, and client-side conversion.',
  },
  {
    id: 'vtt-to-srt',
    name: 'VTT to SRT',
    description: 'Convert subtitles from VTT to SRT',
    category: 'Converters',
    icon: Captions,
    route: '/tools/vtt-to-srt',
    keywords: ['vtt to srt', 'convert vtt to srt', 'webvtt to srt', 'subtitle converter', 'caption tools', 'online vtt srt', 'subrip converter', 'vtt converter'],
    metaTitle: 'VTT to SRT Converter: Convert WebVTT to SubRip Online Free',
    metaDescription: 'Convert WebVTT subtitle files to SubRip (.srt) format. Simplify captions for compatibility with various video players. Fast, free, and client-side conversion.'
  },
  {
    id: 'csv-xml-converter',
    name: 'CSV <-> XML',
    description: 'Convert between CSV and XML formats',
    category: 'Converters',
    icon: ArrowRightLeft,
    route: '/tools/csv-xml-converter',
    keywords: ['csv to xml', 'xml to csv', 'data converter', 'format converter', 'csv xml tool', 'online csv xml', 'convert csv to xml', 'convert xml to csv'],
    metaTitle: 'CSV to XML Converter: Convert Data Formats Online Free',
    metaDescription: 'Convert CSV data to XML or XML to CSV instantly. Structure tabular data into hierarchical XML or flatten XML to CSV. Fast, free, and client-side.',
  },
  {
    id: 'pdf-splitter',
    name: 'PDF Splitter',
    description: 'Extract pages from PDF files',
    category: 'PDF',
    icon: Files,
    route: '/tools/pdf-splitter',
    image: '/Tool Pages/PDF Splitter.png',
    keywords: ['pdf splitter', 'split pdf', 'extract pdf pages', 'cut pdf', 'separate pdf pages', 'pdf extractor', 'remove pdf pages', 'split pdf online', 'free pdf splitter'],
    metaTitle: 'PDF Splitter: Extract Pages & Split PDF Files Online Free',
    metaDescription: 'Split PDF files by page range, extract specific pages, or separate every page into individual PDFs. Fast, free, and client-side processing.',
  },
  {
    id: 'pdf-rotator',
    name: 'PDF Rotator',
    description: 'Rotate PDF pages permanently online',
    category: 'PDF',
    icon: RotateCcw,
    route: '/tools/pdf-rotator',
    image: '/Tool Pages/PDF Rotator.png',
    keywords: ['pdf rotator', 'rotate pdf', 'rotate pdf pages', 'fix pdf orientation', 'turn pdf pages', 'save rotated pdf', 'permanent pdf rotation', 'online pdf tool', 'free pdf rotation'],
    metaTitle: 'Free PDF Rotator: Rotate Pages Permanently Online',
    metaDescription: 'Rotate PDF pages or entire documents permanently online. Fix incorrect orientations and save the adjusted PDF instantly. Secure, no uploads needed.'
  },
  {
    id: 'pdf-watermarker',
    name: 'PDF Watermarker',
    description: 'Add text watermarks to PDF',
    category: 'PDF',
    icon: ShieldCheck,
    route: '/tools/pdf-watermarker',
    image: '/Tool Pages/PDF Watermarker.png',
    keywords: ['pdf watermark', 'add watermark to pdf', 'text watermark pdf', 'protect pdf', 'stamp pdf', 'watermark pdf online', 'pdf copyright', 'secure pdf', 'free pdf watermarker'],
    metaTitle: 'Free PDF Watermarker: Add Text Watermark to PDF Online',
    metaDescription: 'Add custom text watermarks to your PDF documents online. Protect your content with adjustable opacity, position, and font. Secure, no uploads.',
  },
  {
    id: 'heic-to-jpg',
    name: 'HEIC to JPG',
    description: 'Convert HEIC images to JPG',
    category: 'Image',
    icon: Image,
    route: '/tools/heic-to-jpg-converter',
    image: '/Tool Pages/HEIC to JPG.png',
    keywords: ['heic to jpg', 'heic converter', 'convert heic to jpg', 'iphone to jpg', 'heic to jpeg', 'online heic converter', 'batch heic converter', 'free heic converter'],
    metaTitle: 'Free HEIC to JPG Converter: Convert iPhone Photos Online',
    metaDescription: 'Convert HEIC images to JPG format instantly online. Preserve image quality and metadata from iPhone photos. Secure, no uploads needed.'
  },
  {
    id: 'video-to-gif',
    name: 'Video to GIF',
    description: 'Create GIFs from video clips',
    category: 'Media',
    icon: Video,
    route: '/tools/video-to-gif-converter',
    keywords: ['video to gif', 'mp4 to gif', 'video converter', 'make a gif', 'gif maker', 'animated gif', 'webm to gif', 'free video to gif'],
    metaTitle: 'Free Video to GIF Converter: Turn Videos into Animated GIFs',
    metaDescription: 'Convert video clips to animated GIFs online. Create GIFs from MP4, WebM, and more with custom duration and frame rate. Secure, no uploads.'
  },
  {
    id: 'json-to-java',
    name: 'JSON to Java',
    description: 'Generate Java POJO classes from JSON',
    category: 'Dev',
    icon: FileJson,
    route: '/tools/json-to-java',
    keywords: ['json to java', 'java pojo generator', 'json to class converter', 'java bean generator', 'online json to java', 'json to object mapping', 'data class generator', 'json schema to java'],
    metaTitle: 'Free JSON to Java POJO Converter – Generate Class Files Online',
    metaDescription: 'Convert JSON data into Java POJO classes instantly. Generate Java Bean objects with proper data types and getters/setters. Free online JSON to Java class generator.',
  },

  // Phase 2: Hardware & Browser Testing (10)
  {
    id: 'dead-pixel-checker',
    name: 'Dead Pixel Checker',
    description: 'Cycle through solid colors to find stuck or dead pixels on your screen.',
    category: 'Utility',
    icon: Monitor,
    route: '/tools/dead-pixel-checker',
    keywords: ['dead pixel test', 'stuck pixel checker', 'monitor pixel test', 'screen defect checker', 'display pixel test', 'free pixel checker', 'lcd pixel test', 'oled pixel test'],
    metaTitle: 'Free Dead Pixel Checker – Test Monitor for Stuck & Dead Pixels',
    metaDescription: 'Check your screen for dead pixels, stuck pixels, or display imperfections. Cycle through solid colors to identify monitor defects. Free online pixel test.'
  },
  {
    id: 'ghosting-test',
    name: 'UFO Ghosting Test',
    description: 'Test your monitor for ghosting and motion blur issues.',
    category: 'Utility',
    icon: Activity,
    route: '/tools/ghosting-test',
    keywords: ['monitor ghosting test', 'ufo test', 'motion blur test', 'response time test', 'display ghosting checker', '144hz ghosting test', 'monitor blur test', 'free ghosting test'],
    metaTitle: 'Free Monitor Ghosting Test – Check Response Time & Blur',
    metaDescription: 'Test your monitor for ghosting and motion blur with a UFO test. Evaluate display response time and visual clarity. Free online ghosting checker.'
  },
  {
    id: 'gamepad-tester',
    name: 'Gamepad/Controller Tester',
    description: 'Visualize inputs from your connected game controller or joystick.',
    category: 'Utility',
    icon: GamepadIcon,
    route: '/tools/gamepad-tester',
    keywords: ['gamepad tester', 'controller test online', 'joystick tester', 'xbox controller test', 'ps4 controller test', 'game controller input', 'gamepad button test', 'analog stick test'],
    metaTitle: 'Free Gamepad & Controller Tester – Check Inputs Online',
    metaDescription: 'Test your gamepad, joystick, or game controller inputs instantly. Visualize button presses, analog stick movements, and trigger values. Free online tool.'
  },
  {
    id: 'mic-tester',
    name: 'Microphone Tester',
    description: 'Test your microphone and visualize audio input levels.',
    category: 'Media',
    icon: Mic,
    route: '/tools/mic-tester',
    keywords: ['mic test online', 'microphone tester free', 'audio input test', 'sound check tool', 'voice test online', 'webcam mic test', 'headset microphone check', 'browser mic test'],
    metaTitle: 'Free Online Microphone Tester — Check Audio Input Levels',
    metaDescription: 'Test your microphone online instantly. Visualize audio input levels, check sound quality, and troubleshoot mic issues directly in your browser. Free and easy to use.'
  },
  {
    id: 'keyboard-tester',
    name: 'Keyboard Event Viewer',
    description: 'View detailed keyboard event data including key codes and modifiers.',
    category: 'Dev',
    icon: Keyboard,
    route: '/tools/keyboard-tester',
    keywords: ['keyboard tester online', 'key code checker', 'javascript key events', 'keyboard ghosting test', 'key visualizer', 'modifier key test', 'keyboard layout tester', 'key press event'],
    metaTitle: 'Free Keyboard Event Viewer — Key Code & Modifier Tester',
    metaDescription: 'View real-time keyboard event data including key codes, `keyCode`, `code`, and modifier keys. Diagnose keyboard issues and test layouts instantly online.'
  },
  {
    id: 'device-pixel-ratio',
    name: 'Device Pixel Ratio (DPR)',
    description: 'Check your screen\'s pixel density and CSS scaling factors.',
    category: 'Design',
    icon: Scale,
    route: '/tools/device-pixel-ratio',
    keywords: ['device pixel ratio checker', 'dpr test online', 'retina display test', 'screen density checker', 'css pixel ratio', 'physical pixels', 'logical pixels', 'display scaling factor'],
    metaTitle: 'Free Device Pixel Ratio (DPR) Checker — Instant Screen Ratio',
    metaDescription: 'Check your screen\'s Device Pixel Ratio (DPR) instantly. Discover your display\'s physical pixel density and CSS pixel scaling factor online. Free and accurate.',
  },
  {
    id: 'window-size',
    name: 'Browser Window Size',
    description: 'Get precise inner and outer dimensions of your browser window.',
    category: 'Design',
    icon: Maximize2,
    route: '/tools/window-size',
    keywords: ['browser window size', 'viewport size checker', 'screen resolution test', 'window dimensions online', 'inner width height', 'outer width height', 'responsive design helper', 'browser dimensions'],
    metaTitle: 'Free Browser Window Size Checker — Viewport & Screen Dimensions',
    metaDescription: 'Check your browser window\'s inner and outer dimensions, viewport size, and screen resolution instantly. Essential for responsive web design and debugging.'
  },
  {
    id: 'touch-screen-test',
    name: 'Multi-Touch Tester',
    description: 'Test your touch screen for multitouch support and ghost touches.',
    category: 'Utility',
    icon: Fingerprint,
    route: '/tools/touch-screen-test',
    keywords: ['multi-touch tester', 'touch screen test', 'digitizer test online', 'ghost touch test', 'phone screen test', 'tablet display test', 'touch panel diagnostic'],
    metaTitle: 'Multi-Touch Tester Online: Free Touch Screen Test Tool',
    metaDescription: 'Test your touch screen for multi-touch gestures and ghost touches instantly. Identify dead zones and display issues on phones or tablets. Free online touch screen diagnostic.',
  },
  {
    id: 'vibration-tester',
    name: 'Vibration API Tester',
    description: 'Test vibration patterns on mobile devices.',
    category: 'Utility',
    icon: Smartphone, // Re-using Smartphone icon
    route: '/tools/vibration-tester',
    keywords: ['vibration api tester', 'haptic feedback test', 'web vibration api', 'phone vibrator test', 'mobile vibration check', 'device vibration utility', 'vibration pattern tester'],
    metaTitle: 'Vibration API Tester Online: Free Haptic Feedback Tool',
    metaDescription: 'Test device vibration patterns and haptic feedback using the Web Vibration API. Check phone vibrator functionality instantly. Free online utility for developers.',
  },
  {
    id: 'sensor-tester',
    name: 'Device Sensor Tester',
    description: 'Visualize Gyroscope and Accelerometer data from your device.',
    category: 'Utility',
    icon: Move,
    route: '/tools/sensor-tester',
    keywords: ['device sensor tester', 'accelerometer test', 'gyroscope test online', 'mobile motion sensors', 'device orientation api', 'sensor data visualization', 'gravity sensor check'],
    metaTitle: 'Device Sensor Tester: Free Accelerometer & Gyroscope Online',
    metaDescription: 'Visualize real-time accelerometer and gyroscope data from your device. Test motion sensors and orientation online. Free utility for developers and users.'
  },

  // Phase 3: Math, Science & Unit Conversion (10)
  {
    id: 'ppi-calculator',
    name: 'PPI Calculator',
    description: 'Calculate Pixels Per Inch (PPI) density of a display.',
    category: 'Calculator',
    icon: Calculator,
    route: '/tools/ppi-calculator',
    keywords: ['ppi calculator', 'pixels per inch calculator', 'pixel density calculator', 'screen ppi calculator', 'display ppi calculator', 'monitor ppi', 'dpi calculator online'],
    metaTitle: 'PPI Calculator Online: Calculate Pixels Per Inch Free',
    metaDescription: 'Calculate Pixels Per Inch (PPI) and pixel density for any display. Input resolution and screen size to get accurate PPI. Free online screen density calculator.'
  },
  {
    id: 'golden-ratio-calculator',
    name: 'Golden Ratio Calculator',
    description: 'Calculate Golden Ratio sequences (A+B) for design layouts.',
    category: 'Design',
    icon: Ratio,
    route: '/tools/golden-ratio-calculator',
    keywords: ['golden ratio calculator', 'phi calculator', 'golden section calculator', 'design proportion tool', 'typography scale calculator', '1.618 calculator', 'fibonacci ratio', 'golden spiral calculator', 'divine proportion tool'],
    metaTitle: 'Golden Ratio Calculator: Calculate Phi Proportions (1.618)',
    metaDescription: 'Calculate Golden Ratio sequences (A+B) for design layouts and art. Find perfect Phi proportions for typography, images, and UI elements. Free online tool.'
  },
  {
    id: 'percentage-change-calculator',
    name: 'Percentage Change',
    description: 'Calculate the percentage increase or decrease between two values.',
    category: 'Calculator',
    icon: Percent,
    route: '/tools/percentage-change-calculator',
    keywords: ['percentage change calculator', 'percent increase calculator', 'percent decrease calculator', 'growth rate calculator', 'percentage difference', 'change percentage', 'percent change formula', 'percentage variance calculator', 'rate of change calculator'],
    metaTitle: 'Percentage Change Calculator: Calculate Increase & Decrease %',
    metaDescription: 'Calculate the percentage increase or decrease between two values. Determine growth rates, price changes, or performance shifts instantly online.'
  },
  {
    id: 'mean-median-mode',
    name: 'Mean, Median, Mode',
    description: 'Calculate statistical averages (Mean, Median, Mode, Range) of a dataset.',
    category: 'Calculator',
    icon: BarChart3,
    route: '/tools/mean-median-mode',
    keywords: ['mean median mode calculator', 'statistics calculator', 'average calculator', 'central tendency calculator', 'range calculator', 'statistical analysis tool', 'math statistics', 'data analysis calculator', 'descriptive statistics'],
    metaTitle: 'Mean Median Mode Range Calculator: Statistical Analysis Tool',
    metaDescription: 'Calculate the mean, median, mode, and range of a dataset. Analyze central tendency and dispersion for any list of numbers instantly online.'
  },
  {
    id: 'standard-deviation',
    name: 'Standard Deviation',
    description: 'Calculate Standard Deviation and Variance of a data set.',
    category: 'Calculator',
    icon: Activity,
    route: '/tools/standard-deviation',
    keywords: ['standard deviation calculator', 'variance calculator', 'population standard deviation', 'sample standard deviation', 'sigma calculator', 'statistical dispersion', 'data variability tool', 'descriptive statistics', 'spread of data'],
    metaTitle: 'Standard Deviation Calculator: Calculate σ, s & Variance Online',
    metaDescription: 'Calculate population (σ) and sample (s) standard deviation, and variance for a dataset. Analyze data variability and dispersion instantly.'
  },
  {
    id: 'quadratic-solver',
    name: 'Quadratic Equation Solver',
    description: 'Solve Quadratic equations (ax² + bx + c = 0) instantly.',
    category: 'Calculator',
    icon: Braces,
    route: '/tools/quadratic-solver',
    keywords: ['quadratic equation solver', 'quadratic formula calculator', 'solve ax²+bx+c=0', 'quadratic roots calculator', 'discriminant calculator', 'algebra equation solver', 'complex roots calculator', 'parabola roots finder', 'polynomial solver', 'math equation solver'],
    metaTitle: 'Quadratic Equation Solver: Find Roots of ax²+bx+c=0 Online',
    metaDescription: 'Solve quadratic equations (ax² + bx + c = 0) using the quadratic formula. Find real or complex roots instantly. Free online quadratic solver.'
  },
  {
    id: 'gcd-lcm-calculator',
    name: 'GCD & LCM Calculator',
    description: 'Calculate Greatest Common Divisor and Least Common Multiple.',
    category: 'Calculator',
    icon: Calculator, // Re-use calculator
    route: '/tools/gcd-lcm-calculator',
    keywords: ['gcd calculator', 'lcm calculator', 'greatest common divisor calculator', 'least common multiple calculator', 'hcf calculator', 'gcf calculator', 'gcd lcm finder', 'euclidean algorithm', 'prime factorization gcd lcm', 'math calculator'],
    metaTitle: 'GCD & LCM Calculator: Find Greatest Common Divisor & LCM',
    metaDescription: 'Calculate the Greatest Common Divisor (GCD) and Least Common Multiple (LCM) for two or more numbers. Free online HCF and LCM finder.'
  },
  {
    id: 'prime-factorizer',
    name: 'Prime Factorization',
    description: 'Break down any integer into its prime factors.',
    category: 'Calculator',
    icon: Hash,
    route: '/tools/prime-factorizer',
    keywords: ['prime factorization calculator', 'prime factors finder', 'factor tree calculator', 'prime decomposition', 'integer factorization', 'prime number calculator', 'factorize number', 'fundamental theorem of arithmetic', 'prime factor decomposition', 'number theory tool'],
    metaTitle: 'Prime Factorization Calculator: Find Prime Factors of Numbers',
    metaDescription: 'Break down any integer into its prime factors using this free online prime factorization calculator. Find prime decomposition instantly.'
  },
  {
    id: 'matrix-calculator',
    name: 'Matrix Calculator',
    description: 'Calculate Determinant and Inverse of 2x2 and 3x3 matrices.',
    category: 'Calculator',
    icon: LayoutGrid, // Re-use LayoutGrid or Table
    route: '/tools/matrix-calculator',
    keywords: ['matrix calculator', 'determinant calculator', 'matrix inverse calculator', 'linear algebra calculator', '2x2 matrix calculator', '3x3 matrix calculator', 'matrix solver', 'matrix transpose', 'matrix adjoint', 'matrix operations'],
    metaTitle: 'Matrix Calculator: Determinant, Inverse, Transpose (2x2, 3x3)',
    metaDescription: 'Calculate the determinant, inverse, transpose, and adjoint of 2x2 and 3x3 matrices. Free online linear algebra tool for students.'
  },
  {
    id: 'cooking-converter',
    name: 'Cooking Unit Converter',
    description: 'Convert between cups, tablespoons, grams, and milliliters.',
    category: 'Converters',
    icon: Scale,
    route: '/tools/cooking-converter',
    keywords: ['cooking unit converter', 'grams to cups converter', 'cups to grams', 'baking conversion calculator', 'kitchen measurement converter', 'ml to cups', 'tablespoons to grams', 'cooking measurement conversion'],
    metaTitle: 'Cooking Unit Converter: Grams to Cups, ML to Oz & More',
    metaDescription: 'Convert cooking units like grams to cups, milliliters to ounces, and tablespoons to grams instantly. Free online kitchen measurement converter for recipes.'
  },

  // Phase 4: Cognitive & Game Tools (8)
  {
    id: 'reaction-time-test',
    name: 'Reaction Time Test',
    description: 'Measure your visual reaction speed in milliseconds.',
    category: 'Brain Games',
    icon: Zap,
    route: '/tools/reaction-time-test',
    keywords: ['reaction time test', 'reflex test online', 'reaction speed test', 'human benchmark reaction', 'visual reaction time', 'click speed test', 'reflex tester', 'online reaction game'],
    metaTitle: 'Reaction Time Test: Measure Visual Reflexes Online Free',
    metaDescription: 'Measure your visual reaction time in milliseconds with this free online test. Improve reflexes and compare your speed against others. No sign-up needed.'
  },
  {
    id: 'aim-trainer',
    name: 'Aim Trainer',
    description: 'Improve your mouse precision and speed.',
    category: 'Brain Games', // Cognitive/gaming tool
    icon: Crosshair,
    route: '/tools/aim-trainer',
    keywords: ['aim trainer online', 'mouse accuracy trainer', 'fps aim practice', 'aim training tool', 'click accuracy test', 'gaming aim trainer', 'target practice online', 'mouse control game'],
    metaTitle: 'Aim Trainer: Improve Mouse Accuracy & FPS Gaming Skills Free',
    metaDescription: 'Improve mouse accuracy and speed with this free online aim trainer. Practice target acquisition for FPS games and enhance your gaming performance.'
  },
  {
    id: 'chimp-test',
    name: 'Chimp Test',
    description: 'Test your working memory and pattern recognition.',
    category: 'Brain Games',
    icon: BrainCircuit,
    route: '/tools/chimp-test',
    keywords: ['chimp test online', 'working memory test', 'chimpanzee memory game', 'spatial memory test', 'brain training game', 'pattern memory test', 'cognitive assessment', 'number memory test'],
    metaTitle: 'Chimp Test: Working Memory & Pattern Recognition Brain Game',
    metaDescription: 'Test your working memory and pattern recognition with the free online Chimp Test. Challenge your cognitive abilities and track your memory span.'
  },
  {
    id: 'visual-memory-test',
    name: 'Visual Memory Test',
    description: 'Memorize an increasing pattern of tiles on a grid.',
    category: 'Brain Games',
    icon: Grid,
    route: '/tools/visual-memory-test',
    keywords: ['visual memory test', 'spatial memory test', 'pattern memory game', 'brain training game', 'cognitive assessment online', 'tile memory game', 'free memory test'],
    metaTitle: 'Visual Memory Test Online: Spatial Pattern Recognition Game',
    metaDescription: 'Assess your visual memory and spatial recall with this free online pattern recognition game. Memorize tile sequences and track your progress.'
  },
  {
    id: 'sequence-memory-test',
    name: 'Sequence Memory',
    description: 'Memorize a sequence of button presses (Simon-style).',
    category: 'Brain Games',
    icon: ListOrdered,
    route: '/tools/sequence-memory-test',
    keywords: ['sequence memory test', 'simon says game online', 'sequential memory game', 'pattern recall test', 'brain training tool', 'memory order test', 'cognitive assessment'],
    metaTitle: 'Sequence Memory Test Online: Simon-Style Pattern Game',
    metaDescription: 'Test your sequential memory and recall with this free online Simon-style game. Memorize and repeat button press sequences.'
  },
  {
    id: 'number-memory-test',
    name: 'Number Memory',
    description: 'Memorize increasingly long numbers.',
    category: 'Brain Games',
    icon: Hash, // Re-use Hash
    route: '/tools/number-memory-test',
    keywords: ['number memory test', 'digit span test online', 'working memory test', 'numerical memory game', 'cognitive assessment tool', 'memory capacity test', 'free number recall'],
    metaTitle: 'Number Memory Test Online: Digit Span & Working Memory',
    metaDescription: 'Measure your number memory and digit span with this free online test. Recall increasingly long numerical sequences.'
  },
  {
    id: 'verbal-memory-test',
    name: 'Verbal Memory',
    description: 'Test your short-term retention of words.',
    category: 'Brain Games',
    icon: Languages,
    route: '/tools/verbal-memory-test',
    keywords: ['verbal memory test', 'word recognition test', 'language memory game', 'vocabulary memory test', 'word recall game', 'cognitive assessment', 'brain training tool'],
    metaTitle: 'Verbal Memory Test Online: Word Recall & Recognition Game',
    metaDescription: 'Assess your verbal memory and word recall with this free online test. Memorize and identify words from a presented list.'
  },
  {
    id: 'sudoku-solver',
    name: 'Sudoku Solver',
    description: 'Solve any valid Sudoku puzzle instantly.',
    category: 'Utility',
    icon: Grid, // Re-use Grid or similar
    route: '/tools/sudoku-solver',
    keywords: ['sudoku solver online', 'sudoku puzzle solver', '9x9 sudoku solver', 'sudoku solution finder', 'backtracking sudoku solver', 'solve sudoku instantly', 'free sudoku helper', 'sudoku grid solver'],
    metaTitle: 'Sudoku Solver Online: Solve Any 9x9 Puzzle Instantly Free',
    metaDescription: 'Solve any Sudoku puzzle instantly with our online solver. Input your puzzle grid and get the solution using backtracking. Free, no download.'
  },

  // Phase 5: Miscellaneous Utilities (7)
  {
    id: 'bpm-tapper',
    name: 'BPM Tapper',
    description: 'Tap any key to calculate the Beats Per Minute.',
    category: 'Utility', // or Music if existed
    icon: Activity,
    route: '/tools/bpm-tapper',
    keywords: ['bpm tapper online', 'tempo finder tool', 'beats per minute calculator', 'tap bpm online', 'music tempo tapper', 'song bpm finder', 'rhythm tempo tool', 'free bpm counter'],
    metaTitle: 'BPM Tapper Online: Find Song Tempo & Beats Per Minute Free',
    metaDescription: 'Tap any key to calculate Beats Per Minute (BPM) accurately. Find the tempo of music, songs, or your own rhythm instantly. Free, no installation.'
  },
  {
    id: 'metronome',
    name: 'Online Metronome',
    description: 'Accurate audio ticks at a specific BPM.',
    category: 'Utility',
    icon: Activity, // Re-use Activity or Clock
    route: '/tools/metronome',
    keywords: ['online metronome', 'metronome with tap tempo', 'tap bpm metronome', 'digital metronome online', 'rhythm trainer tool', 'music practice metronome', 'free metronome app', 'accented metronome'],
    metaTitle: 'Online Metronome: Tap Tempo, Multi-Sound & Accents Free',
    metaDescription: 'An online metronome with tap tempo, multiple sound options, and beat accents. Practice music with precise rhythmic timing. Free, no download.'
  },
  {
    id: 'guitar-tuner',
    name: 'Guitar Tuner',
    description: 'Microphone-based pitch detection for tuning.',
    category: 'Utility',
    icon: Mic,
    route: '/tools/guitar-tuner',
    keywords: ['guitar tuner online', 'free instrument tuner', 'chromatic tuner online', 'microphone guitar tuner', 'tune guitar online', 'bass guitar tuner', 'ukulele tuner', 'pitch detection tuner'],
    metaTitle: 'Online Guitar Tuner: Free Chromatic Tuner for Instruments',
    metaDescription: 'Tune your guitar or any instrument accurately with our free online chromatic tuner. Uses your microphone for precise pitch detection. No download.'
  },
  {
    id: 'nato-phonetic-translator',
    name: 'NATO Translator',
    description: 'Convert text to "Alpha Bravo Charlie" standard.',
    category: 'Text',
    icon: Languages,
    route: '/tools/nato-phonetic-translator',
    keywords: ['nato phonetic alphabet translator', 'alpha bravo charlie converter', 'military alphabet tool', 'spelling alphabet generator', 'phonetic code translator', 'radio alphabet converter', 'nato phonetic online', 'text to phonetic alphabet'],
    image: '/Tool Pages/NATO Translator.png',
    metaTitle: 'NATO Phonetic Alphabet Translator: Convert Text to Alpha Bravo',
    metaDescription: 'Convert text to NATO phonetic alphabet (Alpha Bravo Charlie) instantly. Encode messages for clear communication over radio. Free online tool.'
  },
  {
    id: 'scrabble-word-finder',
    name: 'Scrabble Word Finder',
    description: 'Find valid words from a set of letters.',
    category: 'Text', // or Game
    icon: Search,
    route: '/tools/scrabble-word-finder',
    keywords: ['scrabble word finder', 'scrabble solver online', 'words with friends helper', 'anagram unscrambler', 'word game cheat', 'scrabble dictionary lookup', 'letter combination solver', 'best scrabble words'],
    image: '/Tool Pages/Scrabble Word Finder.png',
    metaTitle: 'Scrabble Word Finder: Find Words from Letters (Free Online)',
    metaDescription: 'Find all valid Scrabble words from your letters and wildcards instantly. Unscramble anagrams and maximize your score. Free word finder online.'
  },
  {
    id: 'crossword-solver',
    name: 'Crossword Solver',
    description: 'Find words based on known letters and length.',
    category: 'Text',
    icon: Grid,
    route: '/tools/crossword-solver',
    keywords: ['crossword solver online', 'crossword helper tool', 'word pattern finder', 'missing letters solver', 'crossword answer generator', 'word search by pattern', 'crossword puzzle assistant', 'find words with letters'],
    image: '/Tool Pages/Crossword Solver.png',
    metaTitle: 'Crossword Solver: Find Words by Pattern & Missing Letters (Free)',
    metaDescription: 'Solve crossword puzzles by finding words based on known letters, length, and patterns. Fill in missing letters instantly. Free online tool.'
  },
  {
    id: 'qr-code-scanner',
    name: 'QR Code Scanner',
    description: 'Webcam-based QR code reader/decoder.',
    category: 'Utility',
    icon: ScanLine,
    route: '/tools/qr-code-scanner',
    keywords: ['qr code scanner online', 'webcam qr reader', 'scan qr code free', 'qr code decoder', 'online qr scanner', 'image qr code reader', 'barcode scanner online', 'qr code reader webcam'],
    metaTitle: 'QR Code Scanner Online: Scan QR Codes with Webcam (Free)',
    metaDescription: 'Scan QR codes instantly using your webcam or by uploading an image. Decode URLs, text, and contact info. Free online QR code reader.'
  },
  {
    id: 'image-color-picker',
    name: 'Image Color Picker',
    description: 'Pick colors from an image and get their HEX, RGB, and HSL values',
    category: 'Image',
    icon: Eye,
    route: '/tools/image-color-picker',
    keywords: ['image color picker', 'color picker from image', 'extract colors from photo', 'hex color picker online', 'rgb color picker tool', 'hsl color picker', 'get image colors', 'online color extractor', 'photo color analyzer', 'color palette generator'],
    metaTitle: 'Image Color Picker: Extract HEX, RGB, HSL from Photos Online',
    metaDescription: 'Pick colors from an image and get their HEX, RGB, and HSL values instantly. Upload photos to extract color codes for web design or art projects. Free online color picker.',
    image: '/Tool Pages/Image Color Picker.png'
  },
  {
    id: 'color-palette-generator',
    name: 'Color Palette Generator',
    description: 'Generate harmonious color palettes from a base color',
    category: 'Design',
    icon: Palette,
    route: '/tools/color-palette-generator-tool',
    keywords: ['color palette generator', 'color scheme maker', 'palette creator online', 'complementary colors', 'analogous colors', 'triadic color scheme', 'tetradic color palette', 'color theory tool', 'design color generator', 'hex color palette'],
    metaTitle: 'Free Color Palette Generator: Create Harmonious Schemes Online',
    metaDescription: 'Generate harmonious color palettes from a base color using various color theory models. Explore analogous, complementary, triadic, and tetradic schemes. Instant and free.',
    image: '/Tool Pages/Color Palette Generator.png'
  },
  {
    id: 'jigsaw-puzzle-maker',
    name: 'Jigsaw Puzzle Maker',
    description: 'Turn any photo into an interactive jigsaw puzzle game',
    category: 'Brain Games',
    icon: Puzzle,
    route: '/tools/jigsaw-puzzle-maker',
    keywords: ['jigsaw puzzle maker', 'create photo puzzle online', 'custom jigsaw puzzle', 'make puzzle from picture', 'free online puzzle maker', 'interactive photo puzzle', 'jigsaw puzzle game creator'],
    metaTitle: 'Jigsaw Puzzle Maker: Create Custom Photo Puzzles Online Free',
    metaDescription: 'Create custom jigsaw puzzles from any image online. Turn photos into interactive games with adjustable piece counts. Play instantly, no downloads.',
    image: '/Tool Pages/Jigsaw Puzzle Maker.png'
  },

  // New Batch: Statistics Tools
  {
    id: 't-test-calculator',
    name: 'T-Test Calculator',
    description: 'Perform Student\'s t-tests (paired and unpaired)',
    category: 'Science',
    icon: ArrowLeftRight,
    route: '/tools/t-test-calculator',
    keywords: ['t-test calculator', 'student\'s t-test', 'paired t-test', 'unpaired t-test', 'welch\'s t-test', 'p-value calculator', 'statistical significance', 'hypothesis testing', 'two sample t-test', 'degrees of freedom'],
    metaTitle: 'T-Test Calculator: Paired & Unpaired Student\'s t-Test Online',
    metaDescription: 'Calculate Student\'s t-test (paired, unpaired, Welch\'s) with p-value and degrees of freedom. Analyze statistical significance for two sample means. Free online t-test tool.'
  },
  {
    id: 'chi-square-calculator',
    name: 'Chi-Square Calculator',
    description: 'Calculate Chi-Square (χ²) goodness of fit',
    category: 'Science',
    icon: Scale,
    route: '/tools/chi-square-calculator',
    keywords: ['chi-square calculator', 'goodness of fit test', 'chi squared test', 'categorical data analysis', 'statistical hypothesis testing', 'observed vs expected frequencies', 'free chi-square tool', 'online chi-square calculation'],
    metaTitle: 'Free Chi-Square Calculator: Goodness of Fit Test Online',
    metaDescription: 'Calculate Chi-Square (χ²) goodness of fit for categorical data. Determine observed vs. expected frequencies to test hypotheses. Instant results, no sign-up.'
  },
  {
    id: 'correlation-coefficient-calculator',
    name: 'Correlation Calculator',
    description: 'Calculate Pearson\'s Correlation Coefficient (r)',
    category: 'Science',
    icon: TrendingUp,
    route: '/tools/correlation-coefficient-calculator',
    keywords: ['correlation coefficient calculator', 'pearson r calculator', 'statistical correlation', 'linear relationship', 'covariance calculator', 'data analysis tool', 'free correlation online', 'strength of relationship'],
    metaTitle: 'Free Correlation Coefficient Calculator: Pearson\'s r Online',
    metaDescription: 'Calculate Pearson\'s correlation coefficient (r) to quantify the linear relationship between two variables. Determine strength and direction instantly.'
  },
  {
    id: 'linear-regression-calculator',
    name: 'Linear Regression',
    description: 'Find the line of best fit and R-squared',
    category: 'Science',
    icon: TrendingUp,
    route: '/tools/linear-regression-calculator',
    keywords: ['linear regression calculator', 'line of best fit', 'r squared calculator', 'regression analysis', 'slope intercept calculator', 'predictive modeling', 'least squares regression', 'statistical prediction'],
    metaTitle: 'Free Linear Regression Calculator: Line of Best Fit & R²',
    metaDescription: 'Calculate the line of best fit (y = mx + b), R-squared, and correlation for bivariate data. Analyze linear relationships instantly online.'
  },
  {
    id: 'z-score-calculator',
    name: 'Z-Score Calculator',
    description: 'Calculate standard Z-Score from mean and SD',
    category: 'Science',
    icon: BarChart3,
    route: '/tools/z-score-calculator',
    keywords: ['z-score calculator', 'standard score calculator', 'normal distribution', 'statistics calculator', 'probability calculator', 'data standardization', 'free z-score tool', 'statistical analysis'],
    metaTitle: 'Free Z-Score Calculator: Standard Score & Probability Online',
    metaDescription: 'Calculate the Z-score (standard score) for a data point given mean and standard deviation. Find probabilities in a normal distribution.'
  },
  {
    id: 'confidence-interval-calculator',
    name: 'Confidence Interval',
    description: 'Calculate confidence intervals for a mean',
    category: 'Science',
    icon: Braces,
    route: '/tools/confidence-interval-calculator',
    keywords: ['confidence interval calculator', 'margin of error calculator', 'statistics interval', 'sample mean estimate', '95 confidence interval', '99 confidence interval', 't-distribution calculator', 'z-score confidence interval', 'population mean interval', 'statistical significance'],
    metaTitle: 'Confidence Interval Calculator: 95% & 99% Intervals Online',
    metaDescription: 'Calculate confidence intervals for a mean with known or unknown standard deviation. Determine 90%, 95%, or 99% confidence levels instantly. Free online tool.'
  },

  // New Batch: Algebra & Combinatorics
  {
    id: 'permutation-calculator',
    name: 'Permutation Calculator',
    description: 'Calculate nPr permutations (Order matters)',
    category: 'Science',
    icon: ListOrdered,
    route: '/tools/permutation-calculator',
    keywords: ['permutation calculator', 'nPr calculator', 'permutations', 'ordering calculator', 'combinatorics', 'arrangement calculator', 'discrete mathematics', 'probability permutations', 'factorial calculations', 'sequence arrangements'],
    metaTitle: 'Permutation Calculator (nPr): Calculate Orderings Online Free',
    metaDescription: 'Calculate the number of permutations (nPr) for a set of items where order matters. Determine arrangements instantly for selections. Free online tool.'
  },
  {
    id: 'combination-calculator',
    name: 'Combination Calculator',
    description: 'Calculate nCr combinations (Order doesn\'t matter)',
    category: 'Science',
    icon: List,
    route: '/tools/combination-calculator',
    keywords: ['combination calculator', 'nCr calculator', 'combinations', 'selection calculator', 'lottery calculator', 'discrete mathematics combinations', 'probability combinations', 'binomial coefficient', 'subset calculator', 'grouping calculator'],
    metaTitle: 'Combination Calculator (nCr): Calculate Selections Online Free',
    metaDescription: 'Calculate the number of combinations (nCr) for a set of items where order does not matter. Determine selections instantly. Free online tool.'
  },
  {
    id: 'rms-calculator',
    name: 'RMS Calculator',
    description: 'Calculate Root Mean Square (Quadratic Mean)',
    category: 'Science',
    icon: Activity,
    route: '/tools/rms-calculator',
    keywords: ['rms calculator', 'root mean square', 'quadratic mean', 'voltage calculator', 'ac power', 'signal processing', 'statistical average', 'effective value', 'waveform analysis', 'electrical engineering'],
    metaTitle: 'RMS Calculator: Root Mean Square (Quadratic Mean) Online Free',
    metaDescription: 'Calculate the Root Mean Square (RMS) value for a set of numbers or a waveform. Determine the quadratic mean instantly. Free online tool.'
  },

  // New Batch: Geometry Tools
  {
    id: 'midpoint-calculator',
    name: 'Midpoint Calculator',
    description: 'Find the midpoint between two coordinate points',
    category: 'Science',
    icon: LocateFixed,
    route: '/tools/midpoint-calculator',
    keywords: ['midpoint calculator', 'find midpoint', 'coordinate geometry', 'middle point formula', 'line segment midpoint', 'geometry tool online', 'math calculator', 'midpoint formula'],
    metaTitle: 'Midpoint Calculator: Find the Middle Point Online Instantly',
    metaDescription: 'Find the midpoint of a line segment between two coordinate points (x1, y1) and (x2, y2) with this free online calculator. Get instant results for geometry problems.'
  },
  {
    id: 'slope-calculator',
    name: 'Slope Calculator',
    description: 'Calculate slope and distance between two points',
    category: 'Science',
    icon: TrendingUp,
    route: '/tools/slope-calculator',
    keywords: ['slope calculator', 'gradient calculator', 'line slope', 'distance formula', 'geometry calculator', 'find slope online', 'slope formula', 'two point slope', 'coordinate geometry', 'steepness calculator'],
    metaTitle: 'Free Slope Calculator: Find Gradient & Distance Online',
    metaDescription: 'Calculate the slope and distance between two points using coordinates. This online slope calculator determines line steepness and length instantly. Get accurate geometry results.'
  },
  {
    id: 'circle-calculator',
    name: 'Circle Calculator',
    description: 'Calculate Area, Circumference, Radius, Diameter',
    category: 'Science',
    icon: Circle,
    route: '/tools/circle-calculator',
    keywords: ['circle calculator', 'circle area calculator', 'circle circumference calculator', 'radius to diameter', 'diameter to radius', 'area of a circle formula', 'circumference formula', 'geometry calculator online', 'calculate circle properties'],
    metaTitle: 'Circle Calculator: Area, Circumference, Radius & Diameter Online',
    metaDescription: 'Calculate circle area, circumference, radius, and diameter instantly. Input any single value to find all others. Free online geometry tool for students and engineers.'
  },
  {
    id: 'cylinder-calculator',
    name: 'Cylinder Calculator',
    description: 'Calculate Volume and Surface Area of a Cylinder',
    category: 'Science',
    icon: Database,
    route: '/tools/cylinder-calculator',
    keywords: ['cylinder calculator', 'cylinder volume calculator', 'cylinder surface area', 'calculate cylinder dimensions', 'right circular cylinder', 'tank volume calculator', 'geometric shape calculator', 'cylinder formulas'],
    metaTitle: 'Cylinder Calculator: Volume, Surface Area & Dimensions Online',
    metaDescription: 'Calculate cylinder volume, lateral surface area, total surface area, and dimensions instantly. Input radius/diameter and height for accurate results. Free online tool.'
  },
  {
    id: 'cone-calculator',
    name: 'Cone Calculator',
    description: 'Calculate Volume, Surface Area, Slant Height',
    category: 'Science',
    icon: Cone,
    route: '/tools/cone-calculator',
    keywords: ['cone calculator', 'cone volume calculator', 'cone surface area', 'slant height calculator', 'right circular cone', 'geometry calculator online', 'conical shape calculations', '3d shape calculator'],
    metaTitle: 'Free Cone Calculator Online — Volume, Area & Slant Height',
    metaDescription: 'Calculate cone volume, surface area, and slant height instantly. Input radius and height to get precise geometric measurements. Free online tool for students and engineers.'
  },
  {
    id: 'sphere-calculator',
    name: 'Sphere Calculator',
    description: 'Calculate Volume and Surface Area of a Sphere',
    category: 'Science',
    icon: Globe,
    route: '/tools/sphere-calculator',
    keywords: ['sphere calculator', 'sphere volume calculator', 'sphere surface area', 'ball volume calculator', 'geometry sphere calculations', '3d shape calculator', 'radius to volume sphere', 'surface area of a ball'],
    metaTitle: 'Free Sphere Calculator Online — Volume & Surface Area',
    metaDescription: 'Calculate sphere volume and surface area instantly. Input radius or diameter to get precise geometric measurements. Free online tool for geometry and physics.'
  },

  // New Batch: Physics & Electronics
  {
    id: 'ohms-law-calculator',
    name: 'Ohm\'s Law Calculator',
    description: 'Calculate Voltage, Current, Resistance, Power',
    category: 'Science',
    icon: Zap,
    route: '/tools/ohms-law-calculator',
    keywords: ['ohms law calculator', 'voltage calculator', 'current calculator', 'resistance calculator', 'power calculator', 'electronics formula tool', 'electrical calculation', 'ohm\'s law equations'],
    metaTitle: 'Free Ohm\'s Law Calculator Online — V, I, R, P',
    metaDescription: 'Calculate Voltage, Current, Resistance, or Power using Ohm\'s Law. Input any two values to solve for the others instantly. Free online electronics tool.'
  },
  {
    id: 'resistor-color-code',
    name: 'Resistor Color Code',
    description: 'Decode 4-band and 5-band resistor colors',
    category: 'Science',
    icon: Maximize2,
    route: '/tools/resistor-color-code',
    keywords: ['resistor color code calculator', '4 band resistor calculator', '5 band resistor calculator', 'resistor value decoder', 'electronics resistor tool', 'resistor tolerance', 'resistor temperature coefficient', 'color code resistance'],
    metaTitle: 'Free Resistor Color Code Calculator — 4 & 5 Band',
    metaDescription: 'Decode 4-band and 5-band resistor color codes instantly. Input colors to get resistance, tolerance, and temperature coefficient. Free online electronics tool.'
  },
  {
    id: 'kinetic-energy-calculator',
    name: 'Kinetic Energy',
    description: 'Calculate Kinetic Energy (KE = ½mv²)',
    category: 'Science',
    icon: Gauge,
    route: '/tools/kinetic-energy-calculator',
    keywords: ['kinetic energy calculator', 'energy calculator', 'physics calculator', 'joules calculator', 'mass velocity energy', 'calculate kinetic energy', 'KE formula calculator', 'motion energy calculator'],
    metaTitle: 'Kinetic Energy Calculator: Calculate KE = ½mv² Instantly',
    metaDescription: 'Calculate kinetic energy (KE) using mass and velocity. Determine energy in Joules for objects in motion. Free online physics tool for students and engineers.'
  },
  {
    id: 'force-calculator',
    name: 'Force Calculator',
    description: 'Calculate Force (F = ma)',
    category: 'Science',
    icon: ArrowRight,
    route: '/tools/force-calculator',
    keywords: ['force calculator', 'newtons second law', 'f=ma calculator', 'physics calculator', 'mass acceleration force', 'calculate force', 'newtons calculator', 'net force calculator'],
    metaTitle: 'Force Calculator: Calculate Force (F = ma) Online & Free',
    metaDescription: 'Calculate force using mass and acceleration with this free online tool. Apply Newton\'s Second Law (F=ma) for physics problems and engineering tasks instantly.'
  },
  {
    id: 'projectile-motion-calculator',
    name: 'Projectile Motion',
    description: 'Calculate Range, Height, Time of Flight',
    category: 'Science',
    icon: Rocket,
    route: '/tools/projectile-motion-calculator',
    keywords: ['projectile motion calculator', 'trajectory calculator', 'max height calculator', 'physics projectile', 'range calculator', 'time of flight calculator', 'launch angle calculator', 'parabolic motion'],
    metaTitle: 'Projectile Motion Calculator: Range, Height, Time (Free)',
    metaDescription: 'Calculate projectile range, maximum height, and time of flight. Analyze parabolic trajectories with initial velocity and launch angle. Free physics tool.'
  },


  // Finance Calculators
  {
    id: 'loan-payoff-calculator',
    name: 'Loan Payoff Calculator',
    description: 'Calculate loan payoff schedule and savings',
    category: 'Calculator',
    icon: DollarSign,
    route: '/tools/loan-payoff-calculator',
    metaTitle: 'Loan Payoff Calculator: Payoff Date & Interest Savings (Free)',
    metaDescription: 'Calculate your loan payoff date and total interest savings by making extra payments. Create an amortization schedule for mortgages, auto loans, and personal loans.',
    keywords: ['loan payoff calculator', 'early payoff calculator', 'amortization schedule', 'debt payoff', 'mortgage payoff', 'auto loan calculator', 'extra payments', 'interest savings calculator', 'loan acceleration']
  },
  {
    id: 'roi-calculator',
    name: 'ROI Calculator',
    description: 'Calculate Return on Investment percentage',
    category: 'Calculator',
    icon: TrendingUp,
    route: '/tools/roi-calculator',
    metaTitle: 'Free ROI Calculator: Calculate Return on Investment Online',
    metaDescription: 'Calculate Return on Investment (ROI) percentage instantly. Input initial cost and final value to determine investment profitability. Free online ROI calculation tool.',
    keywords: ['roi calculator', 'return on investment calculator', 'calculate roi online', 'investment profit calculator', 'annualized roi', 'business investment analysis', 'project roi calculation', 'marketing roi']
  },
  {
    id: 'apy-calculator',
    name: 'APY Calculator',
    description: 'Calculate Annual Percentage Yield',
    category: 'Calculator',
    icon: Percent,
    route: '/tools/apy-calculator',
    metaTitle: 'Free APY Calculator: Annual Percentage Yield Online Tool',
    metaDescription: 'Calculate Annual Percentage Yield (APY) from interest rate and compounding frequency. Compare savings accounts and investment returns. Free online APY calculator.',
    keywords: ['apy calculator', 'annual percentage yield calculator', 'calculate apy online', 'interest rate calculator', 'compound interest calculator', 'savings account yield', 'effective annual rate', 'investment yield']
  },
  {
    id: 'break-even-calculator',
    name: 'Break Even Calculator',
    description: 'Calculate business break-even point',
    category: 'Calculator',
    icon: DollarSign,
    route: '/tools/break-even-calculator',
    metaTitle: 'Free Break-Even Calculator: Business Profitability Analysis',
    metaDescription: 'Calculate your business\'s break-even point in units or sales revenue. Analyze fixed costs, variable costs, and pricing. Free online break-even analysis tool.',
    keywords: ['break even calculator', 'break even point', 'business break even analysis', 'fixed costs', 'variable costs', 'contribution margin', 'profitability analysis', 'startup break even']
  },
  {
    id: 'capm-calculator',
    name: 'CAPM Calculator',
    description: 'Calculate expected return using CAPM',
    category: 'Calculator',
    icon: TrendingUp,
    route: '/tools/capm-calculator',
    metaTitle: 'Free CAPM Calculator: Capital Asset Pricing Model Online',
    metaDescription: 'Calculate the expected return on an asset using the Capital Asset Pricing Model (CAPM). Input risk-free rate, beta, and market return. Free online CAPM tool.',
    keywords: ['capm calculator', 'capital asset pricing model', 'expected return calculator', 'risk free rate', 'beta coefficient', 'market risk premium', 'investment analysis', 'financial modeling']
  },
  {
    id: 'tvm-calculator',
    name: 'TVM Calculator',
    description: 'Time Value of Money (PV, FV, PMT)',
    category: 'Calculator',
    icon: DollarSign,
    route: '/tools/tvm-calculator',
    metaTitle: 'Free TVM Calculator: Present Value, Future Value, PMT Online',
    metaDescription: 'Calculate Time Value of Money (TVM) for present value (PV), future value (FV), and payments (PMT). Analyze loans, investments, and annuities instantly.',
    keywords: ['tvm calculator', 'time value of money', 'present value calculator', 'future value calculator', 'pmt calculator', 'annuity calculator', 'finance calculator online', 'loan amortization', 'compound interest calculator']
  },
  // Batch 2: Unit Converters
  {
    id: 'roman-numeral-converter',
    name: 'Roman Numeral Converter',
    description: 'Convert Arabic ↔ Roman numerals',
    category: 'Converters',
    icon: Type,
    route: '/tools/roman-numeral-converter',
    keywords: ['roman numeral converter', 'arabic to roman', 'roman to arabic', 'roman numeral translator', 'convert roman numbers', 'MMXXIV to number', 'number to roman numeral'],
    metaTitle: 'Roman Numeral Converter: Arabic to Roman & Vice Versa',
    metaDescription: 'Convert Arabic numbers to Roman numerals and Roman numerals to Arabic numbers instantly. Supports numbers from 1 to 3999.'
  },
  {
    id: 'temperature-converter',
    name: 'Temperature Converter',
    description: 'Convert Celsius, Fahrenheit, Kelvin',
    category: 'Converters',
    icon: Thermometer,
    route: '/tools/temperature-converter',
    keywords: ['temperature converter', 'celsius to fahrenheit', 'fahrenheit to celsius', 'kelvin conversion', 'temperature scale converter', 'convert temperature units', 'celsius kelvin fahrenheit'],
    metaTitle: 'Temperature Converter: Celsius, Fahrenheit, Kelvin Online',
    metaDescription: 'Convert temperatures between Celsius, Fahrenheit, and Kelvin scales instantly. Accurate for scientific, weather, and everyday use.'
  },
  {
    id: 'speed-converter',
    name: 'Speed Converter',
    description: 'Convert mph, km/h, knots, m/s',
    category: 'Converters',
    icon: Gauge,
    route: '/tools/speed-converter',
    keywords: ['speed converter', 'mph to kmh', 'kmh to mph', 'knots converter', 'meters per second converter', 'velocity converter', 'speed unit conversion', 'm/s to km/h'],
    metaTitle: 'Speed Converter: MPH, KM/H, Knots, M/S Online Tool',
    metaDescription: 'Convert speed units between Miles per Hour (mph), Kilometers per Hour (km/h), Knots, and Meters per Second (m/s) instantly.'
  },
  {
    id: 'mass-converter',
    name: 'Mass Converter',
    description: 'Convert kg, lbs, oz, tons',
    category: 'Converters',
    icon: Weight,
    route: '/tools/mass-converter',
    keywords: ['mass converter', 'weight converter', 'kg to lbs converter', 'grams to ounces', 'pounds to kg', 'metric weight converter', 'imperial weight converter', 'food scale converter', 'online mass conversion', 'unit converter weight'],
    metaTitle: 'Mass & Weight Converter: KG, Lbs, Oz, Tons — Free Online Tool',
    metaDescription: 'Convert mass and weight units like kilograms, pounds, ounces, and tons instantly. Accurate conversions for cooking, shipping, and scientific use. Free online tool.'
  },
  {
    id: 'length-converter',
    name: 'Length Converter',
    description: 'Convert m, ft, miles, km',
    category: 'Converters',
    icon: Ruler,
    route: '/tools/length-converter',
    keywords: ['length converter', 'distance converter', 'meters to feet', 'miles to km', 'cm to inches', 'metric length converter', 'imperial length converter', 'height converter', 'online length conversion', 'unit converter length'],
    metaTitle: 'Length & Distance Converter: Meters, Feet, Miles — Free Online',
    metaDescription: 'Convert length and distance units like meters, feet, miles, and kilometers instantly. Accurate for construction, travel, and academic use. Free online tool.'
  },
  {
    id: 'area-converter',
    name: 'Area Converter',
    description: 'Convert m², ft², acres, hectares',
    category: 'Converters',
    icon: BoxSelect,
    route: '/tools/area-converter',
    keywords: ['area converter', 'square meters', 'acres to hectares', 'land measurement', 'square feet', 'sq km to sq miles', 'real estate area converter', 'agricultural area conversion', 'online area calculator', 'unit converter area'],
    metaTitle: 'Area Converter: Sq Meters, Acres, Hectares — Free Online Tool',
    metaDescription: 'Convert area units like square meters, acres, hectares, and square feet instantly. Accurate for real estate, agriculture, and planning. Free online tool.'
  },
  {
    id: 'volume-converter',
    name: 'Volume Converter',
    description: 'Convert L, gallons, ml, cups',
    category: 'Converters',
    icon: Beaker,
    route: '/tools/volume-converter',
    keywords: ['volume converter', 'liters to gallons', 'ml to cups', 'liquid measurement', 'cubic meters to liters', 'cooking converter', 'fluid volume converter', 'imperial volume converter', 'online volume calculator', 'unit converter volume'],
    metaTitle: 'Volume Converter: Liters, Gallons, ML, Cups — Free Online Tool',
    metaDescription: 'Convert volume units like liters, gallons, milliliters, and cups instantly. Accurate for cooking, chemistry, and fluid measurement. Free online tool.'
  },
  {
    id: 'power-converter',
    name: 'Power Converter',
    description: 'Convert Watts, HP, BTU/h',
    category: 'Converters',
    icon: Zap,
    route: '/tools/power-converter',
    keywords: ['power converter', 'watts to hp', 'horsepower calculator', 'kilowatts conversion', 'btu per hour', 'electrical power units', 'mechanical power conversion', 'online power tool', 'convert watts to kw', 'hp to watts', 'power unit conversion'],
    metaTitle: 'Free Power Converter: Watts, HP, Kilowatts & BTU/h Online',
    metaDescription: 'Convert power units: Watts, Horsepower (HP), Kilowatts (kW), and BTU/h instantly. Calculate electrical and mechanical power values for engineering tasks.',
  },
  {
    id: 'energy-converter',
    name: 'Energy Converter',
    description: 'Convert Joules, calories, kWh',
    category: 'Converters',
    icon: BatteryCharging,
    route: '/tools/energy-converter',
    keywords: ['energy converter', 'joules to calories', 'kwh calculator', 'food energy conversion', 'physics energy units', 'electron volt converter', 'thermal energy conversion', 'electrical energy units', 'convert joules to kwh', 'calories to joules', 'energy unit conversion'],
    metaTitle: 'Free Energy Converter: Joules, Calories, kWh & eV Online',
    metaDescription: 'Convert energy units: Joules, Calories, Kilowatt-hours (kWh), and Electron Volts (eV) instantly. Calculate thermal, electrical, and food energy.',
  },
  {
    id: 'pressure-converter',
    name: 'Pressure Converter',
    description: 'Convert Pascal, Bar, PSI, atm',
    category: 'Converters',
    icon: Gauge,
    route: '/tools/pressure-converter',
    keywords: ['pressure converter', 'psi to bar', 'pascal converter', 'tire pressure conversion', 'atmosphere conversion', 'kilopascal to psi', 'fluid pressure units', 'gas pressure calculator', 'convert bar to psi', 'pa to atm', 'pressure unit conversion'],
    metaTitle: 'Free Pressure Converter: PSI, Bar, Pascal, ATM & kPa Online',
    metaDescription: 'Convert pressure units: PSI, Bar, Pascal (Pa), Atmosphere (atm), and Kilopascal (kPa) instantly. Calculate fluid and gas pressures.',
  },
  {
    id: 'torque-converter',
    name: 'Torque Converter',
    description: 'Convert Nm, lb-ft, kg-m',
    category: 'Converters',
    icon: RotateCw,
    route: '/tools/torque-converter',
    keywords: ['torque converter', 'newton meters to lb-ft', 'lb-ft conversion', 'engine torque calculator', 'kg-m to nm', 'rotational force units', 'mechanic tool', 'torque unit conversion', 'convert nm to lb-ft', 'dyne cm converter', 'torque measurement'],
    metaTitle: 'Free Torque Converter: Nm, lb-ft, kg-m & dyne-cm Online',
    metaDescription: 'Convert torque units: Newton-meters (Nm), Pound-feet (lb-ft), Kilogram-meters (kg-m), and dyne-cm instantly. Calculate rotational force.'
  },
  {
    id: 'angle-converter',
    name: 'Angle Converter',
    description: 'Convert Degrees, radians, gradians',
    category: 'Converters',
    icon: Circle,
    route: '/tools/angle-converter',
    keywords: ['angle converter', 'degrees to radians', 'radians to degrees', 'gradians converter', 'angle unit conversion', 'geometry converter', 'trigonometry tools', 'convert angles online', 'degree radian gradian', 'angle measurement converter'],
    metaTitle: 'Angle Converter: Degrees, Radians & Gradians Online Tool',
    metaDescription: 'Convert angles between degrees, radians, and gradians instantly. This free online angle converter supports precise trigonometric and geometric calculations.',
  },
  {
    id: 'data-storage-converter',
    name: 'Data Converter',
    description: 'Convert Bytes, KB, MB, GB, TB',
    category: 'Converters',
    icon: HardDrive,
    route: '/tools/data-storage-converter',
    keywords: ['data storage converter', 'bytes to gb', 'mb to tb converter', 'file size converter', 'data unit conversion', 'kb to mb', 'gb to tb', 'online data converter', 'storage unit calculator', 'convert data units'],
    metaTitle: 'Free Data Storage Converter: Bytes, KB, MB, GB, TB Online',
    metaDescription: 'Convert data storage units: Bytes, KB, MB, GB, TB, PB. Instantly calculate file sizes for downloads, disks, and memory. Free online data unit conversion.'
  },
  {
    id: 'fuel-efficiency-converter',
    name: 'Fuel Converter',
    description: 'Convert MPG, L/100km, km/L',
    category: 'Converters',
    icon: Fuel,
    route: '/tools/fuel-efficiency-converter',
    keywords: ['fuel efficiency converter', 'mpg to l/100km', 'l/100km to mpg', 'km/l to mpg', 'fuel consumption converter', 'gas mileage converter', 'fuel economy calculator', 'liters per 100km converter', 'kilometer per liter conversion', 'online fuel converter'],
    metaTitle: 'Fuel Efficiency Converter: MPG, L/100km, km/L Online',
    metaDescription: 'Convert fuel efficiency units: MPG, L/100km, and km/L instantly. Calculate gas mileage for different regions with this free online tool.'
  },
  // Health & Fitness - Body Composition
  {
    id: 'body-fat-calculator',
    name: 'Body Fat Calculator',
    description: 'Estimate body fat percentage using US Navy Method',
    category: 'Calculator',
    icon: Activity,
    route: '/tools/body-fat-calculator',
    keywords: ['body fat calculator', 'us navy method', 'body composition calculator', 'bf% calculator', 'body fat percentage', 'fitness calculator', 'navy method body fat', 'online body fat tool'],
    metaTitle: 'Body Fat Calculator: US Navy Method Online & Free',
    metaDescription: 'Calculate body fat percentage using the US Navy Method. Input measurements for men and women to get an instant BF% estimate. Free online tool.'
  },
  {
    id: 'lean-body-mass-calculator',
    name: 'Lean Body Mass',
    description: 'Calculate LBM using Boer, James, and Hume formulas',
    category: 'Calculator',
    icon: Dumbbell,
    route: '/tools/lean-body-mass-calculator',
    keywords: ['lean body mass calculator', 'lbm calculator', 'boer formula', 'james formula', 'hume formula', 'muscle mass calculator', 'body composition', 'fat free mass'],
    metaTitle: 'Lean Body Mass Calculator (LBM) - Boer, James & Hume Formulas',
    metaDescription: 'Calculate Lean Body Mass (LBM) using the Boer, James, and Hume formulas. Determine your muscle mass for fitness and health assessments. Free LBM calculator online.'
  },
  {
    id: 'ideal-weight-calculator',
    name: 'Ideal Weight',
    description: 'Find your ideal weight (Devine, Miller, Robinson)',
    category: 'Calculator',
    icon: Smile,
    route: '/tools/ideal-weight-calculator',
    keywords: ['ideal weight calculator', 'ibw calculator', 'healthy weight range', 'devine formula', 'miller formula', 'robinson formula', 'hamwi formula', 'body weight assessment'],
    metaTitle: 'Ideal Weight Calculator (IBW) - Devine, Miller, Robinson, Hamwi',
    metaDescription: 'Calculate your Ideal Body Weight (IBW) using Devine, Miller, Robinson, and Hamwi formulas. Determine a healthy weight range based on height and gender. Free IBW tool.'
  },
  // Health & Fitness - Performance
  {
    id: 'pace-calculator',
    name: 'Pace Calculator',
    description: 'Calculate running pace, time, or distance',
    category: 'Calculator',
    icon: Timer,
    route: '/tools/pace-calculator',
    keywords: ['pace calculator', 'running pace', 'calculate pace', 'running time calculator', 'running distance calculator', 'race pace', 'split times', 'marathon pace', '5k pace'],
    metaTitle: 'Running Pace Calculator: Time, Distance, Speed & Splits Online',
    metaDescription: 'Calculate running pace, time, or distance instantly. Determine average pace, predict race times, and analyze split times for training. Free online pace calculator.'
  },
  {
    id: 'one-rep-max-calculator',
    name: 'One Rep Max',
    description: 'Estimate 1RM strength and training percentages',
    category: 'Calculator',
    icon: Trophy,
    route: '/tools/one-rep-max-calculator',
    keywords: ['1rm calculator', 'one rep max', 'epley formula', 'brzycki formula', 'strength calculator', 'lifting percentages', 'bench press max', 'deadlift max', 'squat max'],
    metaTitle: 'One Rep Max Calculator (1RM) & Lifting Percentages Online',
    metaDescription: 'Estimate your One Rep Max (1RM) using Epley, Brzycki, and other formulas. Calculate lifting percentages for strength training. Free 1RM calculator.'
  },
  {
    id: 'water-intake-calculator',
    name: 'Water Intake',
    description: 'Daily hydration needs based on activity and climate',
    category: 'Calculator',
    icon: GlassWater,
    route: '/tools/water-intake-calculator',
    keywords: ['water intake calculator', 'daily water needs', 'hydration calculator', 'how much water to drink', 'calculate water intake', 'fluid intake calculator', 'personal hydration needs', 'free water calculator'],
    metaTitle: 'Water Intake Calculator: Daily Hydration Needs Online',
    metaDescription: 'Calculate your daily water intake based on activity, climate, and body metrics. Determine how much water to drink for optimal hydration. Free online tool.'
  },
  {
    id: 'target-heart-rate-calculator',
    name: 'Target Heart Rate',
    description: 'Calculate heart rate training zones (Karvonen)',
    category: 'Calculator',
    icon: Heart,
    route: '/tools/target-heart-rate-calculator',
    keywords: ['target heart rate calculator', 'karvonen formula', 'heart rate zones', 'training heart rate', 'cardio zones', 'fat burning heart rate', 'exercise heart rate', 'max heart rate calculator'],
    metaTitle: 'Target Heart Rate Calculator: Karvonen Method Online',
    metaDescription: 'Calculate your target heart rate zones using the Karvonen formula. Optimize cardio workouts for fat burning, endurance, or peak performance. Free tool.'
  },
  // Health & Fitness - Specialized
  {
    id: 'ovulation-calculator',
    name: 'Ovulation Calc',
    description: 'Predict fertile window and next period date',
    category: 'Calculator',
    icon: Baby,
    route: '/tools/ovulation-calculator',
    keywords: ['ovulation calculator', 'fertile window calculator', 'period tracker', 'conception calculator', 'pregnancy planning tool', 'ovulation predictor', 'fertility calendar', 'when do I ovulate'],
    metaTitle: 'Ovulation Calculator: Predict Fertility Window & Period',
    metaDescription: 'Predict your ovulation date and fertile window to increase conception chances. Track your next period with this free online ovulation calculator.',
  },
  {
    id: 'breath-holding-trainer',
    name: 'Breath Trainer',
    description: 'Generate CO2/O2 apnea training tables',
    category: 'Utility',
    icon: Wind,
    route: '/tools/breath-holding-trainer',
    keywords: ['breath holding trainer', 'co2 tables', 'o2 tables', 'apnea training', 'freediving static apnea', 'breath hold exercises', 'carbon dioxide tolerance', 'oxygen deprivation training'],
    metaTitle: 'Breath Holding Trainer: CO2 & O2 Apnea Tables Online',
    metaDescription: 'Generate personalized CO2 and O2 apnea training tables. Improve breath-holding capacity for freediving, spearfishing, or meditation. Free utility.'
  },

  // Batch 12: Date & Time Utilities
  {
    id: 'date-difference-calculator',
    name: 'Date Difference Calculator',
    description: 'Calculate days, months, and years between two dates',
    category: 'Utility',
    icon: Calendar,
    route: '/tools/date-difference-calculator',
    metaTitle: 'Date Difference Calculator Online — Free Days Between Dates',
    metaDescription: 'Calculate the exact difference between two dates in days, months, and years. Find the duration between any two calendar dates instantly. Free online tool.',
    keywords: ['date difference calculator', 'days between dates', 'time duration calculator', 'calculate date span', 'date gap finder', 'time elapsed calculator', 'date range calculator', 'how many days between dates']
  },
  {
    id: 'day-of-year-calculator',
    name: 'Day of Year Calculator',
    description: 'Find the day number (1-366) of any date',
    category: 'Utility',
    icon: Calendar,
    route: '/tools/day-of-year-calculator',
    metaTitle: 'Day of Year Calculator Online — Free Ordinal Date Finder',
    metaDescription: 'Find the day number (1-366) for any date instantly. Determine the ordinal date or Julian day number for any calendar date. Free online tool.',
    keywords: ['day of year calculator', 'ordinal date finder', 'julian day number', 'day number of year', 'calendar day number', 'what day of year is it', 'year progress calculator', 'date to day number']
  },
  {
    id: 'week-number-calculator',
    name: 'Week Number Calculator',
    description: 'Find the ISO week number for any date',
    category: 'Utility',
    icon: Calendar,
    route: '/tools/week-number-calculator',
    metaTitle: 'Week Number Calculator Online — Free ISO Week Finder',
    metaDescription: 'Find the ISO 8601 week number for any date. Determine the week of the year instantly for planning and scheduling. Free online tool.',
    keywords: ['week number calculator', 'iso week number', 'what week is it', 'week of the year', 'iso 8601 week', 'calendar week number', 'planning week number', 'current week number']
  },
  {
    id: 'work-days-calculator',
    name: 'Work Days Calculator',
    description: 'Calculate business days between dates',
    category: 'Utility',
    icon: Calendar,
    route: '/tools/work-days-calculator',
    metaTitle: 'Work Days Calculator Online — Free Business Day Counter',
    metaDescription: 'Calculate the number of work days (business days) between two dates. Exclude weekends and optionally holidays. Free online tool.',
    keywords: ['work days calculator', 'business day counter', 'working days between dates', 'calculate work days', 'project timeline calculator', 'payroll days calculator', 'net working days', 'exclude weekends']
  },
  {
    id: 'time-card-calculator',
    name: 'Time Card Calculator',
    description: 'Calculate total work hours and pay',
    category: 'Utility',
    icon: Clock,
    route: '/tools/time-card-calculator',
    metaTitle: 'Free Time Card Calculator — Work Hours & Gross Pay Online',
    metaDescription: 'Calculate total work hours, breaks, and gross pay with our free online time card calculator. Enter shifts, rates, and overtime rules. Instant results.',
    keywords: ['time card calculator', 'work hours calculator', 'gross pay calculator', 'payroll calculator', 'hourly pay calculator', 'timesheet calculator', 'overtime calculator', 'employee time tracker']
  },

  // Batch 13: Fun & Miscellaneous
  {
    id: 'zodiac-sign-calculator',
    name: 'Zodiac Sign Calculator',
    description: 'Find your Western zodiac sign by birthdate',
    category: 'Fun',
    icon: Sparkles,
    route: '/tools/zodiac-sign-calculator',
    metaTitle: 'Free Zodiac Sign Calculator — Find Your Sun Sign Online',
    metaDescription: 'Find your Western astrological zodiac sign instantly with our free online calculator. Enter your birthdate to discover your sun sign and its dates.',
    keywords: ['zodiac sign calculator', 'what is my zodiac sign', 'sun sign finder', 'astrology calculator', 'star sign by birthdate', 'birth chart calculator', 'zodiac dates'],
    relatedToolIds: ['chinese-zodiac-calculator', 'moon-phase-calculator', 'love-calculator']
  },
  {
    id: 'chinese-zodiac-calculator',
    name: 'Chinese Zodiac Calculator',
    description: 'Find your Chinese zodiac animal by year',
    category: 'Fun',
    icon: Star,
    route: '/tools/chinese-zodiac-calculator',
    metaTitle: 'Free Chinese Zodiac Calculator — Find Your Animal Sign',
    metaDescription: 'Discover your Chinese zodiac animal sign instantly with our free online calculator. Enter your birth year to find your animal and its element.',
    keywords: ['chinese zodiac calculator', 'what is my chinese zodiac', 'zodiac animal finder', 'lunar zodiac calculator', 'chinese astrology by year', 'year of the dragon', 'chinese zodiac element'],
    relatedToolIds: ['zodiac-sign-calculator', 'moon-phase-calculator', 'love-calculator']
  },
  {
    id: 'moon-phase-calculator',
    name: 'Moon Phase Calculator',
    description: 'Calculate moon phase for any date',
    category: 'Fun',
    icon: Moon,
    route: '/tools/moon-phase-calculator',
    metaTitle: 'Free Moon Phase Calculator — Lunar Phases for Any Date',
    metaDescription: 'Calculate the moon phase for any past or future date instantly with our free online moon phase calculator. See illumination and moon age.',
    keywords: ['moon phase calculator', 'lunar phase finder', 'moon age calculator', 'is it a full moon', 'astronomy calculator', 'moon calendar', 'moon cycle dates'],
    relatedToolIds: ['zodiac-sign-calculator', 'chinese-zodiac-calculator', 'love-calculator']
  },
  {
    id: 'love-calculator',
    name: 'Love Calculator',
    description: 'Fun name compatibility test',
    category: 'Fun',
    icon: Heart,
    route: '/tools/love-calculator',
    metaTitle: 'Love Calculator Online — Free Name Compatibility Test',
    metaDescription: 'Test name compatibility with the Love Calculator. Enter two names to get a percentage score based on a numerical algorithm. Free, instant results.',
    keywords: ['love calculator', 'name compatibility test', 'love percentage', 'relationship calculator', 'name love test', 'compatibility score', 'love test online', 'free love calculator']
  },
  {
    id: 'electricity-cost-calculator',
    name: 'Electricity Cost Calculator',
    description: 'Estimate appliance running costs',
    category: 'Utility',
    icon: Zap,
    route: '/tools/electricity-cost-calculator',
    metaTitle: 'Electricity Cost Calculator — Free Appliance Usage Estimator',
    metaDescription: 'Estimate appliance electricity costs. Input wattage, usage hours, and kWh rate to calculate daily, monthly, and yearly expenses. Free, instant.',
    keywords: ['electricity cost calculator', 'appliance running cost', 'energy usage estimator', 'power consumption calculator', 'electricity bill estimator', 'kWh cost calculator', 'appliance wattage cost', 'energy expense calculator']
  },
  {
    id: 'tv-size-calculator',
    name: 'TV Size Calculator',
    description: 'Optimal viewing distance and size',
    category: 'Utility',
    icon: Tv,
    route: '/tools/tv-size-calculator',
    metaTitle: 'TV Size Calculator — Free Optimal Viewing Distance Finder',
    metaDescription: 'Find optimal TV size and viewing distance. Input screen size or distance to get recommended ranges for immersive viewing. Free, instant.',
    keywords: ['tv size calculator', 'optimal viewing distance', 'screen size distance', 'tv distance calculator', 'thx viewing distance', '4k tv viewing distance', 'tv buying guide', 'tv screen size']
  },
  {
    id: 'english-dictionary',
    name: 'English Dictionary',
    description: 'Find meanings, parts of speech, phonetic spellings, synonyms, and audio pronunciations for any English word.',
    category: 'Discovery',
    icon: BookOpen,
    route: '/tools/english-dictionary',
    metaTitle: 'English Dictionary Online — Free Word Meanings & Pronunciations',
    metaDescription: 'Look up definitions, parts of speech, phonetic spellings, synonyms, antonyms, and listen to audio pronunciations for any English word. Free and instant.',
    keywords: ['english dictionary', 'word definition', 'synonym finder', 'dictionary lookup', 'audio pronunciation', 'english meanings', 'define words', 'free dictionary online']
  },
  {
    id: 'thesaurus-synonyms-finder',
    name: 'Thesaurus & Synonyms Finder',
    description: 'Find synonyms, antonyms, and related words to expand your vocabulary and improve your writing.',
    category: 'Discovery',
    icon: Languages,
    route: '/tools/thesaurus-synonyms-finder',
    metaTitle: 'Thesaurus & Synonyms Finder — Free Word Alternator Online',
    metaDescription: 'Discover synonyms, antonyms, and related words instantly. Improve your vocabulary and writing with our free online thesaurus tool.',
    keywords: ['thesaurus online', 'synonym finder', 'antonym finder', 'word synonyms', 'vocabulary builder', 'find antonyms', 'word search thesaurus', 'writing helper']
  },
  {
    id: 'global-public-holidays-finder',
    name: 'Global Public Holidays Finder',
    description: 'Find public, bank, national, and religious holidays for countries worldwide.',
    category: 'Discovery',
    icon: Calendar,
    route: '/tools/global-public-holidays-finder',
    metaTitle: 'Global Public Holidays Finder — Bank & National Holidays Online',
    metaDescription: 'Check public, bank, national, and religious holidays for over 100 countries. Plan your vacations and remote work schedules instantly. Free.',
    keywords: ['public holidays', 'bank holidays finder', 'national holidays', 'global holidays calendar', 'holiday calendar by country', 'religious holidays', 'holiday tracker']
  },
  {
    id: 'book-finder-search',
    name: 'Book Finder & Search',
    description: 'Search the global Open Library catalog by book title, author, or ISBN identifier.',
    category: 'Discovery',
    icon: Search,
    route: '/tools/book-finder-search',
    metaTitle: 'Book Finder & Search — ISBN & Open Library lookup',
    metaDescription: 'Search the global library catalog by title, author, or ISBN. Find editions, publication details, and metadata instantly. Free online search.',
    keywords: ['book finder', 'isbn lookup', 'search books online', 'open library search', 'find book by author', 'book title search', 'book metadata finder']
  },
  {
    id: 'password-breach-checker',
    name: 'Password Breach Checker',
    description: 'Check if your password has appeared in a data breach using secure k-Anonymity.',
    category: 'Security',
    icon: ShieldCheck,
    route: '/tools/password-breach-checker',
    metaTitle: 'Password Breach Checker — Test Password Security Online',
    metaDescription: 'Verify if your password has been exposed in previous data breaches securely. Uses k-Anonymity API to keep your password completely private.',
    keywords: ['password breach checker', 'have i been pwned password', 'password leak checker', 'check password security', 'secure password test', 'data breach lookup']
  },
  {
    id: 'email-breach-checker',
    name: 'Email Breach Checker',
    description: 'Check if your email address has been exposed in a data breach.',
    category: 'Security',
    icon: MailCheck,
    route: '/tools/email-breach-checker',
    metaTitle: 'Email Breach Checker — Find Exposed Accounts Online',
    metaDescription: 'Search for your email address across known data breaches and leaks. Find out if your accounts are compromised and secure them today.',
    keywords: ['email breach checker', 'have i been pwned email', 'email leak check', 'data breach scanner', 'account security checker', 'compromised email checker']
  },
  {
    id: 'fake-person-generator',
    name: 'Fake Person Generator',
    description: 'Generate realistic fake identities for testing and development.',
    category: 'Security',
    icon: UserX,
    route: '/tools/fake-person-generator',
    metaTitle: 'Fake Person Generator — Realistic Random Identity Maker',
    metaDescription: 'Generate realistic random profiles, names, addresses, phone numbers, and emails for testing, mockups, and database development. Free.',
    keywords: ['fake person generator', 'random identity generator', 'mock profile maker', 'test user generator', 'fake address generator', 'database mock data']
  },
  {
    id: 'vin-decoder',
    name: 'VIN Number Lookup & Decoder',
    description: 'Decode any vehicle VIN to get full manufacturer specs from the NHTSA database.',
    category: 'Explore',
    icon: Car,
    route: '/tools/vin-decoder',
    metaTitle: 'VIN Number Lookup & Decoder — Free Vehicle Spec Finder',
    metaDescription: 'Decode any car, truck, or motorcycle VIN instantly. Get manufacturer specifications, engine details, model year, and safety ratings from NHTSA. Free.',
    keywords: ['vin decoder', 'vin number lookup', 'vehicle specification finder', 'decode vin free', 'car spec finder', 'nhtsa vin lookup', 'vehicle history specs']
  },
  {
    id: 'food-barcode-scanner',
    name: 'Food Barcode Scanner',
    description: 'Look up any food product barcode for nutrition facts, ingredients, and allergens.',
    category: 'Explore',
    icon: ScanBarcode,
    route: '/tools/food-barcode-scanner',
    metaTitle: 'Food Barcode Scanner — Nutrition & Ingredient Lookup',
    metaDescription: 'Scan or type food product barcodes to view detailed nutrition facts, ingredients list, allergens, and additives. Free online database search.',
    keywords: ['food barcode scanner', 'nutrition facts lookup', 'food ingredient checker', 'upc code food scanner', 'allergen food checker', 'open food facts search']
  },
  {
    id: 'iss-live-tracker',
    name: 'ISS Live Orbit Tracker',
    description: 'Track the International Space Station in real-time, view day/night terminators, telemetry metrics, and check visibility from your location.',
    category: 'Earth & Space',
    icon: Satellite,
    route: '/tools/iss-live-tracker',
    metaTitle: 'ISS Live Orbit Tracker — Track Space Station in Real-Time',
    metaDescription: 'Follow the ISS live on a map. View current orbital coordinates, velocity, altitude, and get upcoming pass times for your specific location. Free.',
    keywords: ['iss tracker live', 'track space station', 'iss current location', 'international space station map', 'iss pass times finder', 'satellite tracker']
  },
  {
    id: 'earthquake-live-map',
    name: 'Seismic & Tectonic Monitor',
    description: 'Live global earthquake tracker displaying real-time USGS seismic feeds, depth indexes, and tectonic fault line overlaps.',
    category: 'Earth & Space',
    icon: Activity,
    route: '/tools/earthquake-live-map',
    metaTitle: 'Seismic & Tectonic Monitor — Live Global Earthquake Tracker',
    metaDescription: 'Track recent global earthquakes in real-time on an interactive map. Overlaid with tectonic fault lines, magnitude filters, and depth indexes.',
    keywords: ['earthquake live tracker', 'recent earthquakes map', 'seismic activity monitor', 'usgs earthquake feeds', 'tectonic plate fault lines', 'earthquake magnitude finder']
  },
  {
    id: 'air-quality-checker',
    name: 'Air Quality & Allergen Hub',
    description: 'Check real-time air quality index (AQI), PM2.5/PM10, ozone, pollen risk, and 7-day forecast alerts side-by-side.',
    category: 'Earth & Space',
    icon: Wind,
    route: '/tools/air-quality-checker',
    metaTitle: 'Air Quality & Allergen Hub — Live AQI & Pollen Forecast',
    metaDescription: 'Check real-time air quality index (AQI), PM2.5, PM10, ozone levels, and pollen forecasts globally. Stay healthy with 7-day pollution forecasts.',
    keywords: ['air quality index', 'live aqi checker', 'pm2.5 monitor', 'pollen count forecast', 'air pollution checker', 'allergy report online', 'real-time air quality']
  },
  {
    id: 'historical-weather-lookup',
    name: 'Historical Climate Dispatch',
    description: 'Access global meteorological records back to January 1, 1940. Graph hourly temperatures, view decade trivia, and compare historical metrics to today.',
    category: 'Earth & Space',
    icon: CloudSun,
    route: '/tools/historical-weather-lookup',
    metaTitle: 'Historical Climate Dispatch — Past Weather & Records Lookup',
    metaDescription: 'Browse global weather records from 1940 to today. Compare historical temperature, wind, and precipitation trends against today\'s weather. Free.',
    keywords: ['historical weather lookup', 'past weather database', 'weather records search', 'climate archive finder', 'historical temperature history', 'weather history map']
  },
  {
    id: 'country-comparison-tool',
    name: 'Country Comparison Tool',
    description: 'Compare two countries side-by-side with flags, population, GDP, and more.',
    category: 'Explore',
    icon: Globe2,
    route: '/tools/country-comparison-tool',
    metaTitle: 'Country Comparison Tool — Compare Demographic & Economic Data',
    metaDescription: 'Compare population, area, GDP, life expectancy, inflation, and other key indicators of any two countries side-by-side. Free comparative analyzer.',
    keywords: ['country comparison tool', 'compare countries side-by-side', 'gdp comparison', 'population comparison finder', 'country demographic analyzer']
  },
  {
    id: 'color-name-finder',
    name: 'Color Name Finder',
    description: 'Find the official name of any HEX or RGB color with palette generation.',
    category: 'Explore',
    icon: Pipette,
    route: '/tools/color-name-finder',
    metaTitle: 'Color Name Finder — Identify HEX & RGB Color Names',
    metaDescription: 'Identify the official names of any HEX, RGB, or HSL color code. Generate complementary palettes and matching shades instantly. Free.',
    keywords: ['color name finder', 'identify hex color', 'rgb color name finder', 'color palette generator', 'find color names', 'color shade lookup']
  },
  {
    id: 'rhyme-word-finder',
    name: 'Rhyme & Word Finder',
    description: 'Find rhymes, synonyms, antonyms, and related words for any word.',
    category: 'Explore',
    icon: BookOpen,
    route: '/tools/rhyme-word-finder',
    metaTitle: 'Rhyme & Word Finder — Free Online Rhyming Dictionary',
    metaDescription: 'Find perfect rhymes, near rhymes, synonyms, and antonyms for poetry, songwriting, and copywriting. Expand your word choices instantly. Free.',
    keywords: ['rhyme finder online', 'rhyming dictionary', 'poetry writing tool', 'songwriting helper', 'find rhyming words', 'near rhymes dictionary']
  },
  {
    id: 'wikipedia-tldr',
    name: 'Wikipedia Summary Finder',
    description: 'Get an instant plain-English summary of any Wikipedia article.',
    category: 'Explore',
    icon: BookMarked,
    route: '/tools/wikipedia-tldr',
    metaTitle: 'Wikipedia Summary Finder — TL;DR Wikipedia Reader',
    metaDescription: 'Get brief, cleaned-up, plain-English summaries and TL;DR layouts of any Wikipedia topic instantly without reading massive articles.',
    keywords: ['wikipedia summary finder', 'tldr wikipedia', 'wikipedia article summarizer', 'plain english wiki reader', 'fast wiki facts search']
  },
  {
    id: 'grammar-checker',
    name: 'Grammar Checker',
    description: 'Check grammar, spelling, and style in 20+ languages with LanguageTool.',
    category: 'Explore',
    icon: SpellCheck,
    route: '/tools/grammar-checker',
    metaTitle: 'Grammar Checker Online — Free Spelling & Style check',
    metaDescription: 'Check spelling, punctuation, styling, and grammar errors in over 20 languages instantly. Powered by LanguageTool API for professional writing.',
    keywords: ['grammar checker free', 'online spell checker', 'punctuation checker', 'languagetool writing checker', 'fix grammar mistakes', 'writing style check']
  },
  {
    id: 'internet-radio-finder',
    name: 'Global Radio Finder',
    description: 'Stream and search over 30,000+ live internet radio stations by genre, country, quality, or name.',
    category: 'Explore',
    icon: Radio,
    route: '/tools/internet-radio-finder',
    metaTitle: 'Global Radio Finder — Stream 30,000+ Internet Stations',
    metaDescription: 'Browse, search, and stream over 30,000 live international radio stations instantly in your browser by genre, country, language, or bitrate. Free.',
    keywords: ['internet radio finder', 'stream live radio online', 'find radio stations by country', 'free online music radio', 'global radio scanner']
  }
];

export const categoryColors: Record<ToolCategory, string> = {
  Text: '#00FF94',
  Image: '#FF3366',
  Dev: '#FFD600',
  Converters: '#00D4FF',
  Media: '#AF52DE',
  Calculator: '#5856D6',
  SEO: '#34C759',
  Utility: '#FF2D55',
  PDF: '#FF3B30',
  Design: '#FF00FF',
  Network: '#0EA5E9',
  'Brain Games': '#9333EA',
  Science: '#10B981',
  Fun: '#F472B6',
  Discovery: '#A855F7',
  Security: '#EF4444',
  'Earth & Space': '#0EA5E9',
  Explore: '#8B5CF6'
};

